export interface VerticalKpi {
  label: string;
  value: string;
  sub: string;
  color?: string;
}

export interface VerticalTabRow {
  col1: string;
  col2: string;
  col3: string;
  col4: string;
  col5: string;
  badge?: {
    text: string;
    variant: "paid" | "due" | "notice" | "ac" | "nonac";
  };
  action?: string;
}

export interface VerticalTabConfig {
  id: string;
  label: string;
  icon: string;
  badge?: string;
  badgeVariant?: "amber" | "green" | "blue";
  tableHeaders: string[];
  rows: VerticalTabRow[];
  summaryText: string;
  actionButton?: string;
}

export interface VerticalTrustItem {
  icon: string;
  title: string;
  desc: string;
}

export interface VerticalSetupStep {
  step: number;
  title: string;
  desc: string;
}

export interface VerticalPillar {
  badge: string;
  icon: string;
  title: string;
  desc: string;
  bullets: string[];
}

export interface VerticalComparisonRow {
  capability: string;
  axoix: string;
  competitor: string;
  traditional: string;
}

export interface VerticalDetailConfig {
  slug: string;
  title: string;
  subtitle: string;
  headerBranchOptions?: string[];
  headerQuickAction?: string;
  kpis: VerticalKpi[];
  tabs: VerticalTabConfig[];
  trustBar: VerticalTrustItem[];
  setupSteps: VerticalSetupStep[];
  pillars: VerticalPillar[];
  competitorName?: string;
  comparisonRows: VerticalComparisonRow[];
  transparencyPoints: string[];
  faqList: Array<{ question: string; answer: string }>;
}

export const VERTICAL_CONFIGS: Record<string, Partial<VerticalDetailConfig>> = {

  restaurant: {
    slug: "restaurant",
    title: "Restaurant & F&B Management",
    subtitle: "Complete F&B operating system: multi-table POS, kitchen display system (KDS), automated recipe costing, and zero-fee UPI QR payments.",
    headerBranchOptions: ["Main Dining & Patio", "Bar & Lounge Section", "Takeaway & Delivery Counter"],
    headerQuickAction: "+ Quick Walk-In",
    kpis: [
      { label: "Active Tables", value: "18 / 24", sub: "75% seated" },
      { label: "Today's KDS Orders", value: "142", sub: "+12% vs avg" },
      { label: "Today's Revenue", value: "₹68,450", sub: "UPI & Card" },
      { label: "Average Ticket", value: "₹820", sub: "Fine dine & bar" },
      { label: "Table Turn Time", value: "38 min", sub: "Speedy service" },
      { label: "Kitchen Prep SLA", value: "11 min", sub: "Target < 15m" },
      { label: "Delivery Dispatches", value: "38", sub: "Swiggy / Zomato" },
      { label: "Open Dine-In Bills", value: "4", sub: "Active tables" },
    ],
    tabs: [
      {
        id: "tables",
        label: "Table Map & Live Seating",
        icon: "fa-chair",
        badge: "18 Seated",
        badgeVariant: "green",
        summaryText: "Floor 1 & Patio · 18 Tables Occupied · 6 Available",
        actionButton: "+ Quick Walk-In",
        tableHeaders: ["Table #", "Section / Area", "Covers / Guests", "Captain / Server", "Session Time", "Status", "Action"],
        rows: [
          { col1: "Table 04", col2: "Ground AC Dining", col3: "4 Guests", col4: "Vikram S.", col5: "32 mins", badge: { text: "Food Plated", variant: "paid" }, action: "Print KOT" },
          { col1: "Table 08", col2: "Patio Lounge", col3: "2 Guests", col4: "Aman R.", col5: "15 mins", badge: { text: "KDS Cooking", variant: "due" }, action: "Add Items" },
          { col1: "Table 12", col2: "VIP Mezzanine", col3: "6 Guests", col4: "Deepak M.", col5: "48 mins", badge: { text: "Bill Requested", variant: "notice" }, action: "Generate Bill" },
          { col1: "Table 15", col2: "Ground AC Dining", col3: "3 Guests", col4: "Vikram S.", col5: "08 mins", badge: { text: "Appetizers Out", variant: "paid" }, action: "View KOT" },
        ],
      },
      {
        id: "kds",
        label: "Kitchen Display (KDS)",
        icon: "fa-fire-burner",
        badge: "6 Cooking",
        badgeVariant: "amber",
        summaryText: "Live Kitchen Stations · Average Ticket SLA: 11.4 mins",
        actionButton: "Station Router",
        tableHeaders: ["KOT #", "Items Ordered", "Station", "Special Instructions", "Elapsed Time", "Status", "Kitchen Action"],
        rows: [
          { col1: "KOT-921", col2: "2x Butter Chicken, 4x Garlic Naan", col3: "Curry / Tandoor", col4: "Medium spicy, less butter", col5: "08:12 min", badge: { text: "Cooking", variant: "due" }, action: "Mark Ready ✓" },
          { col1: "KOT-922", col2: "1x Paneer Tikka Platter, 2x Dal Makhani", col3: "Tandoor", col4: "Serve sizzler style", col5: "04:30 min", badge: { text: "Fired", variant: "due" }, action: "Mark Ready ✓" },
          { col1: "KOT-920", col2: "1x Biryani Handi, 1x Raita", col3: "Biryani Counter", col4: "Extra salan on side", col5: "12:45 min", badge: { text: "Plated & Ready", variant: "paid" }, action: "Dispatch →" },
          { col1: "KOT-923", col2: "2x Virgin Mojito, 1x Cold Coffee", col3: "Mocktail Bar", col4: "Less ice in drinks", col5: "02:10 min", badge: { text: "Preparing", variant: "due" }, action: "Mark Ready ✓" },
        ],
      },
      {
        id: "inventory",
        label: "Recipe Costing & Raw Stock",
        icon: "fa-boxes-stacked",
        summaryText: "Recipe Level Auto-Deduction · 4 Items Below Safety Reorder",
        actionButton: "+ Purchase Order",
        tableHeaders: ["Ingredient Item", "Current Stock", "Unit", "Deductions Today", "Reorder Level", "Stock Health", "Action"],
        rows: [
          { col1: "Amul Butter (Carton)", col2: "14.5", col3: "Kilograms", col4: "-4.2 kg (KOT auto)", col5: "5.0 kg", badge: { text: "Healthy", variant: "paid" }, action: "Log Waste" },
          { col1: "Fresh Paneer Malai", col2: "3.2", col3: "Kilograms", col4: "-6.8 kg (KOT auto)", col5: "5.0 kg", badge: { text: "Low Stock Alert", variant: "notice" }, action: "Reorder Now" },
          { col1: "Basmati Classic Rice", col2: "45.0", col3: "Kilograms", col4: "-12.0 kg (KOT auto)", col5: "20.0 kg", badge: { text: "Healthy", variant: "paid" }, action: "Log Waste" },
          { col1: "Fresh Boneless Chicken", col2: "4.8", col3: "Kilograms", col4: "-15.2 kg (KOT auto)", col5: "6.0 kg", badge: { text: "Critical Reorder", variant: "due" }, action: "PO Dispatch" },
        ],
      },
      {
        id: "billing",
        label: "Settlement & Day Closeout",
        icon: "fa-receipt",
        summaryText: "Today's Settled Folios · ₹68,450 Realized via UPI & Cards",
        actionButton: "EOD Settlement",
        tableHeaders: ["Bill #", "Table / Order", "Payment Method", "GST Split", "Total Paid", "Status", "Receipt"],
        rows: [
          { col1: "INV-4180", col2: "Table 04 (Dine-In)", col3: "Dynamic UPI QR", col4: "₹142 (2.5% + 2.5%)", col5: "₹2,840", badge: { text: "Settled ✓", variant: "paid" }, action: "View Tax Invoice" },
          { col1: "INV-4181", col2: "Zomato Online Dispatch", col3: "Aggregator Payout", col4: "₹48 (5% GST)", col5: "₹960", badge: { text: "Auto-Reconciled", variant: "paid" }, action: "View Slip" },
          { col1: "INV-4182", col2: "Table 12 (Dine-In)", col3: "Card POS Machine", col4: "₹185 (2.5% + 2.5%)", col5: "₹3,700", badge: { text: "Settled ✓", variant: "paid" }, action: "View Tax Invoice" },
          { col1: "INV-4183", col2: "Direct Takeaway", col3: "Cash Tendered", col4: "₹32 (5% GST)", col5: "₹640", badge: { text: "Cash In Drawer", variant: "paid" }, action: "Print Slip" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-tablet-screen-button", title: "Touch & Tablet POS", desc: "Run your dining room on iPad, Android tablets, or desktop terminals with offline sync." },
      { icon: "fa-fire-burner", title: "Smart KDS Stations", desc: "Zero lost paper slips. Auto-route orders to tandoor, curry, and bar stations." },
      { icon: "fa-scale-balanced", title: "Real Ingredient Costing", desc: "Every dish sold deducts exact raw materials. Never lose margin to kitchen shrinkage." },
      { icon: "fa-gift", title: "Free Forever Starter Tier", desc: "Zero platform fees for single-outlet cafes. Upgrade only as you scale locations." },
    ],
    setupSteps: [
      { step: 1, title: "Import Menu & Floor Plan in 60s", desc: "Upload your dishes, prices, categories, and dining floor tables using our rapid bulk setup wizard." },
      { step: 2, title: "Link Printers & Kitchen Screens", desc: "Connect thermal receipt printers and KDS tablet screens over local WiFi with zero hardware lock-in." },
      { step: 3, title: "Punch Orders & Collect UPI Instantly", desc: "Start taking dine-in, takeaway, and delivery orders with zero-fee dynamic UPI QR codes." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Dining Floor",
        icon: "fa-chair",
        title: "Visual Table & Seating Architecture",
        desc: "Interactive visual table layouts tailored for Indian dining patterns: split billing, table merging, and running captain KOTs.",
        bullets: [
          "Split bills equally or by item with 1-click payment settlement.",
          "Captain mobile ordering with item modifier tags (Less Spicy, Jain, No Onion).",
          "Table reservation diary with automated WhatsApp arrival reminders.",
        ],
      },
      {
        badge: "Pillar 2: Kitchen Control",
        icon: "fa-fire-burner",
        title: "Kitchen Display System (KDS)",
        desc: "Color-coded station monitors that eliminate shouting between captains and chefs, tracking preparation speed against SLAs.",
        bullets: [
          "Item-level routing to specific kitchen stations (Tandoor, Chinese, Bar).",
          "Audio chimes on rush orders and visual alerts on dishes exceeding 15 minutes.",
          "1-touch dispatch button notifying floor captains when food is plated.",
        ],
      },
      {
        badge: "Pillar 3: Raw Materials",
        icon: "fa-boxes-stacked",
        title: "Recipe-Linked Inventory & Shrinkage Prevention",
        desc: "Stop inventory leaks before they eat your profits. Every dish ordered automatically deducts recipe grams from store stocks.",
        bullets: [
          "Batch-level raw material tracking (oil, spices, dairy, meats).",
          "Automatic variance reports comparing kitchen consumption against theoretical recipes.",
          "Purchase orders with automated supplier dispatch when stock touches reorder level.",
        ],
      },
      {
        badge: "Pillar 4: Settlement",
        icon: "fa-qrcode",
        title: "Direct UPI Collections & GST Compliant Billing",
        desc: "Dynamic QR codes printed right on the check. Customers scan and pay with GPay, PhonePe, or Paytm with zero transaction fees.",
        bullets: [
          "Zero merchant deduction fees on UPI payments directly to your current account.",
          "Instant GSTR-1, GSTR-3B tax export with B2B GSTIN validation.",
          "Shift-end cashier closeout balancing cash-in-drawer, cards, and digital payouts.",
        ],
      },
    ],
    competitorName: "Petpooja",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 1,000 monthly orders)", competitor: "Paid from Order 1 + Annual Renewal", traditional: "Excel / Paper Pad (Manual)" },
      { capability: "Integrated Back-Office", axoix: "Built-in Staff Payroll, Attendance & HR", competitor: "None (Requires Separate Software)", traditional: "Fragmented Bookkeeping" },
      { capability: "Recipe-Level Costing", axoix: "Included in Core System with Variance", competitor: "Paid Specialty Add-on", traditional: "Rough Estimates" },
      { capability: "Public Online Ordering", axoix: "Included Custom-Domain Storefront (0% Cut)", competitor: "Extra Monthly Subscription Fee", traditional: "High Swiggy/Zomato Commissions" },
      { capability: "Team Collaboration Chat", axoix: "Built-in CONNECT Team Messaging", competitor: "None", traditional: "Unorganized WhatsApp Groups" },
      { capability: "E-Invoicing & GST Returns", axoix: "Native Indian Tax Engine with E-Way Bill", competitor: "Basic GST Print Only", traditional: "CA Dependency" },
    ],
    transparencyPoints: [
      "No hardware lock-in: works on Android, iOS, Windows, and standard 80mm thermal printers.",
      "100% offline-first billing: keep taking orders even if internet drops during peak dinner rush.",
      "Zero commission on direct customer orders placed through your public web storefront.",
      "Unlimited staff user accounts with customizable role-based permissions (Cashier, Captain, Manager).",
      "Full daily database backups and encrypted cloud sync hosted in certified Tier-4 Indian datacenters.",
    ],
    faqList: [
      { question: "Can AXOIX POS work when the internet is down?", answer: "Yes. AXOIX features offline POS resilience. You can punch orders, print kitchen KOTs, and generate final customer receipts offline. All sales sync to your cloud analytics automatically once connectivity returns." },
      { question: "Do I need to buy specialized POS hardware from AXOIX?", answer: "No. AXOIX runs in any modern web browser on Android tablets, iPads, Windows desktop machines, or standard touch POS terminals. It connects seamlessly to standard ESC/POS thermal printers via USB, LAN, or Bluetooth." },
      { question: "How does the zero-fee UPI QR collection work?", answer: "A dynamic UPI QR code containing the exact bill amount is printed at the bottom of the check or displayed on a tablet screen. The guest scans using any UPI app (GPay, PhonePe, Paytm). The money lands directly into your bank current account with zero MDR transaction fees." },
      { question: "Can I manage multiple restaurant branches in one login?", answer: "Yes. AXOIX is built for multi-outlet operations. You can monitor live sales, switch between outlets, compare kitchen performance, and manage unified menus across all branches from a single central management console." },
      { question: "How long does it take to migrate my existing menu from Petpooja?", answer: "Less than 10 minutes. You can import your menu categories, dishes, variants, and prices using our CSV bulk importer or upload your digital menu for automated structuring." },
    ],
  },

  hotel: {
    slug: "hotel",
    title: "Hotel & Hospitality Management System",
    subtitle: "Enterprise PMS designed for Indian hotels, resorts, and multi-property chains: channel manager, front desk folios, housekeeping, and dynamic pricing.",
    headerBranchOptions: ["Front Desk Tape Chart", "Executive Suites Wing", "Banquets & Conference Hall"],
    headerQuickAction: "+ New Reservation",
    kpis: [
      { label: "Rooms Occupied", value: "48 / 60", sub: "80% Occupancy" },
      { label: "Average Daily Rate", value: "₹3,850", sub: "+8% vs last week" },
      { label: "RevPAR", value: "₹3,080", sub: "Revenue per Room" },
      { label: "Arrivals Today", value: "14", sub: "8 Checked In" },
      { label: "Departures Today", value: "11", sub: "10 Settled" },
      { label: "F&B Room Postings", value: "₹18,400", sub: "Room Service" },
      { label: "OTA Channel Sync", value: "Live", sub: "MMT, Booking, Agoda" },
      { label: "Night Audit Status", value: "Balanced", sub: "Previous day closed" },
    ],
    tabs: [
      {
        id: "matrix",
        label: "Live Room Tape Chart",
        icon: "fa-bed",
        badge: "80% Full",
        badgeVariant: "green",
        summaryText: "Floor 1-4 · 48 Rooms Occupied · 8 Vacant Clean · 4 Dirty",
        actionButton: "+ Quick Reservation",
        tableHeaders: ["Room #", "Room Category", "Guest Name", "Stay Dates", "Folio Balance", "Housekeeping", "Front Desk Action"],
        rows: [
          { col1: "Room 102", col2: "Deluxe King AC", col3: "Rajesh Malhotra", col4: "08 Oct – 12 Oct", col5: "₹14,200", badge: { text: "Occupied Clean", variant: "paid" }, action: "Post Room Charge" },
          { col1: "Room 205", col2: "Executive Suite", col3: "Dr. Ananya Sen", col4: "07 Oct – 10 Oct", col5: "₹22,800", badge: { text: "Occupied Clean", variant: "paid" }, action: "View Folio" },
          { col1: "Room 304", col2: "Standard Queen", col3: "Vacant (Departed)", col4: "Ready for Inspection", col5: "₹0", badge: { text: "Dirty (Cleaning)", variant: "due" }, action: "Inspect Room" },
          { col1: "Room 401", col2: "Presidential Suite", col3: "Karan Johar Group", col4: "09 Oct – 14 Oct", col5: "₹65,000", badge: { text: "VIP Checked-In", variant: "paid" }, action: "Concierge Req" },
        ],
      },
      {
        id: "folios",
        label: "Guest Folios & Postings",
        icon: "fa-file-invoice",
        summaryText: "Live Master Folios · Automatic Room & F&B Consolidation",
        actionButton: "Night Audit Run",
        tableHeaders: ["Folio #", "Guest Name", "Room", "Room Tariff", "Addons", "Total Due", "Folio Action"],
        rows: [
          { col1: "FOL-1092", col2: "Rajesh Malhotra", col3: "Room 102", col4: "₹11,400 (3 nights)", col5: "₹2,800 (F&B)", badge: { text: "Active Guest", variant: "paid" }, action: "Generate Check-out" },
          { col1: "FOL-1093", col2: "Dr. Ananya Sen", col3: "Room 205", col4: "₹18,000 (2 nights)", col5: "₹4,800 (Spa)", badge: { text: "Settlement Due", variant: "notice" }, action: "Send UPI Link" },
          { col1: "FOL-1094", col2: "Amitabh Verma", col3: "Room 108", col4: "₹7,600 (2 nights)", col5: "₹650 (Breakfast)", badge: { text: "Paid Online ✓", variant: "paid" }, action: "Print Tax Bill" },
          { col1: "FOL-1095", col2: "Suresh Pillai", col3: "Room 302", col4: "₹3,800 (1 night)", col5: "₹0", badge: { text: "Corporate Ledger", variant: "paid" }, action: "Print Voucher" },
        ],
      },
      {
        id: "channels",
        label: "Channel Manager & Rates",
        icon: "fa-network-wired",
        summaryText: "2-Way Rate & Inventory Sync across OTA Portals",
        actionButton: "+ Bulk Rate Update",
        tableHeaders: ["OTA Channel", "Connected Rooms", "Occupancy Share", "Sync Status", "Rate Parity", "Status", "Sync Trigger"],
        rows: [
          { col1: "MakeMyTrip / Goibibo", col2: "60 Rooms", col3: "42% bookings", col4: "2-Way Realtime", col5: "₹3,850", badge: { text: "Connected ✓", variant: "paid" }, action: "Sync Rates" },
          { col1: "Booking.com", col2: "60 Rooms", col3: "28% bookings", col4: "2-Way Realtime", col5: "₹3,850", badge: { text: "Connected ✓", variant: "paid" }, action: "Sync Rates" },
          { col1: "Agoda", col2: "60 Rooms", col3: "15% bookings", col4: "2-Way Realtime", col5: "₹3,850", badge: { text: "Connected ✓", variant: "paid" }, action: "Sync Rates" },
          { col1: "Direct Hotel Storefront", col2: "60 Rooms", col3: "15% bookings", col4: "Direct Engine", col5: "₹3,500", badge: { text: "0% Commission", variant: "paid" }, action: "Edit Promo" },
        ],
      },
      {
        id: "housekeeping",
        label: "Housekeeping & Maintenance",
        icon: "fa-broom",
        summaryText: "Mobile Turnaround Tracking · Warden & Staff Shift Roster",
        actionButton: "+ Maintenance Ticket",
        tableHeaders: ["Room #", "Floor", "Current Status", "Assigned Attendant", "Turnaround Timer", "Status", "Inspection"],
        rows: [
          { col1: "Room 304", col2: "3rd Floor", col3: "Cleaning in progress", col4: "Ramesh Kumar", col5: "18 mins elapsed", badge: { text: "Cleaning", variant: "due" }, action: "Verify Room" },
          { col1: "Room 210", col2: "2nd Floor", col3: "AC Filter Service Required", col4: "Engineering Staff", col5: "Reported 10:15 AM", badge: { text: "Maintenance", variant: "notice" }, action: "Resolve Ticket" },
          { col1: "Room 105", col2: "1st Floor", col3: "Inspected & Vacant Clean", col4: "Sunita Sharma", col5: "Ready for Check-In", badge: { text: "Available", variant: "paid" }, action: "Assign Guest" },
          { col1: "Room 402", col2: "4th Floor", col3: "Linen Change Complete", col4: "Ramesh Kumar", col5: "Ready for Check-In", badge: { text: "Available", variant: "paid" }, action: "Assign Guest" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-bed", title: "Tape Chart Visual Matrix", desc: "Interactive room drag-and-drop calendar with real-time housekeeping statuses." },
      { icon: "fa-file-invoice-dollar", title: "Unified Guest Folios", desc: "Auto-post restaurant meals, spa bookings, and laundry directly onto room bills." },
      { icon: "fa-network-wired", title: "2-Way Channel Manager", desc: "Instant rate and inventory sync across Booking.com, MakeMyTrip, and Agoda." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Run properties with up to 10 rooms free forever. Scale without upfront fees." },
    ],
    setupSteps: [
      { step: 1, title: "Create Floors & Room Inventory", desc: "Map your property wings, room numbers, categories (Deluxe, Suite), and seasonal rack rates." },
      { step: 2, title: "Connect OTAs & Payment Gateways", desc: "Link your OTA channels and UPI current account for automated guest payment settlements." },
      { step: 3, title: "Start Checking Guests In & Out", desc: "Generate digital registration cards, capture ID proofs, and run night audits in 1 click." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Front Desk",
        icon: "fa-concierge-bell",
        title: "Frictionless Front Desk Operations",
        desc: "Fast check-ins, Aadhaar/Passport scanner integration, and automated digital guest registration cards (GRC).",
        bullets: [
          "1-click guest check-in with digital signature and photo ID capture.",
          "Early check-in and late check-out fee rules calculated automatically.",
          "Automated police verification exports for Indian regulatory compliance.",
        ],
      },
      {
        badge: "Pillar 2: Channel Management",
        icon: "fa-network-wired",
        title: "Real-Time 2-Way OTA Synchronization",
        desc: "Prevent costly overbookings and rate disparities with sub-second rate and inventory synchronization.",
        bullets: [
          "Zero overbookings: instant inventory reduction across all OTAs on any direct booking.",
          "Bulk rate updates with min-stay rules, weekend markups, and festival surcharges.",
          "0% commission booking engine built directly into your official website.",
        ],
      },
      {
        badge: "Pillar 3: Folios & Revenue",
        icon: "fa-receipt",
        title: "Complete Billing, Night Audit & GST",
        desc: "Itemized multi-folio accounting separating room tariff, restaurant POS charges, mini bar, and luxury taxes.",
        bullets: [
          "Automated night audit balancing daily room revenue, taxes, and departmental postings.",
          "Full SAC code GST invoicing (12% and 18% slab rules calculated automatically).",
          "Company master folios for corporate guests with bill-to-company credit ledgers.",
        ],
      },
      {
        badge: "Pillar 4: Turnaround",
        icon: "fa-broom",
        title: "Mobile Housekeeping & Asset Maintenance",
        desc: "Empower attendants with mobile room lists, marking rooms clean the second inspection finishes.",
        bullets: [
          "Mobile app for housekeeping staff with clean, dirty, and inspected toggles.",
          "Preventive maintenance schedules for air conditioners, geysers, and plumbing.",
          "Laundry linen tracking between in-house laundry and external vendors.",
        ],
      },
    ],
    competitorName: "Cloudbeds",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 10 rooms)", competitor: "Paid Subscription from Room 1", traditional: "Excel / Register Books" },
      { capability: "GST & Indian Compliance", axoix: "Native Indian SAC GST & Police C-Form", competitor: "Generic Foreign Tax Engine", traditional: "Manual CA Cross-checking" },
      { capability: "Restaurant POS Integration", axoix: "Seamless Built-in Room Charge Posting", competitor: "Requires Third-Party POS Addon", traditional: "Paper Slips / Manual Folio Write" },
      { capability: "Direct Booking Website", axoix: "Included Custom-Domain Storefront (0% Cut)", competitor: "Extra Charge or Custom Integration", traditional: "No Direct Digital Engine" },
      { capability: "Full Business Back-Office", axoix: "Built-in Staff Payroll, Accounts & HR", competitor: "PMS Only", traditional: "Separate Disconnected Apps" },
      { capability: "Multi-Property Support", axoix: "Centralized Multi-Hotel Control", competitor: "Separate Charge per Property", traditional: "Fragmented Operations" },
    ],
    transparencyPoints: [
      "No per-booking commissions on direct guest bookings received through your website.",
      "Compliant with Indian Ministry of Tourism and state police foreigner C-Form reporting.",
      "Automated daily night audits that prevent back-dated ledger tampering and leakage.",
      "Full API access and Webhook support for hotel keycard locks and PBX phone systems.",
      "Data sovereignty guaranteed with all records stored in India under enterprise-grade encryption.",
    ],
    faqList: [
      { question: "How does the OTA channel manager synchronize rates?", answer: "AXOIX connects directly to major OTAs via 2-way API. Whenever a room is booked on your website or at the front desk, availability is decremented on Booking.com, MakeMyTrip, and Agoda in real time, preventing double bookings." },
      { question: "Can front desk staff post restaurant and room service orders to the room bill?", answer: "Yes. AXOIX features native integration between F&B POS and the Hotel PMS. Waitstaff can simply select 'Post to Room', verify guest name, and the charge is instantly appended to the master folio." },
      { question: "How does the night audit work?", answer: "The night audit is a 1-click automated process that validates all open folios, posts daily room charges and taxes, updates housekeeping statuses, and produces a daily revenue summary report." },
      { question: "Can I manage multiple hotels or resorts under one company?", answer: "Yes. AXOIX supports multi-property hotel chains. You can switch between properties in one click, transfer guest profiles, and review combined group financial reports." },
      { question: "Is police foreigner verification (Form C) supported?", answer: "Yes. Guest registration captures passport numbers, visa details, and nationality, generating formatted reports compliant with Indian local authority guidelines." },
    ],
  },

  "gym-fitness": {
    slug: "gym-fitness",
    title: "Gym & Fitness Studio Management",
    subtitle: "Complete fitness operating system: biometric turnstiles, automated WhatsApp membership renewals, trainer commissions, and class booking schedules.",
    headerBranchOptions: ["Turnstile Gate 1 & 2", "Cardio & Strength Floor", "Studio Alpha (CrossFit / Yoga)"],
    headerQuickAction: "+ Check-In Member",
    kpis: [
      { label: "Active Members", value: "412 / 500", sub: "82.4% capacity" },
      { label: "Today's Check-ins", value: "146", sub: "Peak 6–9 AM" },
      { label: "Expiring in 7 Days", value: "18", sub: "₹48,600 at stake" },
      { label: "Renewed This Month", value: "54", sub: "91% retention rate" },
      { label: "Class Bookings", value: "38", sub: "Zumba & CrossFit" },
      { label: "Active Trainers", value: "8", sub: "All on shift" },
      { label: "Locker Utilization", value: "94%", sub: "75/80 occupied" },
      { label: "Revenue MTD", value: "₹3,24,000", sub: "UPI & Auto-debit" },
    ],
    tabs: [
      {
        id: "turnstile",
        label: "Live Biometric Check-ins",
        icon: "fa-id-card-clip",
        badge: "146 Today",
        badgeVariant: "green",
        summaryText: "Turnstile Gate 1 & 2 · Biometric RFID & Face Attendance Realtime",
        actionButton: "+ Manual Check-in",
        tableHeaders: ["Member Name", "Membership Plan", "Gate & Time", "Status", "Assigned Trainer", "Status", "Gate Action"],
        rows: [
          { col1: "Rohan Verma", col2: "Annual Platinum", col3: "Gate 1 · 07:14 AM", col4: "Active Member", col5: "Vikram R.", badge: { text: "Pass Granted ✓", variant: "paid" }, action: "View Profile" },
          { col1: "Sneha Kulkarni", col2: "3-Month CrossFit", col3: "Gate 1 · 07:11 AM", col4: "Active Member", col5: "Anjali S.", badge: { text: "Pass Granted ✓", variant: "paid" }, action: "View Profile" },
          { col1: "Amit Joshi", col2: "Monthly Strength", col3: "Gate 2 · 07:05 AM", col4: "Overdue 2 Days", col5: "Self Workout", badge: { text: "Gate Warning ⚠", variant: "due" }, action: "Send UPI Link" },
          { col1: "Kavita Roy", col2: "Annual Gold", col3: "Gate 1 · 06:58 AM", col4: "Active Member", col5: "Vikram R.", badge: { text: "Pass Granted ✓", variant: "paid" }, action: "View Profile" },
        ],
      },
      {
        id: "renewals",
        label: "Expiring Memberships & Dues",
        icon: "fa-clock-rotate-left",
        badge: "18 Due",
        badgeVariant: "amber",
        summaryText: "18 Memberships Expiring in next 7 days · Auto WhatsApp Reminders Active",
        actionButton: "Broadcast UPI Links",
        tableHeaders: ["Member Name", "Plan Category", "Expiry Date", "Renewal Tariff", "Reminder Status", "Status", "Direct Action"],
        rows: [
          { col1: "Deepak Sharma", col2: "3-Month Full Gym", col3: "Tomorrow (11 Oct)", col4: "₹6,500", col5: "WhatsApp Sent", badge: { text: "Pending UPI", variant: "due" }, action: "Re-send WhatsApp" },
          { col1: "Pooja Malhotra", col2: "Annual Premium", col3: "In 3 Days (13 Oct)", col4: "₹18,000", col5: "Auto-debit Mandate", badge: { text: "Scheduled", variant: "paid" }, action: "View Mandate" },
          { col1: "Rahul Nambiar", col2: "6-Month Cardio + Strength", col3: "In 4 Days (14 Oct)", col4: "₹11,500", col5: "SMS + WhatsApp Sent", badge: { text: "Pending UPI", variant: "due" }, action: "Collect Cash" },
          { col1: "Meenakshi Iyer", col2: "Quarterly Yoga Batch", col3: "In 6 Days (16 Oct)", col4: "₹5,500", col5: "Reminder Queued", badge: { text: "Queued", variant: "notice" }, action: "Send Now" },
        ],
      },
      {
        id: "classes",
        label: "Class Schedule & Batches",
        icon: "fa-calendar-days",
        summaryText: "Daily Group Fitness & Personal Training Batches",
        actionButton: "+ Schedule Class",
        tableHeaders: ["Class / Activity", "Batch Timing", "Lead Trainer", "Studio Room", "Booked / Capacity", "Status", "Action"],
        rows: [
          { col1: "Morning High-Intensity CrossFit", col2: "06:30 AM – 07:30 AM", col3: "Vikram R.", col4: "Studio Alpha", col5: "18 / 20 Seats", badge: { text: "In Progress", variant: "paid" }, action: "Mark Attendance" },
          { col1: "Zumba Power Hour", col2: "08:00 AM – 09:00 AM", col3: "Anjali S.", col4: "Studio Beta", col5: "25 / 25 (Full)", badge: { text: "Fully Booked", variant: "notice" }, action: "Open Waitlist" },
          { col1: "Evening Ashtanga Yoga", col2: "06:00 PM – 07:00 PM", col3: "Kavita D.", col4: "Studio Alpha", col5: "14 / 20 Seats", badge: { text: "Upcoming", variant: "paid" }, action: "View Roster" },
        ],
      },
      {
        id: "trainers",
        label: "Personal Trainers & PT Roster",
        icon: "fa-dumbbell",
        summaryText: "8 Active Personal Trainers · Automated Session Punch & Commission Ledgers",
        actionButton: "+ Assign Trainer",
        tableHeaders: ["Trainer Name", "Specialization", "Active PT Clients", "Sessions Today", "Commission Accrued", "Status", "Action"],
        rows: [
          { col1: "Vikram Rathore", col2: "Bodybuilding & Strength", col3: "12 Clients", col4: "6 Sessions", col5: "₹24,800 MTD", badge: { text: "Floor Lead", variant: "paid" }, action: "View Roster" },
          { col1: "Anjali Saxena", col2: "HIIT & Weight Loss", col3: "9 Clients", col4: "4 Sessions", col5: "₹18,200 MTD", badge: { text: "Active", variant: "paid" }, action: "View Roster" },
          { col1: "Ramesh Nair", col2: "Functional & Rehab", col3: "7 Clients", col4: "3 Sessions", col5: "₹14,500 MTD", badge: { text: "Active", variant: "paid" }, action: "View Roster" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-id-card-clip", title: "Biometric & RFID Sync", desc: "Hardware integration with eSSL, ZKTeco, and facial recognition turnstiles." },
      { icon: "fa-clock-rotate-left", title: "Auto-Renewal Engine", desc: "Automated WhatsApp payment links sent 7, 3, and 1 day before plan expiration." },
      { icon: "fa-mobile-screen-button", title: "Member Mobile Portal", desc: "Members book classes, view workout logs, and renew fees on your branded app." },
      { icon: "fa-gift", title: "Free Forever Tier", desc: "Start free with up to 30 members. Scale up affordably as gym enrollment grows." },
    ],
    setupSteps: [
      { step: 1, title: "Create Membership Plans & Pricing", desc: "Set up monthly, quarterly, annual, and PT packages with automatic admission fees." },
      { step: 2, title: "Connect Biometric Turnstiles", desc: "Link gym turnstiles and barcode scanners to automatically enforce valid memberships at the door." },
      { step: 3, title: "Collect UPI Fees & Automate Renewals", desc: "Members pay instantly via zero-fee dynamic UPI QR codes and receive digital receipts on WhatsApp." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Access Control",
        icon: "fa-shield-halved",
        title: "Automated Turnstile & Biometric Gating",
        desc: "Stop unpaid workout entries. Turnstile unlocks only for active, paying members with instantaneous gate alerts.",
        bullets: [
          "Real-time turnstile locking when membership expiry date passes.",
          "Fingerprint, RFID smart card, and face recognition hardware compatibility.",
          "Emergency manual override button with complete security audit log.",
        ],
      },
      {
        badge: "Pillar 2: Retention & Renewals",
        icon: "fa-clock-rotate-left",
        title: "Automated WhatsApp Renewal Pipeline",
        desc: "Overcome member drop-offs with timed payment reminders, interactive UPI pay buttons, and automated invoice delivery.",
        bullets: [
          "Zero merchant deduction fees with direct UPI payment to gym bank account.",
          "One-click renewal links generated with discount coupon tokens.",
          "Daily expired member follow-up dashboard for front-desk sales staff.",
        ],
      },
      {
        badge: "Pillar 3: Classes & PT",
        icon: "fa-calendar-days",
        title: "Batch Capacities & Trainer Commissions",
        desc: "Eliminate crowded studios. Members reserve batch slots in advance, and trainer commissions calculate automatically.",
        bullets: [
          "Waitlist management with instant SMS/WhatsApp promotion when a member cancels.",
          "Trainer session punch logs preventing dispute over completed PT sessions.",
          "Transparent monthly commission split reports for gym accounting.",
        ],
      },
      {
        badge: "Pillar 4: Back-Office Suite",
        icon: "fa-file-invoice-dollar",
        title: "GST Invoicing, Attendance & Payroll",
        desc: "Everything required to run gym finances: SAC tax invoicing, trainer biometric attendance, and staff salaries.",
        bullets: [
          "Full 18% GST SAC code invoicing compliant with Indian tax mandates.",
          "Biometric staff clock-in tracking with leave allowances and overtime rules.",
          "Profit & Loss statement comparing membership revenue against rent and salaries.",
        ],
      },
    ],
    competitorName: "Mindbody",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 30 active members)", competitor: "Starts at ₹10,000+/mo + Setup Fees", traditional: "Excel / Register Book" },
      { capability: "Biometric Gate Sync", axoix: "Native eSSL & ZKTeco Hardware Integration", competitor: "Expensive Custom Hardware Only", traditional: "Manual Security Check" },
      { capability: "Direct UPI Collection", axoix: "Zero Fee Dynamic QR Directly to Current A/C", competitor: "High Gateway Fees (2-3% cut)", traditional: "Unreconciled GPay Screenshots" },
      { capability: "Automated WhatsApp", axoix: "Built-in WhatsApp Renewal Reminders", competitor: "Third-Party Twilio Addon Required", traditional: "Manual Calling" },
      { capability: "Integrated Staff Payroll", axoix: "Built-in Trainer Attendance & Salary Ledger", competitor: "None (Requires Separate Software)", traditional: "Fragmented Spreadsheets" },
    ],
    transparencyPoints: [
      "Zero per-transaction commission on membership fees collected through dynamic UPI QR codes.",
      "Supports leading Indian biometric hardware protocols (TCP/IP and LAN-based eSSL/ZKTeco machines).",
      "Member data confidentiality guaranteed with isolated MySQL tenant databases hosted in India.",
      "Custom branding available: your logo and colors across member portals and WhatsApp messages.",
      "Full daily database backups and instant Excel/CSV data exports whenever required.",
    ],
    faqList: [
      { question: "Can AXOIX integrate with our existing gym biometric turnstile?", answer: "Yes. AXOIX connects with standard eSSL, ZKTeco, Mantra, and Realtime biometric turnstiles over local LAN or cloud relay. When a member's fee expires, their biometric entry is automatically disabled until renewed." },
      { question: "How do members pay for renewals without visiting the front desk?", answer: "The system automatically sends a WhatsApp reminder 3 days before expiry containing an instant UPI payment link. When the member pays via GPay, PhonePe, or Paytm, their membership extends instantly and the turnstile re-enables automatically." },
      { question: "Can we track personal trainer sessions and calculate commissions?", answer: "Yes. Whenever a trainer takes a client through a workout, the session is logged via mobile punch or OTP. Monthly trainer payouts are calculated automatically according to your pre-set commission percentages." },
      { question: "Is multi-branch gym management supported?", answer: "Yes. You can manage multiple fitness centers from a single console. Members can even be granted multi-location access passes across all your studio locations." },
    ],
  },

  healthcare: {
    slug: "healthcare",
    title: "Clinic & Hospital Management System",
    subtitle: "Complete outpatient and hospital software: OPD token queue, doctor chambers, electronic health records (EHR), pharmacy dispensing, and GST medical billing.",
    headerBranchOptions: ["OPD Chambers 101-106", "Diagnostic Pathology Lab", "In-House Pharmacy Counter"],
    headerQuickAction: "Call Next Patient",
    kpis: [
      { label: "OPD Patients Today", value: "84", sub: "52 Completed" },
      { label: "In-Clinic Waiting", value: "12", sub: "Avg wait 14 min" },
      { label: "Doctor Chambers", value: "6", sub: "All active" },
      { label: "Pharmacy Sales", value: "₹42,800", sub: "GST billed" },
      { label: "Lab Tests Today", value: "34", sub: "28 reported" },
      { label: "Active IPD Beds", value: "14", sub: "85% occupancy" },
      { label: "Tele-Consults", value: "8", sub: "Completed" },
      { label: "Daily Revenue", value: "₹1,18,500", sub: "UPI & Insurance" },
    ],
    tabs: [
      {
        id: "opd",
        label: "OPD Token Queue & Chambers",
        icon: "fa-stethoscope",
        badge: "Token #18 Active",
        badgeVariant: "green",
        summaryText: "Chamber 102 · Dr. Rajesh Sharma (Cardiology) · Active Consultation",
        actionButton: "Call Next Patient",
        tableHeaders: ["Token #", "Patient Name", "Age / Gender", "Doctor & Department", "Vitals Recorded", "Queue Status", "Chamber Action"],
        rows: [
          { col1: "Token #18", col2: "Ramesh Gupta", col3: "52 / M", col4: "Dr. Rajesh Sharma (Cardiology)", col5: "BP 128/84 · SpO2 99%", badge: { text: "In Chamber", variant: "paid" }, action: "E-Prescription ↗" },
          { col1: "Token #19", col2: "Sangeeta Rao", col3: "38 / F", col4: "Dr. Rajesh Sharma (Cardiology)", col5: "BP 118/76 · SpO2 98%", badge: { text: "Next in Line", variant: "notice" }, action: "Call Patient" },
          { col1: "Token #20", col2: "Mohammad Farhan", col3: "29 / M", col4: "Dr. Rajesh Sharma (Cardiology)", col5: "Vitals Pending", badge: { text: "Waiting", variant: "due" }, action: "Record Vitals" },
          { col1: "Token #21", col2: "Sunita Devi", col3: "64 / F", col4: "Dr. Rajesh Sharma (Cardiology)", col5: "Vitals Pending", badge: { text: "Waiting", variant: "due" }, action: "Record Vitals" },
        ],
      },
      {
        id: "pharmacy",
        label: "Pharmacy Dispensing & Stock",
        icon: "fa-pills",
        badge: "Rx Live",
        summaryText: "In-House Hospital Pharmacy · Batch-Level Expiry & Schedule H1 Tracking",
        actionButton: "+ Counter Sale",
        tableHeaders: ["Prescription #", "Patient", "Prescribed Medicines", "Batch #", "Subtotal", "Dispense Status", "Action"],
        rows: [
          { col1: "RX-4928", col2: "Ramesh Gupta", col3: "Amoxicillin 500mg, Pan-D 40mg", col4: "BAT-921 (Exp 2027)", col5: "₹480", badge: { text: "Dispensed ✓", variant: "paid" }, action: "Print Tax Bill" },
          { col1: "RX-4929", col2: "Prakash Patel", col3: "Telmisartan 40mg, Atorvastatin 10mg", col4: "BAT-844 (Exp 2028)", col5: "₹620", badge: { text: "Packing", variant: "due" }, action: "Dispense & Bill" },
          { col1: "RX-4930", col2: "Anita Sharma", col3: "Paracetamol 650mg, Cetirizine 10mg", col4: "BAT-710 (Exp 2027)", col5: "₹140", badge: { text: "Ready", variant: "notice" }, action: "Collect UPI" },
        ],
      },
      {
        id: "lab",
        label: "Diagnostic Lab & Reports",
        icon: "fa-vial",
        summaryText: "Pathology & Biochemistry Testing Center · Realtime Barcode Sample Tracking",
        actionButton: "+ New Sample",
        tableHeaders: ["Sample ID", "Patient Name", "Test Panel", "Collected At", "Analyzer Status", "Result Status", "Action"],
        rows: [
          { col1: "SMP-1042", col2: "Ramesh Gupta", col3: "Complete Blood Count (CBC) + Lipid", col4: "08:15 AM", col5: "Sysmex Analyzer 01", badge: { text: "Report Ready ✓", variant: "paid" }, action: "WhatsApp PDF" },
          { col1: "SMP-1043", col2: "Geeta Verma", col3: "HbA1c & Fasting Blood Sugar", col4: "08:30 AM", col5: "Bio-Rad Analyzer", badge: { text: "Testing", variant: "due" }, action: "Enter Values" },
        ],
      },
      {
        id: "billing",
        label: "Medical Invoicing & Insurance",
        icon: "fa-receipt",
        summaryText: "GST Compliant Medical Bills & Cashless TPA Pre-Authorization",
        actionButton: "New Invoice",
        tableHeaders: ["Invoice #", "Patient Name", "Department", "Cons + Rx + Lab", "Payment Mode", "Reconciliation", "Action"],
        rows: [
          { col1: "MED-8120", col2: "Ramesh Gupta", col3: "Cardiology OPD", col4: "₹1,880 (Cons ₹800 + Rx ₹480 + Lab ₹600)", col5: "Dynamic UPI QR", badge: { text: "Settled ✓", variant: "paid" }, action: "Tax Receipt" },
          { col1: "MED-8121", col2: "Suresh Pillai", col3: "Orthopedic IPD", col4: "₹24,500 (Room + Surgery + Rx)", col5: "Star Health TPA", badge: { text: "Pre-Auth Approved", variant: "paid" }, action: "View Claim" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-stethoscope", title: "Smart OPD Token Display", desc: "TV queue display in waiting rooms with automated voice announcements in Hindi & English." },
      { icon: "fa-file-prescription", title: "Digital E-Prescriptions", desc: "Fast prescription pads with pre-loaded medicine brand dosages and lab test bundles." },
      { icon: "fa-pills", title: "Schedule H1 Pharmacy", desc: "Mandatory batch expiry alerts, scheduled drug registers, and automated stock deductions." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Start free with up to 100 OPD consultations per month. No setup or migration fees." },
    ],
    setupSteps: [
      { step: 1, title: "Register Doctors & OPD Chambers", desc: "Configure doctor consultation schedules, fee structures, and chamber allocations." },
      { step: 2, title: "Import Pharmacy Inventory & Lab Master", desc: "Upload drug catalogs, batch expiry dates, and lab test reference ranges in 1 click." },
      { step: 3, title: "Issue Tokens & Collect Consultation Fees", desc: "Start generating patient tokens with instant zero-fee UPI QR codes and digital receipts." },
    ],
    pillars: [
      {
        badge: "Pillar 1: OPD Queue",
        icon: "fa-people-line",
        title: "Intelligent Queue Management & Tokens",
        desc: "End reception overcrowding. Digital token screens and SMS notifications update patients on their exact queue wait time.",
        bullets: [
          "Waiting room TV screen display showing token numbers and doctor chambers.",
          "Automated patient SMS/WhatsApp alerts when their turn is 2 tokens away.",
          "Quick triage vital entry pad (BP, Pulse, SpO2, Temperature, Weight) prior to chamber entry.",
        ],
      },
      {
        badge: "Pillar 2: E-Prescriptions",
        icon: "fa-file-prescription",
        title: "Clinical Electronic Health Records (EHR)",
        desc: "Type or speak prescriptions in seconds. Medication dosages, duration, and patient diagnosis history saved securely.",
        bullets: [
          "Auto-suggest medicine brands with generic salt alternatives and dosage frequencies.",
          "Voice prescription dictation in Indian English and regional accents.",
          "Prescriptions sent directly to patient WhatsApp and clinic pharmacy instantly.",
        ],
      },
      {
        badge: "Pillar 3: Pharmacy & Lab",
        icon: "fa-pills",
        title: "Integrated Pharmacy & Diagnostic Center",
        desc: "Doctors' prescriptions route to the clinic pharmacy and lab counter in realtime, eliminating lost paper slips.",
        bullets: [
          "Barcode medicine dispensation with automated near-expiry inventory alerts.",
          "Diagnostic lab test result entry with automatic abnormal flag highlighting.",
          "WhatsApp delivery of PDF diagnostic lab reports with authenticated doctor signatures.",
        ],
      },
      {
        badge: "Pillar 4: Back-Office Suite",
        icon: "fa-shield-halved",
        title: "ABDM Health Records & GST Invoicing",
        desc: "ABDM Ayushman Bharat Digital Mission compliance, doctor payouts, staff biometric shifts, and GST returns.",
        bullets: [
          "ABDM ABHA ID creation and health record linking architecture.",
          "Itemized GST tax invoices separating non-taxable healthcare from taxable consumables.",
          "Doctor revenue share and fee split reports calculated automatically.",
        ],
      },
    ],
    competitorName: "Practo",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 100 consultations/mo)", competitor: "Paid Monthly Plan from Patient 1", traditional: "Manual Paper Registers" },
      { capability: "Integrated Pharmacy & Lab", axoix: "Included in Single Unified Database", competitor: "Requires Separate Third-Party Systems", traditional: "Disconnected Bills" },
      { capability: "Direct UPI QR Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "High Payment Gateway Fees", traditional: "Cash Only / Loose Receipts" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Clinic Nurse & Staff Payroll", competitor: "None (Doctor Tool Only)", traditional: "Separate Excel Sheets" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Multi-Tenant Cloud Database", traditional: "Vulnerable Physical Files" },
    ],
    transparencyPoints: [
      "All medical health records encrypted with HIPAA and Indian DISHA/ABDM healthcare compliance standards.",
      "100% data residency: medical files and patient records stored exclusively in certified Indian datacenters.",
      "Zero lock-in: export complete patient history, prescriptions, and financial ledgers anytime in standard formats.",
      "Role-based clinical permissions: receptionists cannot view confidential doctor clinical notes.",
      "Daily automated backups with 99.9% guaranteed uptime SLA for mission-critical clinic operations.",
    ],
    faqList: [
      { question: "Can we display the OPD token queue on a waiting room TV?", answer: "Yes. Simply open the TV Queue URL on any smart TV or browser in your clinic reception. It displays the active token numbers, doctor chambers, and sounds an audible chime when the next patient is called." },
      { question: "How does the prescription reach the clinic pharmacy?", answer: "The moment the doctor clicks 'Finish Consultation', the prescription is instantly routed to the internal pharmacy screen. When the patient walks to the counter, their medicines are already staged and ready for bill generation." },
      { question: "Are lab reports automatically sent to patients on WhatsApp?", answer: "Yes. Once the pathologist verifies and signs off on the test results, an encrypted PDF report is delivered straight to the patient's WhatsApp number with no manual emailing required." },
    ],
  },

  education: {
    slug: "education",
    title: "Education & School Management System",
    subtitle: "Enterprise campus operating system: automated fee management with UPI, RFID biometric attendance, report cards, timetable scheduler, and parent communication portal.",
    headerBranchOptions: ["Central Campus (CBSE)", "Primary Wing (Class 1-5)", "Senior Wing (Class 6-12)"],
    headerQuickAction: "+ Add Student",
    kpis: [
      { label: "Total Students", value: "1,240", sub: "Active enrollment" },
      { label: "Present Today", value: "96.4%", sub: "RFID Gate sync" },
      { label: "Fee Collection", value: "₹18.45L", sub: "Month to date" },
      { label: "Pending Fees", value: "₹1.85L", sub: "14 Defaulters" },
      { label: "Total Classes", value: "36 Sections", sub: "Pre-K to 12th" },
      { label: "Upcoming Exams", value: "Mid-Term", sub: "In 4 Days" },
      { label: "Hostel Occupancy", value: "142 / 160", sub: "Beds occupied" },
      { label: "Transport Fleets", value: "12 Buses", sub: "Live GPS active" },
    ],
    tabs: [
      {
        id: "fees",
        label: "Fee Collections & Defaulters",
        icon: "fa-receipt",
        badge: "14 Overdue",
        badgeVariant: "amber",
        summaryText: "Q3 Fee Cycle · ₹18.45L Collected · 14 Defaulters Tracked with Auto-WhatsApp Reminders",
        actionButton: "+ Collect Offline Fee",
        tableHeaders: ["Invoice #", "Student Name", "Class & Section", "Fee Category", "Amount", "Payment Status", "Action"],
        rows: [
          { col1: "INV-SCH-401", col2: "Aryan Malhotra", col3: "Class 10-A", col4: "Q3 Tuition Fee", col5: "₹18,500", badge: { text: "Paid via UPI ✓", variant: "paid" }, action: "Receipt Slip" },
          { col1: "INV-SCH-402", col2: "Kavita Roy", col3: "Class 9-B", col4: "Term 2 Comprehensive", col5: "₹12,000", badge: { text: "18 Days Overdue", variant: "due" }, action: "Send WhatsApp UPI" },
          { col1: "INV-SCH-403", col2: "Rohan Verma", col3: "Class 11-Science", col4: "Annual Lab + Tuition", col5: "₹22,000", badge: { text: "Paid via NetBanking ✓", variant: "paid" }, action: "Receipt Slip" },
          { col1: "INV-SCH-404", col2: "Tanvi Patel", col3: "Class 8-C", col4: "Q3 Tuition Fee", col5: "₹8,500", badge: { text: "24 Days Overdue", variant: "due" }, action: "Send WhatsApp UPI" },
        ],
      },
      {
        id: "attendance",
        label: "Gate RFID & Live Attendance",
        icon: "fa-id-card-clip",
        badge: "96.4% Present",
        badgeVariant: "green",
        summaryText: "Automated RFID Gate Barrier · Turnstiles Synced with Parent SMS Alerts",
        actionButton: "Broadcast Absent Alert",
        tableHeaders: ["RFID Tag", "Student Name", "Class / Section", "Gate Entry Time", "Bus Route", "Day Status", "Action"],
        rows: [
          { col1: "RFID-091", col2: "Rohan Verma", col3: "Class 11-Science", col4: "Gate 01 · 07:48 AM", col5: "Route #04 (Dwarka)", badge: { text: "Present at Gate ✓", variant: "paid" }, action: "View History" },
          { col1: "RFID-092", col2: "Ananya Sen", col3: "Class 10-A", col4: "Gate 02 · 07:52 AM", col5: "Route #02 (Rohini)", badge: { text: "Present at Gate ✓", variant: "paid" }, action: "View History" },
          { col1: "RFID-093", col2: "Aditya Nair", col3: "Class 8-B", col4: "Absent (Unexcused)", col5: "Route #07 (Vasant Kunj)", badge: { text: "Absent • SMS Sent", variant: "due" }, action: "Call Parent" },
          { col1: "RFID-094", col2: "Sneha Iyer", col3: "Class 9-A", col4: "Gate 01 · 07:55 AM", col5: "Private Car Drop", badge: { text: "Present at Gate ✓", variant: "paid" }, action: "View History" },
        ],
      },
      {
        id: "students",
        label: "Student Registry (SIS)",
        icon: "fa-user-graduate",
        summaryText: "1,240 Active Student Profiles · Full Digital Cumulative Record (DCR)",
        actionButton: "+ New Admission",
        tableHeaders: ["Admission #", "Student Name", "Class & Roll #", "Guardian Contact", "Scholarship / House", "Status", "Action"],
        rows: [
          { col1: "STU-2026-089", col2: "Aryan Malhotra", col3: "Class 10-A · Roll 14", col4: "Rajiv Malhotra (+91 98110...)", col5: "Tagore House (Red)", badge: { text: "Enrolled Active", variant: "paid" }, action: "Student 360°" },
          { col1: "STU-2026-090", col2: "Kavita Roy", col3: "Class 9-B · Roll 08", col4: "Sunil Roy (+91 98230...)", col5: "Shivaji House (Blue)", badge: { text: "Enrolled Active", variant: "paid" }, action: "Student 360°" },
          { col1: "STU-2026-091", col2: "Rohan Verma", col3: "Class 11-Science · Roll 03", col4: "Dr. AK Verma (+91 98450...)", col5: "Merit Scholar (Ashoka)", badge: { text: "Enrolled Active", variant: "paid" }, action: "Student 360°" },
          { col1: "STU-2026-092", col2: "Tanvi Patel", col3: "Class 8-C · Roll 19", col4: "Manoj Patel (+91 99010...)", col5: "Raman House (Green)", badge: { text: "Enrolled Active", variant: "paid" }, action: "Student 360°" },
        ],
      },
      {
        id: "exams",
        label: "Exams & Report Cards",
        icon: "fa-file-lines",
        summaryText: "Mid-Term Examination Portal · Automated CCE & CBSE Term Report Card Generator",
        actionButton: "Generate Report Cards",
        tableHeaders: ["Exam Code", "Subject & Assessment", "Target Class", "Exam Date", "Max Marks / Format", "Status", "Action"],
        rows: [
          { col1: "EXAM-101", col2: "Mathematics Mid-Term Board Prep", col3: "Class 10 (All Sections)", col4: "14 Oct 2026 · 09:30 AM", col5: "80 Marks (CBSE)", badge: { text: "Seating Assigned", variant: "paid" }, action: "Hall Tickets" },
          { col1: "EXAM-102", col2: "Physics Practical & Viva", col3: "Class 12-Science", col4: "16 Oct 2026 · 10:00 AM", col5: "30 Marks (Lab)", badge: { text: "Lab Ready", variant: "paid" }, action: "Enter Marks" },
          { col1: "EXAM-103", col2: "English Literature Assessment", col3: "Class 9 (All Sections)", col4: "18 Oct 2026 · 09:30 AM", col5: "80 Marks (Written)", badge: { text: "Questions Locked", variant: "notice" }, action: "Blueprint" },
          { col1: "EXAM-104", col2: "Computer Science Coding Lab", col3: "Class 11-Science", col4: "20 Oct 2026 · 11:00 AM", col5: "50 Marks (Practical)", badge: { text: "Scheduled", variant: "paid" }, action: "View Roster" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-id-card-clip", title: "RFID Gate & Bus GPS", desc: "Automated turnstile gate and live bus tracking alerts sent straight to parents' phones." },
      { icon: "fa-qrcode", title: "Direct UPI Fee Collections", desc: "Dynamic UPI QR codes on fee bills with automated reconciliation and zero payment cuts." },
      { icon: "fa-file-invoice", title: "CBSE & ICSE Report Cards", desc: "1-click generation of term report cards compliant with board guidelines and CCE grading." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Free forever for up to 50 students. Scale affordably as your school or coaching institute grows." },
    ],
    setupSteps: [
      { step: 1, title: "Import Classes & Student Roster", desc: "Upload student records, roll numbers, guardian contacts, and class sections using our CSV importer." },
      { step: 2, title: "Configure Fee Structure & Discounts", desc: "Define quarterly tuition, lab fees, transport slabs, and sibling discount rules." },
      { step: 3, title: "Enable Parent WhatsApp & Fee UPI", desc: "Send automated WhatsApp fee reminders with zero-fee UPI links directly into school accounts." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Fee Pipeline",
        icon: "fa-receipt",
        title: "Automated Fee Invoicing & WhatsApp UPI",
        desc: "End manual fee chasing. Generate digital invoices with dynamic UPI QR codes and scheduled WhatsApp reminders.",
        bullets: [
          "Zero merchant deduction fees with direct UPI payment to school bank account.",
          "Automated late fee rules and sibling discount calculations.",
          "Instant digital payment receipts delivered to parent WhatsApp.",
        ],
      },
      {
        badge: "Pillar 2: Gate Security",
        icon: "fa-id-card-clip",
        title: "Biometric & RFID Gate Attendance",
        desc: "Real-time entry/exit logging with hardware integration for turnstiles, RFID smart ID cards, and school buses.",
        bullets: [
          "Instant automated WhatsApp/SMS alerts when a student swipes into campus.",
          "RFID bus reader tracking onboarding and drop-off points.",
          "Class teacher attendance mobile app with 1-click absent broadcasts.",
        ],
      },
      {
        badge: "Pillar 3: Academics",
        icon: "fa-file-lines",
        title: "CBSE/ICSE Grading & Exam Scheduler",
        desc: "Comprehensive academic management with automatic hall ticket generation, mark entry sheets, and board report cards.",
        bullets: [
          "Automated examination timetable and seating arrangement matrix.",
          "Teacher grading portal with auto-calculation of weighted term scores.",
          "Downloadable PDF report cards with institutional crest and digital signature.",
        ],
      },
      {
        badge: "Pillar 4: Institution Back-Office",
        icon: "fa-users-gear",
        title: "Staff Payroll, Transport & Hostels",
        desc: "Unified administration covering teacher biometric shifts, vehicle GPS maintenance, and residential student hostels.",
        bullets: [
          "Biometric teacher clock-in with leave ledger and monthly payroll calculation.",
          "Hostel bed allocation matrix with room warden meal attendance.",
          "Vehicle maintenance and diesel mileage logs for transport fleet.",
        ],
      },
    ],
    competitorName: "Teachmint",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 50 students)", competitor: "Paid Subscription from Student 1", traditional: "Excel / Fee Registers" },
      { capability: "Direct UPI Fees", axoix: "Zero Fee Dynamic QR Directly to School A/C", competitor: "Gateway Cut (1.8% - 2.5% per fee)", traditional: "Cash / Cheque Bounce Friction" },
      { capability: "RFID & Gate Hardware", axoix: "Native eSSL & Mantra Gate Biometrics", competitor: "Third-Party Vendor Integration", traditional: "Manual Gate Sign-in Register" },
      { capability: "Teacher Payroll & HR", axoix: "Included Staff Biometric Shifts & Payroll", competitor: "None (Requires Separate Software)", traditional: "Disconnected Bookkeeping" },
      { capability: "School Bus GPS Sync", axoix: "Built-in Fleet GPS & Route Stops", competitor: "Paid Transport Add-on", traditional: "Manual Calling Drivers" },
      { capability: "Data Residency & Security", axoix: "Isolated MySQL Tenant Databases in India", competitor: "Shared Multi-Tenant Cloud DB", traditional: "Unprotected Paper Records" },
    ],
    transparencyPoints: [
      "Zero commission cuts on tuition fees collected through school dynamic UPI QR codes.",
      "Compliant with Indian CBSE, ICSE, and state education board data protection directives.",
      "Full student academic cumulative records (DCR) exported to Excel or PDF in one click.",
      "Granular staff roles: teachers cannot access institution financial fee balance ledgers.",
      "Encrypted cloud hosting in certified Indian Tier-4 datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "How do parents pay school fees using AXOIX?", answer: "Parents receive a WhatsApp notification with a dynamic UPI link. They tap and pay using Google Pay, PhonePe, or Paytm. Funds land directly in the school current account with zero gateway fee deductions." },
      { question: "Can AXOIX sync with our existing RFID gate turnstiles?", answer: "Yes. AXOIX connects with standard eSSL, ZKTeco, and Mantra RFID turnstiles over LAN. Student check-ins register instantaneously and trigger parent arrival SMS/WhatsApp messages." },
      { question: "Can we generate official CBSE/ICSE format report cards?", answer: "Yes. The report card engine supports CBSE, ICSE, IB, and state board grading templates with scholastic, co-scholastic, and teacher remarks." },
      { question: "Does AXOIX handle school transport routes and bus fees?", answer: "Yes. You can manage transport fleets, map bus stop fee slabs, and monitor live vehicle GPS movement from the administration console." },
      { question: "How long does it take to import our existing student database?", answer: "Under 5 minutes. Use our CSV bulk importer to upload student names, roll numbers, guardian contacts, and historical balances in a single upload." },
    ],
  },

  "salon-spa": {
    slug: "salon-spa",
    title: "Salon & Spa Management Software",
    subtitle: "Complete salon operating system: real-time chair calendar, stylist commissions, automated WhatsApp reminders, service packages, and register closeout.",
    headerBranchOptions: ["Main Studio (8 Chairs)", "VIP Styling Lounge", "Express Grooming Bar"],
    headerQuickAction: "+ New Booking",
    kpis: [
      { label: "Today's Bookings", value: "24", sub: "6 Walk-ins" },
      { label: "Completed Today", value: "18", sub: "₹28,400 billed" },
      { label: "Active Stylists", value: "6 Chairs", sub: "All staffed" },
      { label: "In-Chair / Pending", value: "6", sub: "In service" },
      { label: "Today's Revenue", value: "₹34,800", sub: "UPI & POS" },
      { label: "Avg Service Ticket", value: "₹1,450", sub: "Services & Add-ons" },
      { label: "Membership Packages", value: "42 Active", sub: "Bridal & Prepaid" },
      { label: "Register Closeout", value: "Balanced", sub: "Zero variance" },
    ],
    tabs: [
      {
        id: "appointments",
        label: "Chair Appointments & Walk-ins",
        icon: "fa-calendar-check",
        badge: "6 In-Chair",
        badgeVariant: "green",
        summaryText: "Live Studio Schedule · 8 Chairs · Stylist Turnaround Target < 45m",
        actionButton: "+ Quick Walk-in",
        tableHeaders: ["Booking #", "Customer Name", "Services Selected", "Stylist & Chair", "Time & Duration", "Status", "Action"],
        rows: [
          { col1: "APT-8401", col2: "Priya Mehra", col3: "Hair Spa + Hydra Facial", col4: "Sameer K. (Chair 02)", col5: "11:30 AM · 90 min", badge: { text: "In-Chair 💇", variant: "paid" }, action: "Add Add-on" },
          { col1: "APT-8402", col2: "Rhea Kapoor", col3: "Keratin Hair Smoothing", col4: "Pooja M. (Chair 04)", col5: "12:00 PM · 150 min", badge: { text: "Waiting in Lounge", variant: "notice" }, action: "Assign Chair" },
          { col1: "APT-8403", col2: "Ananya Sen", col3: "Gel Manicure & Pedicure", col4: "Neha S. (Chair 01)", col5: "10:00 AM · 60 min", badge: { text: "Completed ✓", variant: "paid" }, action: "Collect UPI" },
          { col1: "APT-8404", col2: "Dr. Sunita Rao", col3: "Bridal Makeup Trial", col4: "Sameer K. (Chair 03)", col5: "02:00 PM · 75 min", badge: { text: "Scheduled", variant: "paid" }, action: "Send WhatsApp" },
        ],
      },
      {
        id: "stylists",
        label: "Stylist Roster & Commissions",
        icon: "fa-scissors",
        summaryText: "Automated Commission Ledgers · Instant Shift Earnings Calculation",
        actionButton: "+ Add Staff",
        tableHeaders: ["Stylist ID", "Stylist Name & Title", "Assigned Chair", "Clients Today", "Services Total", "Commission Earned", "Status"],
        rows: [
          { col1: "STY-01", col2: "Sameer Khan (Senior Master Stylist)", col3: "Chair 02", col4: "5 Clients", col5: "₹8,000 billed", badge: { text: "In-Service 💇", variant: "paid" }, action: "View Payout" },
          { col1: "STY-02", col2: "Pooja Mishra (Color & Texture)", col3: "Chair 04", col4: "4 Clients", col5: "₹7,250 billed", badge: { text: "Waiting", variant: "notice" }, action: "View Payout" },
          { col1: "STY-03", col2: "Neha Sharma (Nail & Skin)", col3: "Chair 01", col4: "6 Clients", col5: "₹8,000 billed", badge: { text: "Available", variant: "paid" }, action: "View Payout" },
          { col1: "STY-04", col2: "Vikram Joshi (Master Barber)", col3: "Chair 05", col4: "7 Clients", col5: "₹9,000 billed", badge: { text: "In-Service ✂️", variant: "paid" }, action: "View Payout" },
        ],
      },
      {
        id: "packages",
        label: "Memberships & Packages",
        icon: "fa-tags",
        summaryText: "42 Active Prepaid Memberships · Automated Package Depletion per Visit",
        actionButton: "+ Issue Package",
        tableHeaders: ["Package Code", "Package Name", "Client Name", "Services Redemptions", "Package Value", "Status", "Action"],
        rows: [
          { col1: "PKG-101", col2: "Annual Premium Glow Club", col3: "Priya Mehra", col4: "4 of 12 Services Used", col5: "₹18,000", badge: { text: "Active Member", variant: "paid" }, action: "Punch Service" },
          { col1: "PKG-102", col2: "Complete Bridal Makeover Suite", col3: "Rhea Kapoor", col4: "2 of 6 Pre-Bridal Sessions", col5: "₹24,000", badge: { text: "Active", variant: "paid" }, action: "Punch Service" },
          { col1: "PKG-103", col2: "10-Session Hair Care Pass", col3: "Rahul Verma", col4: "8 of 10 Used", col5: "₹7,500", badge: { text: "2 Sessions Left", variant: "notice" }, action: "Renew Pass" },
          { col1: "PKG-104", col2: "Wellness Spa Bundle (5 Hours)", col3: "Sunita Rao", col4: "1 of 5 Sessions Used", col5: "₹12,500", badge: { text: "Active", variant: "paid" }, action: "Punch Service" },
        ],
      },
      {
        id: "closeout",
        label: "Day Closeout & Cash Drawer",
        icon: "fa-cash-register",
        summaryText: "End of Day (EOD) Settlement · Zero Cash Leakage Guarantee",
        actionButton: "Run Night Audit",
        tableHeaders: ["Register ID", "Tender Type", "Declared Amount", "System Computed", "Variance", "Status", "Action"],
        rows: [
          { col1: "REG-01", col2: "Dynamic UPI QR (GPay/PhonePe)", col3: "₹21,400", col4: "₹21,400", col5: "₹0 (Exact Match)", badge: { text: "Auto-Reconciled ✓", variant: "paid" }, action: "View Ledger" },
          { col1: "REG-02", col2: "Card Swipe POS Machine", col3: "₹9,800", col4: "₹9,800", col5: "₹0 (Exact Match)", badge: { text: "Settled ✓", variant: "paid" }, action: "View Slip" },
          { col1: "REG-03", col2: "Cash in Drawer", col3: "₹3,600", col4: "₹3,600", col5: "₹0 (Balanced)", badge: { text: "Count Verified", variant: "paid" }, action: "Print Summary" },
          { col1: "REG-TOT", col2: "Total Gross Business", col3: "₹34,800", col4: "₹34,800", col5: "₹0 Variance", badge: { text: "Day Closed ✓", variant: "paid" }, action: "Lock Shift" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-chair", title: "Visual Chair Timeline", desc: "Drag-and-drop appointment diary with stylist chair allocation and buffer intervals." },
      { icon: "fa-percent", title: "Stylist Commission Ledger", desc: "Transparent automatic commission splits by service, product sale, and senior stylist tier." },
      { icon: "fa-whatsapp", title: "Automated WhatsApp Confirmations", desc: "Reduce client no-shows by 70% with automated booking confirmations and arrival alerts." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Free forever for salons with up to 3 chairs. No monthly subscription to get started." },
    ],
    setupSteps: [
      { step: 1, title: "Set Up Service Menu & Stylist Chairs", desc: "Enter haircuts, spa therapies, packages, service durations, and commission percentages." },
      { step: 2, title: "Enable Online Booking & WhatsApp", desc: "Share your branded booking link for customers to pick preferred stylists and appointment slots." },
      { step: 3, title: "Collect UPI & Run Day Closeouts", desc: "Generate zero-fee UPI QR checkouts, track product sales, and balance cash drawers in 1 click." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Booking & Chairs",
        icon: "fa-calendar-check",
        title: "Visual Chair Scheduling & Walk-In Queue",
        desc: "Color-coded appointment grid matching available chairs with stylist skill sets to eliminate client waiting times.",
        bullets: [
          "Drag-and-drop appointments with custom processing buffer times for hair color.",
          "Real-time salon floor display showing chair occupancies and active timers.",
          "Automated WhatsApp arrival reminder with one-touch client confirmation.",
        ],
      },
      {
        badge: "Pillar 2: Stylist Earnings",
        icon: "fa-scissors",
        title: "Transparent Stylist Commissions & Tips",
        desc: "Never calculate commissions in paper notebooks. Auto-split service revenues, retail product incentives, and digital tips.",
        bullets: [
          "Configurable commission slabs by service type (hair, skin, nails, makeup).",
          "Daily stylist earnings summary accessible on staff mobile portals.",
          "Biometric shift attendance linked directly to staff monthly salaries.",
        ],
      },
      {
        badge: "Pillar 3: Packages & Retail",
        icon: "fa-tags",
        title: "Bridal Packages & Shelf Stock Control",
        desc: "Upsell high-margin memberships and salon retail products with automated consumption tracking.",
        bullets: [
          "Prepaid service packages with automated decrement upon each visit.",
          "Retail stock management with barcode scanning and low-stock alerts.",
          "Separation of back-bar professional consumption from retail counter inventory.",
        ],
      },
      {
        badge: "Pillar 4: Front Desk",
        icon: "fa-cash-register",
        title: "Zero-Fee UPI Checkout & Day Closeout",
        desc: "Fast checkout terminals printing itemized GST bills with dynamic UPI QR codes and balancing cash drawers.",
        bullets: [
          "Direct UPI payments landing instantly into salon bank current account.",
          "Comprehensive day closeout sheet balancing cash, card, and digital collections.",
          "Native GST tax reporting compliant with Indian SAC service codes.",
        ],
      },
    ],
    competitorName: "Zenoti",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 3 chairs)", competitor: "Expensive Enterprise Contract ($200+/mo)", traditional: "Paper Appointment Diary" },
      { capability: "Direct UPI QR Collections", axoix: "Zero Fee Dynamic QR Directly to Salon A/C", competitor: "High Processing Cut per Transaction", traditional: "Unreconciled GPay Screenshots" },
      { capability: "Stylist Commission Tracker", axoix: "Realtime Automatic Split by Service & Tier", competitor: "Complex Setup / Add-on Required", traditional: "Manual Notebook Calculations" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Salon Staff Attendance & Payroll", competitor: "None (Booking Engine Only)", traditional: "Separate Excel Sheets" },
      { capability: "Retail Inventory & Consumption", axoix: "Integrated In-Chair Use & Shelf Sales", competitor: "Paid Inventory Module Addon", traditional: "Unchecked Product Shrinkage" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant Databases in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Physical Paper Logs" },
    ],
    transparencyPoints: [
      "Zero commission cuts on appointments booked through your official website or WhatsApp.",
      "Automated day closeout balancing cards, UPI, and cash to prevent front-desk leakage.",
      "Full exportability of client contact lists, service histories, and revenue ledgers anytime.",
      "Granular staff permissions: stylists cannot view overall salon profits or other stylists' commission rates.",
      "Hosted in certified Tier-4 Indian datacenters with automated encrypted daily backups.",
    ],
    faqList: [
      { question: "How do clients book appointments online?", answer: "Clients visit your branded website link, select their desired service, choose their preferred stylist, and pick an available time slot. An instant WhatsApp confirmation is generated." },
      { question: "Can AXOIX calculate different commission rates for different stylists?", answer: "Yes. You can assign custom commission tiers (e.g., 20% for Senior Stylists, 15% for Juniors) and set separate commission rates for retail product sales." },
      { question: "How does the WhatsApp reminder prevent no-shows?", answer: "The system automatically dispatches an interactive WhatsApp reminder 2 hours prior to the appointment asking the client to confirm or reschedule in one tap." },
      { question: "Can we sell bridal packages and prepaid memberships?", answer: "Yes. AXOIX tracks package redemption balances automatically, so customers can use individual services across multiple visits." },
      { question: "Does AXOIX track salon retail products like shampoos and serums?", answer: "Yes. The inventory module separates internal professional consumption (back-bar) from retail counter sales with barcode scanning support." },
    ],
  },

  retail: {
    slug: "retail",
    title: "Retail Store & Wholesale Management System",
    subtitle: "Modern retail & trading OS: barcode POS, batch & expiry inventory, multi-store stock transfers, dynamic UPI QR billing, and B2B GST e-invoicing.",
    headerBranchOptions: ["Main Counter POS", "Store 02 (Indiranagar)", "Central Warehouse"],
    headerQuickAction: "+ Counter Sale",
    kpis: [
      { label: "Today's Sales", value: "₹84,200", sub: "142 Invoices" },
      { label: "Active SKUs", value: "2,840", sub: "In Stock" },
      { label: "Low Stock Alerts", value: "12 Items", sub: "Reorder queued" },
      { label: "Average Basket", value: "₹590", sub: "3.4 Items/bill" },
      { label: "Gross Margin", value: "32.4%", sub: "Realized" },
      { label: "B2B Credit Dues", value: "₹1,45,000", sub: "8 Parties" },
      { label: "Warehouse Stock", value: "₹48.2L", sub: "Valuation" },
      { label: "E-Way Bills", value: "6 Today", sub: "B2B Dispatches" },
    ],
    tabs: [
      {
        id: "pos",
        label: "Counter POS & Invoices",
        icon: "fa-cash-register",
        badge: "Active",
        badgeVariant: "green",
        summaryText: "Barcode Terminal 01 · 142 Billed Today · Average Checkout 42s",
        actionButton: "+ New Sale",
        tableHeaders: ["Bill #", "Customer / Party", "Items / Quantity", "Payment Mode", "Invoice Total", "Status", "Action"],
        rows: [
          { col1: "BILL-1041", col2: "Walk-in Retail Customer", col3: "4 Groceries (Scan)", col4: "Dynamic UPI QR", col5: "₹1,420", badge: { text: "Paid ✓", variant: "paid" }, action: "Print Slip" },
          { col1: "BILL-1042", col2: "Ramesh Provision Store (B2B)", col3: "45 Cartons (FMCG)", col4: "30-Day Credit Ledger", col5: "₹32,000", badge: { text: "Credit Due", variant: "due" }, action: "Tax Invoice" },
          { col1: "BILL-1043", col2: "Anjali Mehta", col3: "Personal Care Bundle", col4: "Card Machine Swipe", col5: "₹680", badge: { text: "Paid ✓", variant: "paid" }, action: "Print Slip" },
          { col1: "BILL-1044", col2: "Green Mart Supermarket", col3: "Wholesale Bulk Dispatch", col4: "Bank RTGS / NEFT", col5: "₹44,500", badge: { text: "Verified ✓", variant: "paid" }, action: "E-Way Bill" },
        ],
      },
      {
        id: "inventory",
        label: "Batch & Expiry Stock",
        icon: "fa-boxes-stacked",
        summaryText: "2,840 Active SKUs · FIFO Batch Deduction with Auto-Expiry Quarantine",
        actionButton: "+ Stock Inward",
        tableHeaders: ["SKU Code", "Product Description", "Current Stock", "Batch Number", "Expiry Date", "Status", "Action"],
        rows: [
          { col1: "SKU-102", col2: "Fortune Sunlite Oil 1L Pouch", col3: "240 Pcs", col4: "BAT-890", col5: "Nov 2027", badge: { text: "Healthy", variant: "paid" }, action: "Barcode Print" },
          { col1: "SKU-105", col2: "Aashirvaad Atta 10kg Bag", col3: "8 Bags", col4: "BAT-410", col5: "Feb 2027", badge: { text: "Low Stock Alert", variant: "notice" }, action: "PO Reorder" },
          { col1: "SKU-210", col2: "Tata Salt Vacuum Evaporated 1kg", col3: "350 Pcs", col4: "BAT-102", col5: "Jan 2028", badge: { text: "Healthy", variant: "paid" }, action: "Barcode Print" },
          { col1: "SKU-304", col2: "Dettol Antiseptic Liquid 500ml", col3: "4 Bottles", col4: "BAT-502", col5: "Oct 2026", badge: { text: "Critical Reorder", variant: "due" }, action: "PO Reorder" },
        ],
      },
      {
        id: "transfers",
        label: "Store & Warehouse Transfers",
        icon: "fa-truck-ramp-box",
        summaryText: "Inter-Store Stock Movement · Central Hub to Retail Branch Outlets",
        actionButton: "+ Create Transfer",
        tableHeaders: ["Transfer #", "Source → Destination", "Item Count", "Vehicle / Transporter", "Gatepass Status", "Status", "Action"],
        rows: [
          { col1: "TRF-801", col2: "Central Warehouse → Indiranagar", col3: "150 Units", col4: "Tata Ace (KA-03-AA-1200)", col5: "Dispatched (10:15 AM)", badge: { text: "In-Transit", variant: "notice" }, action: "Verify Inward" },
          { col1: "TRF-802", col2: "Central Warehouse → MG Road", col3: "80 Units", col4: "Direct Courier Pickup", col5: "Received & Checked", badge: { text: "Reconciled ✓", variant: "paid" }, action: "View Note" },
        ],
      },
      {
        id: "gst",
        label: "E-Invoicing & GSTR-1",
        icon: "fa-file-invoice",
        summaryText: "Automated B2B E-Invoicing with NIC Portal IRN & QR Generation",
        actionButton: "Sync NIC Portal",
        tableHeaders: ["IRN #", "Recipient GSTIN", "Trade Name", "Taxable Value", "GST (CGST/SGST/IGST)", "Status", "Action"],
        rows: [
          { col1: "IRN-4F8A9C...", col2: "29AABCU9603R1ZM", col3: "Ramesh Provision Store", col4: "₹32,000", col5: "₹5,760 (18% GST)", badge: { text: "Generated ✓", variant: "paid" }, action: "Download PDF" },
          { col1: "IRN-7B2C1D...", col2: "29AAACH7409R1ZZ", col3: "Green Mart Supermarket", col4: "₹44,500", col5: "₹5,340 (12% GST)", badge: { text: "Generated ✓", variant: "paid" }, action: "Download PDF" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-barcode", title: "Rapid Barcode POS", desc: "Support for wireless handheld scanners, thermal printers, and weighing scales." },
      { icon: "fa-boxes-stacked", title: "Batch & Expiry Controls", desc: "Prevent expired stock losses with FIFO auto-routing and near-expiry discount alerts." },
      { icon: "fa-file-invoice", title: "Instant GST E-Invoicing", desc: "Direct integration with the GST government portal for B2B e-invoices and e-way bills." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Start free for up to 100 SKUs. Upgrade affordably as your shop or wholesale turnover grows." },
    ],
    setupSteps: [
      { step: 1, title: "Import Inventory Catalog & Barcodes", desc: "Upload product names, barcodes, purchase costs, selling prices, and GST rates using our Excel tool." },
      { step: 2, title: "Connect Barcode Scanners & Printers", desc: "Plug in any USB/Bluetooth thermal printer and barcode reader with zero driver installation." },
      { step: 3, title: "Scan, Bill & Collect Instant UPI", desc: "Generate GST bills in seconds with dynamic UPI QR codes and track real-time stock balances." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Counter Speed",
        icon: "fa-cash-register",
        title: "Sub-Second Barcode POS Billing",
        desc: "Keep checkout queues moving with fast barcode scanning, shortcut hotkeys, and dynamic UPI QR code payments.",
        bullets: [
          "Supports all 1D/2D wireless barcode readers and POS weighing scales.",
          "Dynamic UPI QR code printed directly on cash receipts for zero-MDR settlements.",
          "Offline billing capability: continues scanning and printing even when internet drops.",
        ],
      },
      {
        badge: "Pillar 2: Stock Intelligence",
        icon: "fa-boxes-stacked",
        title: "Batch, Expiry & Multi-Warehouse Tracking",
        desc: "End stock shrinkage and expired goods. Auto-deduct items by FIFO and receive automated purchase order alerts.",
        bullets: [
          "Batch-wise inventory with automated manufacturing and expiry date tracking.",
          "Inter-branch stock transfer notes with transit gatepass validation.",
          "Low-stock purchase order automation linked directly to supplier catalogs.",
        ],
      },
      {
        badge: "Pillar 3: B2B Wholesale",
        icon: "fa-handshake",
        title: "B2B Credit Ledgers & GST E-Invoicing",
        desc: "Manage wholesale distributor credit limits, payment terms, and automated IRN e-invoicing in one interface.",
        bullets: [
          "Customer credit aging reports with automatic credit limit gating.",
          "1-click government IRN generation for B2B e-invoices above ₹5Cr mandate.",
          "Automated E-Way bill generation linked directly to transporter GSTINs.",
        ],
      },
      {
        badge: "Pillar 4: Complete ERP",
        icon: "fa-chart-pie",
        title: "P&L Accounting, Staff Shifts & Payroll",
        desc: "Stop running separate accounting software. Complete financial accounting, cashier shifts, and payroll included.",
        bullets: [
          "End-of-shift cashier closeout balancing drawer cash and digital receipts.",
          "Complete double-entry general ledger with automatic GSTR-1 tax return preparation.",
          "Staff attendance tracking and salary ledger calculation.",
        ],
      },
    ],
    competitorName: "Vyapar / Marg ERP",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 100 SKUs)", competitor: "Paid License from Day 1", traditional: "Excel / Paper Registers" },
      { capability: "Cloud Multi-Store Sync", axoix: "Real-time Multi-Branch Cloud Sync", competitor: "Desktop-Only with Fragile Sync", traditional: "Disconnected Store Data" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "Third-Party Gateway Setup", traditional: "Cash Only / Loose Receipts" },
      { capability: "Public Storefront Engine", axoix: "Included Custom-Domain Online Catalog", competitor: "None (Offline Tool Only)", traditional: "No Digital Presence" },
      { capability: "Staff Attendance & Payroll", axoix: "Built-in Biometric Attendance & Payroll", competitor: "None (Separate Software Needed)", traditional: "Manual Bookkeeping" },
      { capability: "Hardware Compatibility", axoix: "Works on Windows, Android, Mac & iPad", competitor: "Windows Desktop Locked Only", traditional: "Manual Registers" },
    ],
    transparencyPoints: [
      "Zero per-transaction fees on retail UPI payments collected through invoice QR codes.",
      "100% compliant with Indian GST E-Invoicing, E-Way Bill, and GSTR filing standards.",
      "Full daily database backups and encrypted cloud sync hosted in certified Tier-4 Indian datacenters.",
      "Cashier permission controls: employees cannot delete past bills or modify unit costs.",
      "Full exportability of inventory ledgers and customer transaction histories.",
    ],
    faqList: [
      { question: "Does AXOIX support barcode scanners and thermal printers?", answer: "Yes. AXOIX works seamlessly with standard USB and Bluetooth barcode scanners as well as 2-inch and 3-inch ESC/POS thermal receipt printers." },
      { question: "Can I manage multiple retail branches from one dashboard?", answer: "Yes. You can manage central warehouse stocks, view individual store revenues, and transfer stock between branches in real time." },
      { question: "How does batch and expiry date tracking work?", answer: "When receiving new stock, enter the batch number and expiry date. POS sales automatically deduct the oldest batch (FIFO), and items approaching expiry trigger clearance alerts." },
      { question: "Does AXOIX handle wholesale credit accounts?", answer: "Yes. You can set customer credit limits, track outstanding payment terms, and generate itemized statement of accounts for wholesale buyers." },
      { question: "Can I generate E-Invoices and E-Way bills directly?", answer: "Yes. AXOIX connects with the government GST e-invoicing portal to generate authentic IRN numbers and E-Way bills in 1 click." },
    ],
  },

  ecommerce: {
    slug: "ecommerce",
    title: "Ecommerce & D2C Storefront Software",
    subtitle: "Unified omnichannel storefront, multi-courier shipping (Shiprocket/Delhivery), real-time inventory sync, COD fraud prevention, and automated WhatsApp abandoned cart recovery.",
    headerBranchOptions: ["Direct Storefront (0% Fee)", "Shopify & Amazon Sync", "Pan-India Fulfillment"],
    headerQuickAction: "+ New Product",
    kpis: [
      { label: "Today's Orders", value: "184", sub: "+18% vs yesterday" },
      { label: "Online Revenue", value: "₹1,48,500", sub: "Today MTD" },
      { label: "Average Order Value", value: "₹807", sub: "Free ship threshold" },
      { label: "Pending Fulfillment", value: "32 Orders", sub: "Ready to pack" },
      { label: "Shipped Today", value: "146 Packages", sub: "AWBs generated" },
      { label: "Return Rate (RTO)", value: "4.2%", sub: "Industry low" },
      { label: "Abandoned Cart Recovery", value: "18.4%", sub: "Recovered via WhatsApp" },
      { label: "Active Catalog", value: "840 SKUs", sub: "Live storefront" },
    ],
    tabs: [
      {
        id: "orders",
        label: "Live Orders & Processing",
        icon: "fa-shopping-bag",
        badge: "32 Pending",
        badgeVariant: "amber",
        summaryText: "Omnichannel Order Queue · 184 Orders Today across Web, WhatsApp & Marketplaces",
        actionButton: "+ Bulk Manifest",
        tableHeaders: ["Order #", "Customer Name", "Items / Quantity", "Payment Mode", "Order Total", "Status", "Action"],
        rows: [
          { col1: "ORD-9201", col2: "Tanmay Joshi (Mumbai)", col3: "2x Oversized Heavyweight Tee", col4: "Prepaid UPI (GPay)", col5: "₹1,598", badge: { text: "Packed & Labelled", variant: "paid" }, action: "Print Label" },
          { col1: "ORD-9202", col2: "Divya Sharma (New Delhi)", col3: "1x Vegan Leather Tote Bag", col4: "Cash on Delivery (OTP Verified)", col5: "₹2,499", badge: { text: "In Transit", variant: "notice" }, action: "Track AWB" },
          { col1: "ORD-9203", col2: "Rahul Nair (Bengaluru)", col3: "3x Fitness Band Strap", col4: "Prepaid Credit Card", col5: "₹3,897", badge: { text: "Delivered ✓", variant: "paid" }, action: "View Order" },
          { col1: "ORD-9204", col2: "Priya Rao (Pune)", col3: "1x Botanical Face Serum", col4: "COD Confirmed", col5: "₹1,299", badge: { text: "Ready to Pack", variant: "due" }, action: "Generate AWB" },
        ],
      },
      {
        id: "shipping",
        label: "Multi-Courier Shipping & Tracking",
        icon: "fa-truck-fast",
        summaryText: "Automated Courier Selection · Lowest Rate & Fastest Delivery Routing",
        actionButton: "Sync Couriers",
        tableHeaders: ["AWB Number", "Courier Partner", "Order Ref", "Origin → Destination", "Latest Tracking Update", "Status", "Action"],
        rows: [
          { col1: "AWB-SR-9102", col2: "Shiprocket (Delhivery Air)", col3: "ORD-9201", col4: "Bhiwandi Hub → Mumbai South", col5: "Out for Delivery Today", badge: { text: "Out for Delivery", variant: "paid" }, action: "Track Live" },
          { col1: "AWB-BL-4019", col2: "BlueDart Express", col3: "ORD-9203", col4: "Bengaluru Hub → Indiranagar", col5: "Delivered to Customer", badge: { text: "Delivered ✓", variant: "paid" }, action: "View POD" },
          { col1: "AWB-EK-8190", col2: "Ekart Surface", col3: "ORD-9202", col4: "Delhi Hub → Gurugram Hub", col5: "In-Transit (Line Haul)", badge: { text: "In-Transit", variant: "notice" }, action: "Track Live" },
        ],
      },
      {
        id: "abandoned",
        label: "Abandoned Cart Recovery",
        icon: "fa-cart-arrow-down",
        summaryText: "Automated WhatsApp Recovery Funnel · 18.4% Win-Back Rate",
        actionButton: "Broadcast Campaign",
        tableHeaders: ["Cart ID", "Customer Contact", "Cart Value", "Abandoned Items", "Recovery Step", "Status", "Action"],
        rows: [
          { col1: "CART-410", col2: "+91 98112 09876", col3: "₹2,400", col4: "2x Organic Skincare Essentials", col5: "WhatsApp Link Sent (Discount 10%)", badge: { text: "Recovered ✓", variant: "paid" }, action: "View Order" },
          { col1: "CART-411", col2: "+91 98450 12340", col3: "₹1,150", col4: "1x Oversized Hoodie (M)", col5: "Free Shipping Reminder Sent", badge: { text: "Pending Pay", variant: "due" }, action: "Send Offer" },
          { col1: "CART-412", col2: "+91 99001 87654", col3: "₹3,800", col4: "Footwear Sneaker Classic", col5: "Cart Left 30 mins ago", badge: { text: "Queued", variant: "notice" }, action: "Send Now" },
        ],
      },
      {
        id: "inventory",
        label: "Omnichannel Inventory Sync",
        icon: "fa-arrows-rotate",
        summaryText: "Real-Time Stock Synchronization across Website, Amazon & Store",
        actionButton: "+ Quick Adjust",
        tableHeaders: ["SKU Code", "Product Variant", "Direct Storefront", "Amazon India", "Warehouse Buffer", "Status", "Action"],
        rows: [
          { col1: "SKU-V-101", col2: "Heavyweight Tee (M / Black)", col3: "42 Pcs", col4: "42 Pcs (Synced)", col5: "10 Pcs Safety", badge: { text: "Healthy", variant: "paid" }, action: "Edit SKU" },
          { col1: "SKU-V-102", col2: "Heavyweight Tee (L / Black)", col3: "14 Pcs", col4: "14 Pcs (Synced)", col5: "5 Pcs Safety", badge: { text: "Low Stock Alert", variant: "notice" }, action: "PO Dispatch" },
          { col1: "SKU-V-103", col2: "Heavyweight Tee (XL / Black)", col3: "0 Pcs", col4: "0 Pcs (Out of Stock)", col5: "0 Pcs", badge: { text: "Sold Out", variant: "due" }, action: "Restock" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-store", title: "0% Commission Storefront", desc: "Sell directly to your customers with zero platform cuts on transactions." },
      { icon: "fa-truck-fast", title: "Pre-Integrated Shipping", desc: "Connect Shiprocket, Delhivery, and BlueDart in 1 click with automated AWB generation." },
      { icon: "fa-whatsapp", title: "WhatsApp Checkout & Recovery", desc: "Recover abandoned carts and send automated tracking updates straight to WhatsApp." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Start free for up to 100 products. Scale as your daily order volumes grow." },
    ],
    setupSteps: [
      { step: 1, title: "Add Products & Variants", desc: "Upload product photos, descriptions, sizes, colors, and prices in our simple catalog editor." },
      { step: 2, title: "Connect Shipping & Payment Gateways", desc: "Link Shiprocket or Delhivery for courier labels and enable instant zero-fee UPI payments." },
      { step: 3, title: "Launch Storefront & Process Orders", desc: "Share your custom domain storefront link and fulfill customer orders in 1-click manifests." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Storefront",
        icon: "fa-store",
        title: "High-Converting Mobile Storefront",
        desc: "Ultra-fast custom domain ecommerce website optimized for mobile buyers, sub-second page loads, and 1-click UPI checkout.",
        bullets: [
          "Zero commission on sales: 100% of revenue stays in your bank account.",
          "Frictionless 1-click checkout with auto-filled shipping addresses and PhonePe/GPay UPI.",
          "Mobile-first responsive theme with custom branding, logo, and domain.",
        ],
      },
      {
        badge: "Pillar 2: Shipping & AWBs",
        icon: "fa-truck-fast",
        title: "Multi-Courier Automation & Tracking",
        desc: "Automatically compare shipping rates across Delhivery, BlueDart, and Shadowfax, printing shipping labels in bulk.",
        bullets: [
          "1-click shipping label and packing slip printing.",
          "Automated courier rate calculator picking the cheapest and fastest route.",
          "Real-time tracking links sent to customers via SMS and WhatsApp.",
        ],
      },
      {
        badge: "Pillar 3: WhatsApp Automation",
        icon: "fa-whatsapp",
        title: "WhatsApp Cart Recovery & COD Verification",
        desc: "Slash Return to Origin (RTO) rates with automated WhatsApp Cash on Delivery confirmation OTPs.",
        bullets: [
          "Automated WhatsApp messages sent to shoppers who abandon their checkout carts.",
          "COD fraud protection: buyers verify order intent via 1-tap WhatsApp buttons.",
          "Automated post-purchase order status notifications and shipping alerts.",
        ],
      },
      {
        badge: "Pillar 4: Back-Office Suite",
        icon: "fa-chart-pie",
        title: "Inventory Sync, GST & Staff Payroll",
        desc: "Keep multi-channel inventory synchronized, export GSTR-1 sales data, and manage order packing teams.",
        bullets: [
          "Unified inventory sync across your website, Amazon, and offline store.",
          "Automated GST invoicing with SAC/HSN codes and state tax splits.",
          "Warehouse staff shift tracking with packaging SLA monitoring.",
        ],
      },
    ],
    competitorName: "Shopify",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 100 products)", competitor: "Paid Subscription ($29+/mo) + 2% Cut", traditional: "Manual WhatsApp Selling" },
      { capability: "Transaction Commission", axoix: "0% Platform Cut on Direct Sales", competitor: "0.5% - 2% Platform Fee per Order", traditional: "High Marketplace Cuts (15-30%)" },
      { capability: "Automated WhatsApp Recovery", axoix: "Built-in WhatsApp Cart Recovery", competitor: "Requires Paid Third-Party Shopify Apps", traditional: "Manual Calling" },
      { capability: "Integrated Staff HR & Payroll", axoix: "Built-in Warehouse Staff Attendance", competitor: "None (Storefront Only)", traditional: "Fragmented Excel Sheets" },
      { capability: "GST Invoicing & Indian Tax", axoix: "Native Indian GST Engine with HSN Codes", competitor: "Generic Foreign Tax Calculation", traditional: "CA Manual Compilation" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Customer Lists" },
    ],
    transparencyPoints: [
      "Zero percentage commission on orders processed through your branded website.",
      "No mandatory payment gateway lock-in: use PhonePe, Razorpay, Cashfree, or direct UPI.",
      "Full customer ownership: export complete email, phone, and purchase histories anytime.",
      "Hosted on certified Indian cloud servers with enterprise-grade SSL and data encryption.",
      "Automated daily backups with 99.9% storefront uptime guarantee.",
    ],
    faqList: [
      { question: "Do I have to pay transaction fees on customer orders?", answer: "No. Unlike other platforms that charge 1-2% on every transaction, AXOIX charges 0% platform commission on direct customer orders." },
      { question: "How does multi-courier shipping integration work?", answer: "AXOIX connects directly with Shiprocket, Delhivery, and BlueDart. When an order arrives, you can generate AWBs and print shipping labels with 1 click." },
      { question: "Can we reduce COD return rates (RTO)?", answer: "Yes. AXOIX sends an automated WhatsApp message requiring COD customers to confirm their delivery address, filtering out bogus and unreachable orders." },
      { question: "Can I connect my own custom web domain?", answer: "Yes. You can link your custom domain (e.g., yourbrand.com) in 1 click with free automatic SSL encryption." },
      { question: "How does abandoned cart recovery work?", answer: "When a shopper leaves items in their cart without completing payment, AXOIX sends a personalized WhatsApp reminder with an instant checkout link, recovering up to 20% of lost revenue." },
    ],
  },

  logistics: {
    slug: "logistics",
    title: "Logistics, Fleet & Freight Management",
    subtitle: "End-to-end logistics operating system: GPS vehicle dispatch, multi-stop manifest routing, driver advances, trip settlement, and COD cash remittance.",
    headerBranchOptions: ["North Hub / Dispatch", "Western Transshipment Hub", "South Delivery Center"],
    headerQuickAction: "+ Create Manifest",
    kpis: [
      { label: "Active Vehicles", value: "28 / 32", sub: "On active routes" },
      { label: "Shipments in Transit", value: "412", sub: "On schedule 98%" },
      { label: "Deliveries Today", value: "168", sub: "152 Completed" },
      { label: "Fuel Cost / KM", value: "₹14.2", sub: "Optimized routing" },
      { label: "COD Cash in Custody", value: "₹3,42,000", sub: "To reconcile" },
      { label: "Driver Advances Settled", value: "₹48,500", sub: "Fastag & Diesel" },
      { label: "Vehicle Uptime", value: "96.8%", sub: "4 in workshop" },
      { label: "Turnaround SLA", value: "4.2 Hrs", sub: "Intra-city express" },
    ],
    tabs: [
      {
        id: "dispatch",
        label: "Live Dispatch & Route Tracking",
        icon: "fa-truck",
        badge: "28 Active",
        badgeVariant: "green",
        summaryText: "Live GPS Vehicle Operations · 28 Trucks on Road · 412 Consignments in Transit",
        actionButton: "+ New Dispatch",
        tableHeaders: ["Trip #", "Vehicle & Driver", "Route / Corridor", "Consignments", "Fuel / Advance", "Status", "Live Action"],
        rows: [
          { col1: "DISP-401", col2: "MH-04-GP-8102 (14ft Eicher) · Suresh Yadav", col3: "Bhiwandi Hub → Pune Distribution", col4: "42 Consignments (3.8 Tons)", col5: "₹4,500 Fastag + Diesel", badge: { text: "In-Transit (GPS Live)", variant: "paid" }, action: "Track Map" },
          { col1: "DISP-402", col2: "DL-1L-AA-3940 (Tata 407) · Manjeet Singh", col3: "Okhla Phase 2 → Gurugram Sec 18", col4: "18 Consignments (1.2 Tons)", col5: "₹1,800 Diesel Card", badge: { text: "Out for Delivery", variant: "notice" }, action: "Track Map" },
          { col1: "DISP-403", col2: "KA-01-MJ-5521 (Ashok Leyland) · R. Govind", col3: "Peenya Industrial → Whitefield Hub", col4: "24 Consignments (2.4 Tons)", col5: "₹2,200 Fastag", badge: { text: "Delivered & Settled ✓", variant: "paid" }, action: "View POD" },
          { col1: "DISP-404", col2: "GJ-06-BT-9104 (BharatBenz 16T) · Anil Rao", col3: "Ahmedabad → Surat Highway", col4: "68 Consignments (9.5 Tons)", col5: "₹8,500 Advance", badge: { text: "Loading at Bay 3", variant: "due" }, action: "Dispatch" },
        ],
      },
      {
        id: "fleet",
        label: "Fleet Maintenance & Health",
        icon: "fa-wrench",
        summaryText: "32 Commercial Vehicles · Automated Fitness, Insurance & Servicing Alerts",
        actionButton: "+ Log Service",
        tableHeaders: ["Vehicle #", "Make & Capacity", "Fitness Certificate", "Insurance Expiry", "Odometer & Last Service", "Status", "Action"],
        rows: [
          { col1: "MH-04-GP-8102", col2: "Eicher Pro 2049 (4 Ton)", col3: "Valid till Nov 2027", col4: "Active (National)", col5: "142,500 km (1,200 km ago)", badge: { text: "Roadworthy ✓", variant: "paid" }, action: "Vehicle 360°" },
          { col1: "DL-1L-AA-3940", col2: "Tata 407 SFC (2.5 Ton)", col3: "Valid till Mar 2027", col4: "Active (ICICI)", col5: "98,200 km (Brake Inspection)", badge: { text: "Service Due", variant: "notice" }, action: "Book Bay" },
          { col1: "KA-01-MJ-5521", col2: "Ashok Leyland Dost (1.5 Ton)", col3: "Valid till Oct 2026", col4: "Renewal in 18 Days", col5: "64,100 km (Healthy)", badge: { text: "Insurance Alert", variant: "due" }, action: "Renew Policy" },
        ],
      },
      {
        id: "cod",
        label: "COD Remittance & Driver Handover",
        icon: "fa-indian-rupee-sign",
        summaryText: "Cash-on-Delivery Reconciliation · ₹3,42,000 In Custody Balanced with Bank Deposits",
        actionButton: "Reconcile Cash",
        tableHeaders: ["Handover #", "Driver Name", "Vehicle Ref", "Cash Collected", "Settled into Bank", "Variance", "Action"],
        rows: [
          { col1: "COD-912", col2: "Suresh Yadav", col3: "MH-04-GP-8102", col4: "₹42,800", col5: "₹42,800 (Cash Counter Deposit)", badge: { text: "Reconciled ✓", variant: "paid" }, action: "Receipt Voucher" },
          { col1: "COD-913", col2: "Manjeet Singh", col3: "DL-1L-AA-3940", col4: "₹18,400", col5: "Pending Handover", badge: { text: "Driver Custody", variant: "due" }, action: "Collect Cash" },
          { col1: "COD-914", col2: "R. Govind", col3: "KA-01-MJ-5521", col4: "₹64,200", col5: "₹64,200 (Bank Deposit)", badge: { text: "Reconciled ✓", variant: "paid" }, action: "Receipt Voucher" },
        ],
      },
      {
        id: "eway",
        label: "E-Way Bills & Consignment Notes (LR)",
        icon: "fa-file-lines",
        summaryText: "Automated Lorry Receipts (LR) with Government E-Way Bill Verification",
        actionButton: "+ Generate LR",
        tableHeaders: ["LR Number", "Consignor → Consignee", "E-Way Bill Number", "Weight / Metric", "Freight Charges", "Status", "Action"],
        rows: [
          { col1: "LR-8901", col2: "Sun Pharma → Apollo Pharmacy Hub", col3: "EWB-2410...901", col4: "3,800 kg (Medical)", col5: "₹18,400 + 5% GST", badge: { text: "Active E-Way ✓", variant: "paid" }, action: "Print LR" },
          { col1: "LR-8902", col2: "Tata Consumer → BigBasket Hub", col3: "EWB-8812...410", col4: "8,500 kg (FMCG)", col5: "₹34,200 + 5% GST", badge: { text: "Active E-Way ✓", variant: "paid" }, action: "Print LR" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-truck", title: "Real-Time Fleet Dispatch", desc: "GPS tracking, driver mobile app, and instant digital proof of delivery (POD)." },
      { icon: "fa-indian-rupee-sign", title: "Zero Cash Leakage COD", desc: "Driver cash collection reconciliation and automated bank remittance tracking." },
      { icon: "fa-file-lines", title: "Instant E-Way Bills & LR", desc: "1-click Lorry Receipts (LR) with NIC government E-Way Bill verification." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 5 vehicles free forever. Upgrade only as your transport fleet expands." },
    ],
    setupSteps: [
      { step: 1, title: "Add Fleet Vehicles & Drivers", desc: "Register registration numbers, vehicle capacities, driver licenses, and FASTag accounts." },
      { step: 2, title: "Map Delivery Hubs & Corridors", desc: "Set up freight rate cards, transit turnaround SLAs, and regular distribution routes." },
      { step: 3, title: "Dispatch Trips & Track Deliveries", desc: "Generate consignment notes, track live trips, and collect digital proof of delivery." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Fleet Dispatch",
        icon: "fa-truck",
        title: "Intelligent Dispatch & Route Planning",
        desc: "Optimize multi-drop vehicle routes, assign qualified drivers, and monitor delivery progress with live GPS updates.",
        bullets: [
          "Vehicle weight capacity optimization preventing under-utilization.",
          "Driver trip advances for FASTag, toll gates, and diesel expenses.",
          "Digital Proof of Delivery (e-POD) with consignee signature and photo capture.",
        ],
      },
      {
        badge: "Pillar 2: Maintenance & Costs",
        icon: "fa-wrench",
        title: "Preventive Fleet Maintenance & OEE",
        desc: "Prevent costly highway breakdowns with mileage-based maintenance schedules, tire tracking, and document renewals.",
        bullets: [
          "Automated alerts for expiring commercial vehicle fitness, insurance, and road tax.",
          "Diesel fuel efficiency monitoring flagging sudden fuel consumption spikes.",
          "Workshop job cards and spare parts inventory tracking.",
        ],
      },
      {
        badge: "Pillar 3: Cash & COD",
        icon: "fa-indian-rupee-sign",
        title: "Rigorous COD Cash Reconciliation",
        desc: "Eliminate cash leakage between field drivers, regional sorting hubs, and corporate bank accounts.",
        bullets: [
          "End-of-day driver cash settlement balancing invoice cash with bank deposits.",
          "Instant discrepancy detection with manager sign-off workflows.",
          "Automated customer refund and return to origin (RTO) management.",
        ],
      },
      {
        badge: "Pillar 4: Regulatory Compliance",
        icon: "fa-file-lines",
        title: "GST E-Way Bills & Indian Transport Laws",
        desc: "Full compliance with Ministry of Road Transport directives and GST E-Way Bill transit requirements.",
        bullets: [
          "Instant Lorry Receipt (LR) generation compliant with Indian Goods Transport regulations.",
          "Automated Part-B E-Way bill updates when vehicles are changed in-transit.",
          "Driver attendance, daily bata allowances, and payroll calculation.",
        ],
      },
    ],
    competitorName: "FarEye",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 5 vehicles)", competitor: "Expensive Enterprise SaaS Setup", traditional: "Excel / Register Books" },
      { capability: "Integrated Back-Office", axoix: "Built-in Driver Payroll, HR & Accounts", competitor: "Standalone Dispatch Only", traditional: "Disconnected Software" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "Payment Gateway Integration Cuts", traditional: "Loose Cash / Screenshots" },
      { capability: "COD Cash Ledger", axoix: "Included Driver Custody Reconciliation", competitor: "Paid Specialty Add-on", traditional: "Manual Ledger Discrepancies" },
      { capability: "E-Way Bill & LR Engine", axoix: "Native NIC Portal & E-Way Part-B Sync", competitor: "Manual External Generation", traditional: "Paper Pad Lorry Receipts" },
      { capability: "Hardware Agnostic", axoix: "Works on Driver Smartphones & GPS Units", competitor: "Expensive Proprietary GPS Hardware", traditional: "Manual Calling Drivers" },
    ],
    transparencyPoints: [
      "No per-trip transaction commissions on direct freight invoices or consignments.",
      "100% compliant with Indian Carriage by Road Act and GST E-Way regulations.",
      "Driver data privacy maintained with location tracking limited to active duty shifts.",
      "Full exportability of fleet trip logs, fuel consumption, and financial ledgers.",
      "Encrypted cloud hosting in certified Indian Tier-4 datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX integrate with our vehicle GPS devices?", answer: "Yes. AXOIX integrates with standard AIS-140 and OBD GPS trackers as well as driver smartphone location services." },
      { question: "How does driver trip settlement work?", answer: "Before dispatch, log fuel and FASTag advances. At the end of the trip, the driver submits toll slips and fuel receipts, and the balance reconciles automatically." },
      { question: "Can we generate official Lorry Receipts (LR) and E-Way bills?", answer: "Yes. Generate GST-compliant consignment notes (LR) with auto-filled Part-A and Part-B E-Way bill information in 1 click." },
      { question: "How does AXOIX prevent COD cash leakage?", answer: "Every cash collection is tied to the driver's active trip folio. Drivers cannot close their shift until physical cash matches the exact system collection amount." },
      { question: "Can we track vehicle fitness and insurance expiry?", answer: "Yes. Automated alerts notify your transport manager 30, 15, and 3 days before any vehicle fitness, permit, or insurance policy expires." },
    ],
  },

  manufacturing: {
    slug: "manufacturing",
    title: "Manufacturing & Factory Operations (ERP)",
    subtitle: "Complete shop floor control: multi-level Bill of Materials (BOM), work order routing, machine maintenance OEE, raw material batch tracking, and quality QC inspection.",
    headerBranchOptions: ["Plant 1 (Lines 1-8)", "Fabrication Unit", "Assembly & Dispatch"],
    headerQuickAction: "+ New Work Order",
    kpis: [
      { label: "Active Work Orders", value: "14", sub: "On shop floor" },
      { label: "Equipment OEE", value: "86.4%", sub: "World class" },
      { label: "Today's Output", value: "1,850 Units", sub: "Target: 2,000" },
      { label: "QC Rejection Rate", value: "0.8%", sub: "Passed 99.2%" },
      { label: "Raw Material Stock", value: "₹64.2L", sub: "18 Days reserve" },
      { label: "Machine Downtime", value: "42 Mins", sub: "Preventive service" },
      { label: "Workstations Active", value: "8 / 8 Lines", sub: "Shift A running" },
      { label: "Dispatched MTD", value: "₹1.18Cr", sub: "Finished goods" },
    ],
    tabs: [
      {
        id: "shopfloor",
        label: "Live Shop Floor & Work Orders",
        icon: "fa-industry",
        badge: "14 Active",
        badgeVariant: "green",
        summaryText: "Shop Floor Routing · 8 Active Lines · Target Turnaround SLA 94%",
        actionButton: "+ Release Work Order",
        tableHeaders: ["Work Order #", "Product Assembly", "Workstation Line", "Batch Target", "Produced Units", "Status", "Action"],
        rows: [
          { col1: "WO-2026-41", col2: "Precision Brass Valve 2-Inch", col3: "Line 03 (CNC Milling)", col4: "500 Pcs", col5: "380 Pcs (76%)", badge: { text: "In Production", variant: "paid" }, action: "Log Output" },
          { col1: "WO-2026-42", col2: "Stainless Steel Flange 150#", col3: "Line 01 (Lathe Section)", col4: "250 Pcs", col5: "240 Pcs (96%)", badge: { text: "Near Completion", variant: "notice" }, action: "QC Handover" },
          { col1: "WO-2026-43", col2: "Hydraulic Cylinder Piston", col3: "Line 04 (Assembly Bay)", col4: "100 Pcs", col5: "35 Pcs (35%)", badge: { text: "In Production", variant: "paid" }, action: "Log Output" },
          { col1: "WO-2026-44", col2: "High-Pressure Seal Ring Kit", col3: "Line 02 (Molding Press)", col4: "1,000 Pcs", col5: "0 Pcs (Staged)", badge: { text: "Material Staged", variant: "due" }, action: "Start Run" },
        ],
      },
      {
        id: "bom",
        label: "Bill of Materials (BOM)",
        icon: "fa-cubes",
        summaryText: "Multi-Level Engineering BOMs · Automated Raw Material Consumption Deduction",
        actionButton: "+ Create BOM",
        tableHeaders: ["BOM Code", "Assembly Description", "Child Components", "Raw Materials Required", "Scrap Allowance", "Status", "Action"],
        rows: [
          { col1: "BOM-PV-01", col2: "Brass Ball Valve 2-Inch", col3: "5 Child Parts", col4: "Forged Brass C360 + Teflon Seal", col5: "2.1% Scrap Allowance", badge: { text: "Active Rev 3", variant: "paid" }, action: "Cost Explode" },
          { col1: "BOM-SF-02", col2: "Stainless Steel Flange 4-Inch", col3: "3 Child Parts", col4: "SS316 Ingot Forging", col5: "1.5% Scrap Allowance", badge: { text: "Active Rev 2", variant: "paid" }, action: "Cost Explode" },
          { col1: "BOM-HC-04", col2: "Hydraulic Piston 80mm", col3: "8 Child Parts", col4: "Chrome Plated Rod + Nitrile Seal", col5: "0.8% Scrap Allowance", badge: { text: "Active Rev 1", variant: "paid" }, action: "Cost Explode" },
        ],
      },
      {
        id: "quality",
        label: "Quality Assurance & QC",
        icon: "fa-circle-check",
        summaryText: "In-Process & Finished Goods Quality Testing · Rejection Rate 0.8%",
        actionButton: "+ QC Inspection",
        tableHeaders: ["QC Report #", "Inspection Stage", "Sample Size", "Tolerance / Parameter", "Defect Count", "Status", "Action"],
        rows: [
          { col1: "QC-841", col2: "WO-2026-41 (Brass Valve)", col3: "25 Pcs Sample", col4: "Dimension ±0.02mm · Pressure 16 Bar", col5: "0 Defects", badge: { text: "Passed ✓", variant: "paid" }, action: "QC Certificate" },
          { col1: "QC-842", col2: "WO-2026-42 (SS Flange)", col3: "10 Pcs Sample", col4: "Tensile Strength 580 MPa", col5: "0 Defects", badge: { text: "Passed ✓", variant: "paid" }, action: "QC Certificate" },
          { col1: "QC-843", col2: "Raw Material Inward (SS Ingot)", col3: "5 Ingots", col4: "Chemical Composition Spec", col5: "1 Defect (Surface Pitting)", badge: { text: "Quarantine Hold", variant: "due" }, action: "Vendor NCR" },
        ],
      },
      {
        id: "equipment",
        label: "Machine Health & OEE",
        icon: "fa-screwdriver-wrench",
        summaryText: "Preventive Machine Maintenance · Total Factory OEE 86.4%",
        actionButton: "+ Maintenance Job",
        tableHeaders: ["Equipment ID", "Machine Description", "Current Shift Runtime", "OEE Rating", "Next Scheduled Service", "Status", "Action"],
        rows: [
          { col1: "CNC-01", col2: "Haas VF2 4-Axis Vertical Machining", col3: "7.2 Hours", col4: "91.2% OEE", col5: "In 140 Hours (Oil Change)", badge: { text: "Operating ✓", variant: "paid" }, action: "View Telemetry" },
          { col1: "LATHE-02", col2: "Colchester Heavy Duty Turning Lathe", col3: "6.8 Hours", col4: "84.5% OEE", col5: "In 65 Hours", badge: { text: "Operating ✓", variant: "paid" }, action: "View Telemetry" },
          { col1: "PRESS-01", col2: "100-Ton Hydraulic Stamping Press", col3: "5.4 Hours", col4: "78.0% OEE", col5: "Preventive Inspection Due", badge: { text: "Service Queued", variant: "notice" }, action: "Schedule PM" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-industry", title: "Visual Shop Floor", desc: "Track work orders across cutting, machining, assembly, and testing lines." },
      { icon: "fa-cubes", title: "Multi-Level BOM", desc: "Automated Bill of Materials exploding raw material requirements and scrap margins." },
      { icon: "fa-circle-check", title: "In-Process QC Checklists", desc: "Stage-gate quality testing preventing defective units from reaching customer shipments." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Run up to 2 production lines free forever. Scale as your factory output grows." },
    ],
    setupSteps: [
      { step: 1, title: "Create Workstations & Machines", desc: "Map your factory machinery, rated capacities, shift timings, and maintenance intervals." },
      { step: 2, title: "Define Bills of Materials (BOM)", desc: "Build multi-level BOMs with component raw materials, scrap tolerances, and routing operations." },
      { step: 3, title: "Release Work Orders & Track Production", desc: "Issue production jobs, record operator outputs, and auto-deduct raw material inventory." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Production Control",
        icon: "fa-industry",
        title: "Work Order Routing & Shop Floor Control",
        desc: "Gain real-time visibility into every manufacturing stage, tracking actual cycle times against engineering standards.",
        bullets: [
          "Stage-by-stage work order routing from raw material cutting to finished dispatch.",
          "Shop floor tablet interface for operators to log hourly piece output.",
          "Automated scrap and wastage tracking comparing actual vs theoretical consumption.",
        ],
      },
      {
        badge: "Pillar 2: Bill of Materials",
        icon: "fa-cubes",
        title: "Multi-Level BOM & Material Requirements (MRP)",
        desc: "Prevent production halts caused by missing hardware. Explode nested BOMs and auto-generate supplier purchase orders.",
        bullets: [
          "Nested sub-assembly hierarchies with precise raw material weight requirements.",
          "Automated Material Requirements Planning (MRP) forecasting purchase needs.",
          "Batch costing factoring raw material prices, machine hours, and labor overheads.",
        ],
      },
      {
        badge: "Pillar 3: Quality Control",
        icon: "fa-circle-check",
        title: "Stage-Gate Quality & ISO Compliance",
        desc: "Enforce strict quality gates at raw material inward, first-piece inspection, and final pre-dispatch packing.",
        bullets: [
          "Configurable QC parameter sheets with tolerance limits and inspection instruments.",
          "Non-Conformance Reports (NCR) triggering automated vendor debit notes.",
          "Batch traceability tracking finished serial numbers back to raw heat lots.",
        ],
      },
      {
        badge: "Pillar 4: Plant & Operations",
        icon: "fa-screwdriver-wrench",
        title: "Preventive OEE & Factory Financials",
        desc: "Maximize machine uptime with scheduled maintenance routines, shift attendance, and GST tax invoicing.",
        bullets: [
          "Overall Equipment Effectiveness (OEE) tracking machine availability and performance.",
          "Factory worker biometric attendance, shift rotations, and overtime payroll.",
          "GST tax invoicing with HSN classification and government e-invoicing export.",
        ],
      },
    ],
    competitorName: "Katana ERP",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 2 production lines)", competitor: "Starts at ₹25,000+/mo + Setup Fees", traditional: "Excel Worksheets / Whiteboards" },
      { capability: "Integrated Back-Office", axoix: "Built-in Factory Payroll, HR & Accounts", competitor: "Manufacturing Only (No HR/Accounts)", traditional: "Disconnected Accounting" },
      { capability: "Shop Floor Mobile Tablets", axoix: "Included Responsive Operator Terminal", competitor: "Expensive Per-User Barcode Licenses", traditional: "Paper Job Route Cards" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "None (B2B Bank Transfer Only)", traditional: "Loose Cheques / Manual Entry" },
      { capability: "Indian Tax & E-Way Bills", axoix: "Native Indian GST, HSN & E-Way Engine", competitor: "Generic Foreign Tax Modules", traditional: "CA Manual Calculations" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Paper Logs" },
    ],
    transparencyPoints: [
      "No per-work-order transaction fees or limits on production output volume.",
      "100% compliant with Indian Factory Act attendance rules and GST E-Invoicing mandates.",
      "Full exportability of production histories, quality inspection logs, and machine metrics.",
      "Role-based plant access: machine operators cannot view customer pricing or financial ledgers.",
      "Encrypted cloud hosting in certified Indian Tier-4 datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX handle multi-level Bills of Materials (BOM)?", answer: "Yes. AXOIX supports complex, deeply nested BOMs with sub-assemblies, raw material specifications, labor minutes, and scrap percentages." },
      { question: "How do shop floor operators log their daily production output?", answer: "Operators use a simple touch tablet interface or barcode scanner at each workstation to log completed piece counts and record scrap in real time." },
      { question: "Does AXOIX calculate true batch manufacturing cost?", answer: "Yes. The costing engine combines raw material purchase costs, machine run-time depreciation, and labor hours to give you exact per-unit production costs." },
      { question: "Can we track machine maintenance and prevent breakdowns?", answer: "Yes. Log run-time hours and trigger automated preventive maintenance tickets before machine failures occur." },
      { question: "Is quality inspection (QC) integrated with inventory?", answer: "Yes. Rejected batches are automatically placed into quarantine hold, preventing defective parts from moving to the next workstation or shipping to customers." },
    ],
  },

  "real-estate": {
    slug: "real-estate",
    title: "Real Estate CRM & Property Developer Suite",
    subtitle: "Complete developer & brokerage OS: visual unit stacking matrix, site visit scheduler, lead scoring, automated demand letters, RERA milestone payment tracking, and channel partner commissions.",
    headerBranchOptions: ["All Projects (3 Active)", "Skyline Towers (Phase 1)", "Greenfield Luxury Villas"],
    headerQuickAction: "+ Book Site Visit",
    kpis: [
      { label: "Total Units", value: "240 Units", sub: "Across 3 projects" },
      { label: "Units Booked", value: "188 Units", sub: "78.3% absorbed" },
      { label: "Site Visits Today", value: "18 Scheduled", sub: "12 Completed" },
      { label: "Collections MTD", value: "₹4.25Cr", sub: "Milestone slabs" },
      { label: "Outstanding Dues", value: "₹82.4L", sub: "Demand notes sent" },
      { label: "Broker Partners", value: "48 CP Firms", sub: "RERA registered" },
      { label: "Cost Per Booking", value: "₹42,000", sub: "Marketing CAC" },
      { label: "Token Pipeline", value: "₹14.8Cr", sub: "Hot leads" },
    ],
    tabs: [
      {
        id: "inventory",
        label: "Unit Stacking Matrix",
        icon: "fa-building",
        badge: "78% Sold",
        badgeVariant: "green",
        summaryText: "Tower A, B & C Inventory · 188 Sold · 14 Token Stage · 38 Available Units",
        actionButton: "+ Add Unit",
        tableHeaders: ["Unit #", "Project & Tower", "Configuration / Carpet Area", "Agreement Value", "Buyer / Lead Name", "Status", "Action"],
        rows: [
          { col1: "Unit 402", col2: "Skyline Towers (Tower A)", col3: "3 BHK Luxury (1,850 sq.ft)", col4: "₹1.45Cr", col5: "Vikramaditya Singhania", badge: { text: "Agreement Signed ✓", variant: "paid" }, action: "View Ledger" },
          { col1: "Unit 705", col2: "Skyline Towers (Tower B)", col3: "2 BHK Premium (1,280 sq.ft)", col4: "₹98.5L", col5: "Dr. Rajiv Sen", badge: { text: "Token Paid (₹5L)", variant: "notice" }, action: "Issue Demand" },
          { col1: "Unit 1102", col2: "Skyline Towers (Tower A)", col3: "4 BHK Penthouse (3,100 sq.ft)", col4: "₹2.85Cr", col5: "Available (Hot Inquiries)", badge: { text: "Available", variant: "paid" }, action: "Book Visit" },
          { col1: "Unit 301", col2: "Greenfield Villas (Plot 14)", col3: "3 BHK Independent Villa", col4: "₹1.65Cr", col5: "Amitabh Verma", badge: { text: "Site Visit Today", variant: "due" }, action: "Lead Sheet" },
        ],
      },
      {
        id: "sitevisits",
        label: "Site Visits & Escorted Tours",
        icon: "fa-car",
        summaryText: "18 Site Visits Today · Driver Cab Dispatch & Sales Manager Assignment",
        actionButton: "+ Schedule Visit",
        tableHeaders: ["Visit ID", "Prospective Buyer", "Project of Interest", "Assigned Sales Rep", "Transport Arrangement", "Status", "Action"],
        rows: [
          { col1: "SV-901", col2: "Amitava Roy (+91 98110...)", col3: "Skyline Towers", col4: "Rahul Verma (Sr Sales)", col5: "Executive Cab Pick (Bandra)", badge: { text: "Completed · Highly Interested", variant: "paid" }, action: "Send Token Link" },
          { col1: "SV-902", col2: "Dr. Radhika Sen (+91 98220...)", col3: "Greenfield Villas", col4: "Sunita P.", col5: "Self Drive Arrival", badge: { text: "Tour in Progress", variant: "notice" }, action: "Log Feedback" },
          { col1: "SV-903", col2: "Karan Mehra (+91 99010...)", col3: "Skyline Towers", col4: "Rahul Verma", col5: "Family Re-Visit", badge: { text: "Scheduled 04:30 PM", variant: "due" }, action: "Confirm Cab" },
        ],
      },
      {
        id: "milestones",
        label: "Construction Slabs & Demand Letters",
        icon: "fa-file-invoice-dollar",
        summaryText: "RERA Milestone Slabs · Automated Demand Letters & Escrow Bank Reconciliation",
        actionButton: "+ Raise Demand",
        tableHeaders: ["Demand Note #", "Unit # & Owner", "Milestone Stage", "Demand Amount", "Escrow Account", "Status", "Action"],
        rows: [
          { col1: "DEM-101", col2: "Unit 402 · V. Singhania", col3: "5th Floor Slab Cast", col4: "₹14,50,000", col5: "HDFC RERA Escrow", badge: { text: "Paid via RTGS ✓", variant: "paid" }, action: "Tax Receipt" },
          { col1: "DEM-102", col2: "Unit 501 · Meenakshi Rao", col3: "Plaster Completion", col4: "₹9,80,000", col5: "HDFC RERA Escrow", badge: { text: "Due in 7 Days", variant: "notice" }, action: "WhatsApp Notice" },
          { col1: "DEM-103", col2: "Unit 204 · Suresh Nair", col3: "Possession Handover", col4: "₹12,40,000", col5: "HDFC RERA Escrow", badge: { text: "Overdue 14 Days", variant: "due" }, action: "Send Reminder" },
        ],
      },
      {
        id: "commissions",
        label: "Channel Partner (CP) Payouts",
        icon: "fa-handshake",
        summaryText: "48 RERA Channel Partners · Automated Brokerage Slabs with 5% TDS Deduction",
        actionButton: "+ Register CP",
        tableHeaders: ["Partner ID", "Channel Partner Firm", "RERA Reg #", "Units Booked", "Brokerage Slab", "Status", "Action"],
        rows: [
          { col1: "CP-301", col2: "PropTiger Realty Pvt Ltd", col3: "RERA: A51900004120", col4: "2 Units (402, 501)", col5: "2% (₹4,87,000 - TDS)", badge: { text: "Dispatched ✓", variant: "paid" }, action: "Voucher Slip" },
          { col1: "CP-302", col2: "Anarock Property Consultants", col3: "RERA: A51800001090", col4: "1 Unit (Unit 705)", col5: "2% (₹1,97,000)", badge: { text: "Token Approved", variant: "notice" }, action: "Process Payout" },
          { col1: "CP-303", col2: "Square Yards Partners", col3: "RERA: A52000008814", col4: "1 Unit (Plot 14)", col5: "2.5% (₹4,12,500)", badge: { text: "Agreement Pending", variant: "due" }, action: "View Deal" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-building", title: "Visual Unit Matrix", desc: "Interactive floor-wise stacking chart showing sold, blocked, and vacant units." },
      { icon: "fa-file-invoice-dollar", title: "RERA Milestone Demands", desc: "Automated slab completion notices linking bank escrow accounts with zero manual math." },
      { icon: "fa-handshake", title: "Channel Partner Portal", desc: "Dedicated broker login to track lead registrations, site visits, and commission payouts." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 25 property listings free forever. Scale up as project phases launch." },
    ],
    setupSteps: [
      { step: 1, title: "Configure Projects & Towers", desc: "Set up project wings, floor stacking matrices, unit carpet areas, and base selling prices." },
      { step: 2, title: "Define RERA Payment Milestones", desc: "Enter construction milestone slabs (Foundation, Plinth, Slabs) and escrow bank account details." },
      { step: 3, title: "Log Inquiries & Send Demand Notices", desc: "Capture buyer leads from portals, schedule site visits, and generate automated demand letters." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Inventory Management",
        icon: "fa-building",
        title: "Interactive Unit Stacking Matrix",
        desc: "Empower sales teams with real-time floor plans, preventing double bookings and showing live prices with car parking & floor rise.",
        bullets: [
          "Color-coded inventory grid showing sold, token-locked, and available apartments.",
          "Automatic calculation of GST, stamp duty, registration, and clubhouse maintenance fees.",
          "Digital cost sheet generator exported directly to prospective buyer WhatsApp.",
        ],
      },
      {
        badge: "Pillar 2: Lead Engine",
        icon: "fa-car",
        title: "Site Visit CRM & Lead Scoring",
        desc: "Capture buyer inquiries from 99acres, Magicbricks, and Google Ads, scoring high-intent buyers and dispatching site visit cabs.",
        bullets: [
          "Automated lead assignment to sales managers with strict response time SLAs.",
          "Driver cab booking and visitor check-in logging at sales gallery entrance.",
          "WhatsApp follow-up sequences with project brochures and walkthrough videos.",
        ],
      },
      {
        badge: "Pillar 3: Billing & Collections",
        icon: "fa-file-invoice-dollar",
        title: "RERA Milestones & Demand Notices",
        desc: "Raise construction-linked payment demands in 1 click as site engineers sign off on structural milestone certificates.",
        bullets: [
          "Demand letters with automated interest calculations for overdue payments.",
          "Integrated bank current and RERA escrow accounts for statutory compliance.",
          "Instant receipt vouchers and client payment history ledgers.",
        ],
      },
      {
        badge: "Pillar 4: Broker Network",
        icon: "fa-handshake",
        title: "Channel Partner (CP) Payout Engine",
        desc: "Maintain strong broker loyalty with transparent lead protection, automated commission tracking, and statutory TDS deduction.",
        bullets: [
          "Lead tagging preventing duplicate broker commission claims.",
          "Tiered brokerage structures paying bonuses on rapid volume absorption.",
          "Direct broker bank transfer records with Form 16A TDS certificate exports.",
        ],
      },
    ],
    competitorName: "Sell.Do",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 25 units)", competitor: "Hefty Enterprise Fees (₹50,000+/mo)", traditional: "Excel Inventory Sheets" },
      { capability: "Interactive Stacking Matrix", axoix: "Included Visual Floor-by-Floor Matrix", competitor: "Paid Specialty Custom Module", traditional: "Manual Whiteboard Grid" },
      { capability: "Direct UPI Token Booking", axoix: "Zero Fee Dynamic QR Directly to Escrow A/C", competitor: "High Payment Gateway Deductions", traditional: "Paper Cheques / Wire Screenshots" },
      { capability: "Integrated Staff HR & Payroll", axoix: "Built-in Site Engineer & Staff Payroll", competitor: "None (Sales CRM Only)", traditional: "Separate Excel Sheets" },
      { capability: "RERA Escrow Multi-Bank", axoix: "Native Indian Escrow & Milestone Engine", competitor: "Generic US/European Billing Logic", traditional: "CA Manual Compilation" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Vulnerable Physical Files" },
    ],
    transparencyPoints: [
      "No percentage cuts on property bookings or construction milestone collection amounts.",
      "100% compliant with Indian Real Estate (Regulation and Development) Act (RERA) rules.",
      "Complete data ownership: export buyer registries and financial audit ledgers anytime.",
      "Strict data privacy: sales reps cannot export buyer phone lists or confidential contracts.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX integrate with real estate portals like 99acres and Magicbricks?", answer: "Yes. Inquiries from property portals, Google Ads, and Facebook campaigns flow directly into the CRM with automated salesperson assignment." },
      { question: "How does the interactive unit stacking matrix prevent double bookings?", answer: "When a salesperson locks a unit with a token amount, it immediately displays as 'Locked' across all sales team screens, preventing conflicting bookings." },
      { question: "Can we generate RERA-compliant milestone demand notices?", answer: "Yes. When site engineers mark a construction slab complete, AXOIX generates branded demand letters with payment deadlines and bank escrow details." },
      { question: "How does the channel partner (broker) management work?", answer: "Brokers can register leads through a dedicated portal. The system timestamps the inquiry, protecting the broker's commission claim for 60-90 days." },
      { question: "Can buyers pay booking token fees via UPI?", answer: "Yes. You can generate a dynamic UPI QR code for the exact token amount (e.g., ₹50,000) that deposits directly into your project bank account." },
    ],
  },

  "car-dealer": {
    slug: "car-dealer",
    title: "Car Dealership & Showroom Management System",
    subtitle: "End-to-end auto retail OS: showroom vehicle stock, VIN vehicle tracking, test drive booking diary, RTO registration workflow, auto loan financing, and service bay job cards.",
    headerBranchOptions: ["Main Showroom & Yard", "Pre-Owned Auto Wing", "Central Workshop Bays"],
    headerQuickAction: "+ Book Test Drive",
    kpis: [
      { label: "Showroom Inventory", value: "42 Cars", sub: "₹6.8Cr Value" },
      { label: "Test Drives Today", value: "14 Scheduled", sub: "9 Completed" },
      { label: "Bookings This Month", value: "28 Vehicles", sub: "₹4.4Cr Gross" },
      { label: "Deliveries Today", value: "6 Vehicles", sub: "PDI Complete" },
      { label: "Auto Loan Approvals", value: "88% Rate", sub: "HDFC, SBI, ICICI" },
      { label: "Active Workshop Bays", value: "8 / 10 Bays", sub: "24 Service jobs" },
      { label: "RTO Registration", value: "4.2 Days", sub: "Number plate issued" },
      { label: "Insurance Renewals", value: "34 This Month", sub: "₹8.4L Gross" },
    ],
    tabs: [
      {
        id: "inventory",
        label: "Showroom Stock & VIN Tracking",
        icon: "fa-car",
        badge: "42 Cars",
        badgeVariant: "green",
        summaryText: "Showroom Floor & Stock Yard Inventory · 42 Vehicles Available for Delivery",
        actionButton: "+ Inward Vehicle",
        tableHeaders: ["VIN / Chassis #", "Vehicle Model & Variant", "Color / Fuel Type", "Stock Location", "Ex-Showroom Price", "Status", "Action"],
        rows: [
          { col1: "VIN-9201", col2: "Hyundai Creta SX(O) 1.5 Turbo", col3: "Abyss Black · Petrol DCT", col4: "Showroom Display Ramp", col5: "₹18,40,000", badge: { text: "Available for Sale", variant: "paid" }, action: "Create Quotation" },
          { col1: "VIN-9202", col2: "Mahindra XUV700 AX7L Diesel", col3: "Everest White · Auto AWD", col4: "Yard Bay 14 (Transit In)", col5: "₹24,20,000", badge: { text: "Booked (Token Paid)", variant: "notice" }, action: "Loan File" },
          { col1: "VIN-9203", col2: "Tata Nexon Fearless+ DT", col3: "Daytona Grey · Petrol MT", col4: "Showroom Floor", col5: "₹14,80,000", badge: { text: "Available for Sale", variant: "paid" }, action: "Create Quotation" },
          { col1: "VIN-9204", col2: "Maruti Brezza ZXi+ Dual Tone", col3: "Splendid Silver · Hybrid", col4: "PDI Inspection Bay", col5: "₹12,60,000", badge: { text: "PDI in Progress", variant: "due" }, action: "PDI Checklist" },
        ],
      },
      {
        id: "testdrives",
        label: "Test Drive Diary & Lead Logs",
        icon: "fa-gauge-high",
        summaryText: "14 Test Drives Scheduled Today · Driving License Verification Active",
        actionButton: "+ Log Test Drive",
        tableHeaders: ["Test Drive ID", "Customer Name", "Vehicle Requested", "Assigned Sales Rep", "DL Verification", "Status", "Action"],
        rows: [
          { col1: "TD-401", col2: "Dr. Nikhil Agarwal (+91 98110...)", col3: "Hyundai Creta SX(O)", col4: "Aman Sharma (Exec)", col5: "DL Verified ✓ (DigiLocker)", badge: { text: "Completed · Booking Token Paid", variant: "paid" }, action: "View Lead" },
          { col1: "TD-402", col2: "Sunita Menon (+91 98450...)", col3: "Tata Nexon EV Max", col4: "Aman Sharma", col5: "DL Verified ✓", badge: { text: "On Road (12km Route)", variant: "notice" }, action: "Live Route" },
          { col1: "TD-403", col2: "Rajesh Khanna (+91 99010...)", col3: "Mahindra XUV700", col4: "Vikram R.", col5: "DL Verified ✓", badge: { text: "Scheduled 04:30 PM", variant: "due" }, action: "Stage Vehicle" },
        ],
      },
      {
        id: "delivery",
        label: "PDI & Customer Delivery Ramp",
        icon: "fa-ribbon",
        summaryText: "Pre-Delivery Inspection (PDI) 120-Point Audit & Vehicle Delivery Schedule",
        actionButton: "+ Schedule Delivery",
        tableHeaders: ["Delivery Ref", "Customer Name", "Vehicle Allocated", "120-Point PDI", "FASTag & HSRP", "Status", "Action"],
        rows: [
          { col1: "DEL-101", col2: "Dr. Nikhil Agarwal", col3: "Hyundai Creta SX(O)", col4: "120 Points Passed ✓", col5: "Affixed (HSRP Fitted)", badge: { text: "Ready on Ramp (5:00 PM)", variant: "paid" }, action: "Delivery Gatepass" },
          { col1: "DEL-102", col2: "Rohit Bansal", col3: "Tata Nexon DT", col4: "Passed ✓ (Ceramic Coated)", col5: "FASTag Active", badge: { text: "Ready on Ramp", variant: "paid" }, action: "Delivery Gatepass" },
        ],
      },
      {
        id: "workshop",
        label: "Service Bays & Repair Job Cards",
        icon: "fa-screwdriver-wrench",
        summaryText: "Automated Workshop Operations · 10 Service Bays · 24 Jobs in Progress",
        actionButton: "+ New Job Card",
        tableHeaders: ["Job Card #", "Service Bay", "Vehicle Reg # / Model", "Service Type", "Service Advisor", "Status", "Action"],
        rows: [
          { col1: "JOB-8901", col2: "Bay 02 (Hydraulic Ramp)", col3: "MH-02-CP-1209 (Kia Seltos)", col4: "30,000 km Periodic Service", col5: "Vikram R. (Advisor)", badge: { text: "Washing & Polishing", variant: "notice" }, action: "Bill Invoice" },
          { col1: "JOB-8902", col2: "Bay 04 (Electrical Bay)", col3: "DL-3C-AZ-4411 (Hyundai Verna)", col4: "Brake Pad + AC Coil Service", col5: "Vikram R.", badge: { text: "Work in Progress", variant: "paid" }, action: "View Spares" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-car", title: "Complete VIN Inventory", desc: "Track every car from factory dispatch through yard staging to customer delivery." },
      { icon: "fa-gauge-high", title: "Test Drive Diary", desc: "Digital driving license verification and GPS route logging for every customer trial." },
      { icon: "fa-wrench", title: "Workshop Service Bays", desc: "Integrated parts inventory, labor rate cards, and digital service job cards." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 15 vehicles free forever. Scale as your automotive dealership grows." },
    ],
    setupSteps: [
      { step: 1, title: "Add Vehicle Stock & VINs", desc: "Enter vehicle makes, variants, colors, chassis VINs, ex-showroom prices, and yard bays." },
      { step: 2, title: "Set Up Auto Finance & RTO Slabs", desc: "Configure bank loan tie-ups, road tax RTO registration rules, and insurance packages." },
      { step: 3, title: "Manage Test Drives & Deliveries", desc: "Track customer trial rides, run 120-point PDI checklists, and celebrate vehicle handovers." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Showroom Sales",
        icon: "fa-car",
        title: "VIN Inventory & On-Road Price Quotations",
        desc: "Generate professional on-road price sheets in 10 seconds factoring RTO taxes, insurance, FASTag, and genuine accessories.",
        bullets: [
          "Itemized on-road price breakdown separating ex-showroom, state road tax, and TCS.",
          "Real-time yard inventory tracking showing color and transmission variant availability.",
          "Digital quotation delivery directly to customer WhatsApp with loan EMI calculator.",
        ],
      },
      {
        badge: "Pillar 2: Test Drive Diary",
        icon: "fa-gauge-high",
        title: "DigiLocker DL Verification & Feedback",
        desc: "Ensure complete vehicle security with instant driving license verification before keys leave the showroom floor.",
        bullets: [
          "Mobile photo capture and DigiLocker driving license verification.",
          "Kilometer in/out logging and route speed monitoring.",
          "Automated post-drive customer feedback survey sent via WhatsApp.",
        ],
      },
      {
        badge: "Pillar 3: RTO & Financing",
        icon: "fa-file-invoice-dollar",
        title: "Bank Loan Desk & RTO Registration SLA",
        desc: "Accelerate vehicle deliveries with integrated auto loan tracking across HDFC, SBI, and ICICI, plus RTO registration workflows.",
        bullets: [
          "Loan disbursement status tracking from login to delivery order (DO).",
          "Automated RTO document preparation and High Security Registration Plate (HSRP) tracking.",
          "Pre-Delivery Inspection (PDI) 120-point digital checklist with quality sign-off.",
        ],
      },
      {
        badge: "Pillar 4: After-Sales Service",
        icon: "fa-screwdriver-wrench",
        title: "Service Bays, Job Cards & Spares",
        desc: "Turn your workshop into a high-margin profit center with automated job cards, spare parts inventory, and service reminders.",
        bullets: [
          "Digital service bay management with mechanic technician allocation.",
          "OEM spare parts inventory with barcode scanning and low-stock reorders.",
          "Automated WhatsApp service due reminders sent 6 months after delivery.",
        ],
      },
    ],
    competitorName: "CDK Global",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 15 vehicles)", competitor: "Hefty Enterprise Dealer Contracts", traditional: "Excel / Paper Stock Registers" },
      { capability: "Integrated Workshop PMS", axoix: "Included Service Bay & Job Card System", competitor: "Requires Separate Workshop DMS", traditional: "Manual Paper Job Cards" },
      { capability: "Direct UPI Booking Tokens", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "Payment Gateway Processing Cuts", traditional: "Manual Cheques / Cash" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Sales Rep & Mechanic Payroll", competitor: "None (Showroom Software Only)", traditional: "Disconnected Excel Sheets" },
      { capability: "Indian RTO & Tax Engine", axoix: "Native Indian RTO, HSRP & TCS Tax Slabs", competitor: "Foreign US/EU Automotive Suites", traditional: "CA Manual Calculations" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Physical Records" },
    ],
    transparencyPoints: [
      "No commission cuts on car booking token payments or workshop service invoices.",
      "100% compliant with Indian Motor Vehicles Act guidelines and GST e-invoicing standards.",
      "Complete customer data confidentiality with isolated tenant MySQL databases.",
      "Granular role-based controls: workshop mechanics cannot access showroom sales finance margins.",
      "Encrypted cloud hosting in certified Indian Tier-4 datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX generate on-road price quotations with state road tax?", answer: "Yes. AXOIX automatically calculates ex-showroom price, state RTO registration tax, zero-depreciation insurance, FASTag, and TCS in one printout." },
      { question: "How does the digital test drive diary work?", answer: "Sales executives capture the customer's driving license on their phone and log vehicle odometer readings before and after the test drive." },
      { question: "Can we track bank loan files and delivery orders?", answer: "Yes. Track auto finance files across partner banks (HDFC, SBI, ICICI) through loan application, sanction letter, and final delivery order (DO)." },
      { question: "Does AXOIX include a workshop management module?", answer: "Yes. The workshop module includes job cards, bay scheduling, spare parts inventory with barcode scanning, and mechanic labor tracking." },
      { question: "Can we send automated service reminders to car owners?", answer: "Yes. AXOIX automatically sends WhatsApp reminders for periodic services and insurance renewals based on purchase date and mileage." },
    ],
  },

  "bike-dealer": {
    slug: "bike-dealer",
    title: "Two-Wheeler & Bike Dealership System",
    subtitle: "Purpose-built for 2-wheeler showrooms: chassis & engine inventory, rapid test rides, instant exchange vehicle valuation, RTO HSRP tracking, and quick workshop service bays.",
    headerBranchOptions: ["Main Showroom & Ramps", "EV Experience Center", "Quick Service Bay"],
    headerQuickAction: "+ Quick Test Ride",
    kpis: [
      { label: "Showroom Inventory", value: "68 Two-Wheelers", sub: "EV & ICE models" },
      { label: "Test Rides Today", value: "26 Completed", sub: "High conversion" },
      { label: "Deliveries Month", value: "84 Bikes", sub: "₹98.5L Billed" },
      { label: "Service Bays Active", value: "6 Ramps", sub: "38 Bikes serviced" },
      { label: "Exchange Valuations", value: "14 Done", sub: "Trade-in offers" },
      { label: "Accessories Sold", value: "₹48,200", sub: "Helmets & Guards" },
      { label: "HSRP Plates Ready", value: "18 Plates", sub: "Fitment scheduled" },
      { label: "Instant EMI Approval", value: "92% Rate", sub: "Bajaj & L&T Finance" },
    ],
    tabs: [
      {
        id: "inventory",
        label: "Two-Wheeler Showroom Stock",
        icon: "fa-motorcycle",
        badge: "68 Bikes",
        badgeVariant: "green",
        summaryText: "Showroom Floor & Staging Ramps · 68 Two-Wheelers Ready for Immediate Delivery",
        actionButton: "+ Stock Inward",
        tableHeaders: ["Chassis / Engine #", "Two-Wheeler Model & Variant", "Color / Fuel Type", "Ex-Showroom Price", "Insurance Status", "Status", "Action"],
        rows: [
          { col1: "CHAS-8401", col2: "Royal Enfield Classic 350", col3: "Halcyon Black · Single Channel", col4: "₹2,15,000", col5: "1+4 Year Bundled", badge: { text: "Floor Ready", variant: "paid" }, action: "Create Quotation" },
          { col1: "CHAS-8402", col2: "TVS Apache RTR 160 4V", col3: "Racing Red · Dual Disc ABS", col4: "₹1,34,000", col5: "Ready for Policy", badge: { text: "Booked (Token Paid)", variant: "notice" }, action: "EMI Approval" },
          { col1: "CHAS-8403", col2: "Ather 450X Gen 3 (EV)", col3: "Space Grey · 3.7 kWh Battery", col4: "₹1,55,000", col5: "FAME-II / State Subsidy", badge: { text: "Available for Sale", variant: "paid" }, action: "Create Quotation" },
          { col1: "CHAS-8404", col2: "Honda Activa 6G DLX", col3: "Pearl Precious White · Alloy", col4: "₹82,500", col5: "Bundled Policy", badge: { text: "Floor Ready", variant: "paid" }, action: "Create Quotation" },
        ],
      },
      {
        id: "testrides",
        label: "Quick Test Ride Log",
        icon: "fa-gauge-high",
        summaryText: "26 Test Rides Completed Today · Instant Driving License Verification",
        actionButton: "+ Start Test Ride",
        tableHeaders: ["Ride ID", "Rider Name & Contact", "Bike Model", "Assigned Sales Rep", "DL Verification", "Status", "Action"],
        rows: [
          { col1: "TR-301", col2: "Aditya Sharma (+91 98110...)", col3: "Royal Enfield Classic 350", col4: "Ravi Kumar (Sales)", col5: "DL Verified ✓", badge: { text: "Completed · Loan Sanctioned", variant: "paid" }, action: "View Booking" },
          { col1: "TR-302", col2: "Sneha Rao (+91 98450...)", col3: "Ather 450X Gen 3 (EV)", col4: "Ravi Kumar", col5: "DL Verified ✓", badge: { text: "On Road (Test Circuit)", variant: "notice" }, action: "Track Return" },
          { col1: "TR-303", col2: "Varun Nair (+91 99010...)", col3: "TVS Apache RTR 160", col4: "Anand M.", col5: "DL Verified ✓", badge: { text: "Scheduled 05:00 PM", variant: "due" }, action: "Stage Bike" },
        ],
      },
      {
        id: "workshop",
        label: "Quick Service Ramps & Job Cards",
        icon: "fa-screwdriver-wrench",
        summaryText: "6 Hydraulic Ramps · Average Turnaround SLA 38 Mins per Bike",
        actionButton: "+ New Job Card",
        tableHeaders: ["Job Card #", "Ramp #", "Vehicle Reg # / Bike", "Service Type", "Technician", "Status", "Action"],
        rows: [
          { col1: "RAMP-01", col2: "Ramp 01 (Quick Service)", col3: "KA-03-HL-9102 (Classic 350)", col4: "1st Free Service + Synthetic Oil", col5: "Anand M. (Master Tech)", badge: { text: "Ready for Delivery ✓", variant: "paid" }, action: "Print Invoice" },
          { col1: "RAMP-02", col2: "Ramp 03 (Engine Tuning)", col3: "KA-04-ER-2231 (Activa 6G)", col4: "Carburetor Clean & Brake Pads", col5: "Suresh P.", badge: { text: "In Service (Washing)", variant: "notice" }, action: "Add Spares" },
        ],
      },
      {
        id: "accessories",
        label: "Accessories & Apparel POS",
        icon: "fa-helmet-safety",
        summaryText: "Showroom Accessory Desk · ₹48,200 Today in Helmets, Crash Guards & Riding Jackets",
        actionButton: "+ Quick Sale",
        tableHeaders: ["Sale Ref", "Product Name", "Category / Brand", "Sold With Vehicle", "Invoice Total", "Status", "Action"],
        rows: [
          { col1: "ACC-101", col2: "Steelbird SA-2 Full Face Helmet (ISI)", col3: "Safety / ISI Certified", col4: "Bundled with CHAS-8402", col5: "₹2,400", badge: { text: "Paid via UPI ✓", variant: "paid" }, action: "Receipt Slip" },
          { col1: "ACC-102", col2: "Stainless Steel Engine Crash Guard", col3: "Protection Accessory", col4: "Fitted on CHAS-8401", col5: "₹3,800", badge: { text: "Fitted in PDI ✓", variant: "paid" }, action: "Receipt Slip" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-motorcycle", title: "Two-Wheeler Stock", desc: "Real-time engine and chassis inventory with instant variant quotation builder." },
      { icon: "fa-bolt", title: "EV & ICE Support", desc: "Manage battery warranties, state subsidies, and home charger installations." },
      { icon: "fa-wrench", title: "Quick Service Ramps", desc: "Turnaround tracking for 1st, 2nd, and paid services with parts auto-deduction." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 25 two-wheelers free forever. Upgrade affordably as sales scale." },
    ],
    setupSteps: [
      { step: 1, title: "Add Two-Wheeler Models & Colors", desc: "Upload brand catalogs, chassis numbers, engine numbers, and ex-showroom price slabs." },
      { step: 2, title: "Connect Two-Wheeler Financiers", desc: "Link instant loan partners (Bajaj Finance, L&T, TVS Credit) for on-the-spot customer approvals." },
      { step: 3, title: "Punch Deliveries & Workshop Jobs", desc: "Issue gatepasses, fit HSRP number plates, and manage quick service hydraulic ramps." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Showroom Sales",
        icon: "fa-motorcycle",
        title: "Fast Quotations & Stock Inward",
        desc: "Generate professional on-road quotations in under 30 seconds including RTO registration, 1+4 insurance, and helmet accessories.",
        bullets: [
          "Instant on-road price calculation by city and state RTO slabs.",
          "Chassis and engine number barcode scanning during trailer unloading.",
          "Digital cost sheet delivered straight to customer WhatsApp.",
        ],
      },
      {
        badge: "Pillar 2: Instant EMI Desk",
        icon: "fa-percent",
        title: "On-the-Spot Two-Wheeler Financing",
        desc: "Boost showroom closing rates with multi-lender EMI calculations and instant Aadhaar-based loan application workflows.",
        bullets: [
          "Side-by-side EMI comparison between Bajaj, TVS Credit, and L&T Finance.",
          "Down payment collection via zero-fee dynamic UPI QR codes.",
          "Instant delivery order (DO) status tracking.",
        ],
      },
      {
        badge: "Pillar 3: Exchange & RTO",
        icon: "fa-arrows-rotate",
        title: "Vehicle Exchange & HSRP Registration",
        desc: "Value old two-wheelers in 5 minutes with our trade-in appraisal tool and track RTO number plate fitments.",
        bullets: [
          "Old bike evaluation checklist estimating realistic market exchange values.",
          "High Security Registration Plate (HSRP) delivery and customer fitment scheduling.",
          "Automated insurance renewal reminders sent 11 months post-delivery.",
        ],
      },
      {
        badge: "Pillar 4: Rapid Workshop",
        icon: "fa-wrench",
        title: "Quick-Lube Service Bay Management",
        desc: "Run high-volume service centers with hydraulic ramp timers, periodic free service coupon validation, and spare parts stock.",
        bullets: [
          "Free and paid service job cards with automatic labor coupon redemption.",
          "Engine oil and consumables auto-deducted from workshop stock upon job close.",
          "Customer vehicle ready alerts sent via automated WhatsApp message.",
        ],
      },
    ],
    competitorName: "SparesHub / Marg Two-Wheeler",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 25 two-wheelers)", competitor: "Expensive Dealer Software Subscriptions", traditional: "Excel / Paper Stock Registers" },
      { capability: "Quick Ramp Service PMS", axoix: "Included 2-Wheeler Hydraulic Ramp System", competitor: "Requires Separate Workshop DMS", traditional: "Manual Paper Job Cards" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "High Payment Processing Fees", traditional: "Cash Only / Unreconciled GPay" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Sales Rep & Mechanic Payroll", competitor: "None (Showroom Software Only)", traditional: "Disconnected Excel Sheets" },
      { capability: "EV Battery & Subsidy", axoix: "Native Battery Warranty & Subsidy Logging", competitor: "Generic ICE-Only Dealer Software", traditional: "Manual Paper Vouchers" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Physical Records" },
    ],
    transparencyPoints: [
      "No transaction commission cuts on bike bookings, token amounts, or workshop invoices.",
      "100% compliant with Indian Central Motor Vehicles Rules (CMVR) and HSRP fitment norms.",
      "Full exportability of bike inventories, customer registries, and workshop repair logs.",
      "Cashier permission controls: staff cannot delete bills or modify inventory unit costs.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX calculate on-road price quotations for two-wheelers?", answer: "Yes. In under 30 seconds, generate itemized quotations with ex-showroom, state RTO, bundled 5-year insurance, crash guards, and helmet." },
      { question: "Does the system support EV scooters and battery tracking?", answer: "Yes. Track battery pack serial numbers, motor controller numbers, state EV subsidies, and charger delivery records." },
      { question: "How does the workshop ramp system handle free services?", answer: "When a customer brings their bike for a 1st or 2nd free service, AXOIX validates warranty coupons, bills only engine oil/consumables, and notifies the customer when ready." },
      { question: "Can we track old bike exchange and trade-in valuations?", answer: "Yes. Use the built-in valuation checklist to inspect old vehicles, calculate trade-in allowances, and adjust them against new bike down payments." },
      { question: "Can customers pay booking advances via UPI QR?", answer: "Yes. Generate dynamic UPI QR codes for down payments and booking tokens with instant receipt generation." },
    ],
  },

  travel: {
    slug: "travel",
    title: "Travel Agency & Tour Operator Operating System",
    subtitle: "Complete travel software: group tour itinerary builder, Amadeus/Sabre GDS flight booking, hotel voucher issuance, visa tracking, and automated travel agent commission payouts.",
    headerBranchOptions: ["Central Agency Desk", "Corporate Travel Desk", "B2B Sub-Agent Network"],
    headerQuickAction: "+ New Tour Package",
    kpis: [
      { label: "Active Tour Bookings", value: "86 Packages", sub: "Domestic & Outbound" },
      { label: "Departures Week", value: "14 Groups", sub: "248 Travelers" },
      { label: "Flight Tickets", value: "112 Today", sub: "Zero void error" },
      { label: "Monthly Package Gross", value: "₹68.4L", sub: "Kerala, Bali, Europe" },
      { label: "Visa Applications", value: "42 In Process", sub: "Schengen, Dubai, UK" },
      { label: "Sub-Agent Cuts", value: "₹4.12L", sub: "Calculated MTD" },
      { label: "Hotel Vouchers", value: "140 Issued", sub: "Instant PDF" },
      { label: "Reconciliation", value: "99.4%", sub: "Multi-currency cards" },
    ],
    tabs: [
      {
        id: "packages",
        label: "Holiday Packages & Itineraries",
        icon: "fa-map-location-dot",
        badge: "86 Active",
        badgeVariant: "green",
        summaryText: "Domestic & International Fixed Departures · 86 Live Tour Packages",
        actionButton: "+ Create Package",
        tableHeaders: ["Package Ref", "Destination & Itinerary", "Group Size / Departure", "Per Person Price", "Seat Availability", "Status", "Action"],
        rows: [
          { col1: "PKG-TR-101", col2: "7D/6N Scenic Bali with Nusa Penida", col3: "18 Guests · 18 Oct 2026", col4: "₹54,000 / Person", col5: "18 / 20 Seats Booked", badge: { text: "Guaranteed Departure", variant: "paid" }, action: "View Roster" },
          { col1: "PKG-TR-102", col2: "5D/4N Royal Kashmir with Houseboat", col3: "12 Guests · 22 Oct 2026", col4: "₹32,500 / Person", col5: "8 / 12 Seats Booked", badge: { text: "4 Seats Left", variant: "notice" }, action: "Send Itinerary" },
          { col1: "PKG-TR-103", col2: "10D/9N Grand Europe Classic", col3: "24 Guests · 05 Nov 2026", col4: "₹1,85,000 / Person", col5: "24 / 24 Seats (Full)", badge: { text: "Fully Booked", variant: "paid" }, action: "View Roster" },
        ],
      },
      {
        id: "flights",
        label: "GDS & PNR Flight Tickets",
        icon: "fa-plane-departure",
        summaryText: "Amadeus & Direct Airline PNR Tickets · 112 Issued Today",
        actionButton: "+ Issue Ticket",
        tableHeaders: ["PNR #", "Airline & Route", "Passengers", "Departure Date", "Fare / Tax Breakdown", "Status", "Action"],
        rows: [
          { col1: "PNR-6X9Q2", col2: "IndiGo 6E-204 (DEL → BLR)", col3: "4 Passengers (Aryan M. + 3)", col4: "14 Oct 2026 · 06:15 AM", col5: "₹24,800 (Taxes Incl)", badge: { text: "Confirmed ✓", variant: "paid" }, action: "WhatsApp Tickets" },
          { col1: "PNR-8H2K1", col2: "Emirates EK-511 (BOM → DXB)", col3: "2 Passengers (Priya S. + 1)", col4: "18 Oct 2026 · 10:30 PM", col5: "₹68,400", badge: { text: "Confirmed ✓", variant: "paid" }, action: "Print E-Ticket" },
        ],
      },
      {
        id: "visa",
        label: "Visa Documentation & VFS",
        icon: "fa-passport",
        summaryText: "42 Visa Applications in Process · Realtime VFS Appointment & Embassy Tracking",
        actionButton: "+ New Visa File",
        tableHeaders: ["Visa File #", "Applicant Name", "Country & Visa Category", "VFS Appointment / Biometrics", "Embassy Decision", "Status", "Action"],
        rows: [
          { col1: "VISA-801", col2: "Dr. Anand Sen", col3: "United Kingdom (Standard Visitor)", col4: "VFS New Delhi (Biometrics Done)", col5: "Awaiting Passport Return", badge: { text: "Under Process", variant: "notice" }, action: "Track VFS" },
          { col1: "VISA-802", col2: "Priya Sharma", col3: "United Arab Emirates (30-Day E-Visa)", col4: "Instant Online Submission", col5: "E-Visa Approved & Stamped", badge: { text: "Approved ✓", variant: "paid" }, action: "Download Visa" },
        ],
      },
      {
        id: "vouchers",
        label: "Hotel Vouchers & Transfers",
        icon: "fa-file-invoice",
        summaryText: "B2B Hotel Vouchers & Sightseeing Transfer Slips Generated Instantly",
        actionButton: "+ Issue Voucher",
        tableHeaders: ["Voucher #", "Hotel / Property Name", "Lead Guest", "Nights / Meal Plan", "Supplier Rate / Margin", "Status", "Action"],
        rows: [
          { col1: "VOU-401", col2: "Grand Mirage Resort Bali (Deluxe Ocean)", col3: "Dr. Anand Sen", col4: "5 Nights · Bed & Breakfast", col5: "Settled with Agoda B2B", badge: { text: "Issued ✓", variant: "paid" }, action: "PDF Voucher" },
          { col1: "VOU-402", col2: "The Khyber Himalayan Resort Gulmarg", col3: "Rohit Verma", col4: "3 Nights · MAP Plan", col5: "Direct Hotel Payment", badge: { text: "Issued ✓", variant: "paid" }, action: "PDF Voucher" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-map-location-dot", title: "Smart Itinerary Builder", desc: "Build beautiful day-by-day itineraries with photos, hotel details, and inclusion lists." },
      { icon: "fa-plane-departure", title: "Direct GDS Integration", desc: "Issue flight tickets with Amadeus, Sabre, and direct low-cost carriers with instant markups." },
      { icon: "fa-passport", title: "Visa Milestone Tracking", desc: "Keep clients updated with automated WhatsApp alerts as visa applications move through VFS." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 25 bookings per month free forever. Scale without upfront software fees." },
    ],
    setupSteps: [
      { step: 1, title: "Create Tour Itineraries & Packages", desc: "Upload destinations, day-by-day sightseeing programs, hotel tiers, and pricing tables." },
      { step: 2, title: "Connect Flight GDS & B2B Wholesalers", desc: "Link flight ticketing credentials and B2B hotel consolidators to automate bookings." },
      { step: 3, title: "Publish Storefront & Take Bookings", desc: "Share branded tour proposals on WhatsApp and collect tour deposits with zero-fee UPI QR codes." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Tour Operations",
        icon: "fa-map-location-dot",
        title: "Dynamic Itinerary & Proposal Engine",
        desc: "Generate stunning branded holiday PDF proposals in 60 seconds with day-wise itineraries, hotel vouchers, and transparent inclusion terms.",
        bullets: [
          "Pre-loaded templates for top destinations: Bali, Dubai, Thailand, Europe, and Kashmir.",
          "Automatic calculation of per-person pricing across adult, child, and single supplements.",
          "Mobile-friendly proposals with interactive WhatsApp share buttons.",
        ],
      },
      {
        badge: "Pillar 2: Ticketing & GDS",
        icon: "fa-plane-departure",
        title: "Flight & B2B Hotel Voucher Desk",
        desc: "Issue corporate and leisure travel tickets with automated markup calculations, avoiding costly cancellation errors.",
        bullets: [
          "Amadeus, Sabre, and direct LCC airline PNR imports.",
          "Automated hotel voucher generation with custom agency branding.",
          "Airline cancellation and refund tracker with transparent ledger entries.",
        ],
      },
      {
        badge: "Pillar 3: Visa Workflow",
        icon: "fa-passport",
        title: "Visa Document Locker & VFS Tracker",
        desc: "Track client passport submissions, VFS appointments, insurance policies, and embassy approval decisions in one dashboard.",
        bullets: [
          "Checklist of required visa documents tailored by country and applicant profile.",
          "Automated WhatsApp updates to clients on appointment and embassy milestones.",
          "Travel insurance issuance integrated directly with leading Indian insurers.",
        ],
      },
      {
        badge: "Pillar 4: Back-Office & B2B",
        icon: "fa-handshake",
        title: "Sub-Agent Commissions & Multi-Currency Accounts",
        desc: "Manage B2B travel agent networks with automated commission splits, customer credit ledgers, and SAC GST invoicing.",
        bullets: [
          "Sub-agent portal for external freelance agents to quote and book packages.",
          "Full SAC code GST invoicing compliant with Indian tour operator tax regulations.",
          "Staff payroll and tour leader daily allowance tracking.",
        ],
      },
    ],
    competitorName: "TravelCarma",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 25 bookings/mo)", competitor: "Hefty Enterprise Software Contracts", traditional: "Excel / WhatsApp Groups" },
      { capability: "Itinerary Proposal Generator", axoix: "Included Branded PDF Itinerary Builder", competitor: "Paid Specialty Add-on", traditional: "Manual Word Document Typing" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "Payment Gateway Deductions (2-3%)", traditional: "Wire Transfer Screenshots" },
      { capability: "Sub-Agent Commission Ledger", axoix: "Real-time B2B Commission Engine", competitor: "Complex Setup / Add-on Required", traditional: "Manual Notebook Calculations" },
      { capability: "GST & Tour Tax Engine", axoix: "Native Indian SAC GST & TCS Compliance", competitor: "Generic Foreign Tax Modules", traditional: "CA Manual Compilation" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Vulnerable Physical Files" },
    ],
    transparencyPoints: [
      "No commission cuts on tour package sales or direct client flight bookings.",
      "100% compliant with Indian Ministry of Tourism and Tax Collected at Source (TCS) rules.",
      "Complete client contact ownership: export passenger manifests and booking records anytime.",
      "Granular staff permissions: junior reservation agents cannot view net supplier purchase rates.",
      "Encrypted cloud hosting in certified Indian Tier-4 datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can AXOIX generate branded day-wise holiday itineraries?", answer: "Yes. In under 2 minutes, create professional PDF and web itineraries with photos, day-by-day programs, flight times, hotel names, and inclusions." },
      { question: "How does the sub-agent (B2B) commission engine work?", answer: "Assign custom commission percentages or net rates to partner travel agents. When they book a client, their commission calculates automatically." },
      { question: "Can we track visa application statuses and documents?", answer: "Yes. Upload passport copies, bank statements, and photos to a secure client locker and track progress through VFS biometrics to passport return." },
      { question: "Does AXOIX handle Indian GST and TCS for international tours?", answer: "Yes. The billing engine handles 5% GST without input tax credit for tour operators and calculates 5% or 20% TCS on overseas tour packages." },
      { question: "Can clients pay package booking advances via UPI QR?", answer: "Yes. Generate dynamic UPI QR codes for package deposits (e.g., ₹25,000) that settle straight into your agency current account with zero MDR fees." },
    ],
  },

  construction: {
    slug: "construction",
    title: "Construction & Contractor ERP",
    subtitle: "Purpose-built for civil contractors & builders: site Daily Progress Reports (DPR), material indent & cement/steel consumption, subcontractor measurement books (MB), and plant & machinery tracking.",
    headerBranchOptions: ["Site 01 (Skyline Towers)", "Highway Project P-14", "Commercial Plaza"],
    headerQuickAction: "+ Daily Progress (DPR)",
    kpis: [
      { label: "Active Sites", value: "6 Projects", sub: "Residential & Infra" },
      { label: "Daily Labor", value: "340 Workers", sub: "Skilled & Unskilled" },
      { label: "Cement Consumed", value: "850 Bags", sub: "Slab concreting" },
      { label: "Steel Rebar Stock", value: "42 Tons", sub: "On site" },
      { label: "Subcontractor MB", value: "₹38.5L", sub: "Verified bills" },
      { label: "Machinery Active", value: "14 Units", sub: "JCB, Cranes, Pumps" },
      { label: "Site Petty Cash", value: "₹1,24,000", sub: "Vouchers signed" },
      { label: "Safety Record", value: "420 Days", sub: "Zero lost-time injury" },
    ],
    tabs: [
      {
        id: "dpr",
        label: "Daily Progress Reports (DPR)",
        icon: "fa-clipboard-check",
        badge: "6 Sites Live",
        badgeVariant: "green",
        summaryText: "Site Operations · 340 Workers on Duty · 6 Sites Reporting Live Today",
        actionButton: "+ Submit DPR",
        tableHeaders: ["Site Ref", "Work Executed Today", "Labor Strength", "Material Consumed", "Site Engineer", "Status", "Action"],
        rows: [
          { col1: "SITE-01 (Skyline Towers)", col2: "Pouring 7th Floor Slab (M25 Grade)", col3: "84 Men (Mason 24, Labor 60)", col4: "Cement: 420 Bags · RMC: 40 Cu.m", col5: "Rajesh K. (Site Eng)", badge: { text: "On Schedule ✓", variant: "paid" }, action: "View DPR Photos" },
          { col1: "SITE-02 (Highway P-14)", col2: "Pier Cap Shuttering & Rebar Binding", col3: "48 Men (Bar Benders)", col4: "Steel: 14.2 MT Jindal Fe550", col5: "Manas D.", badge: { text: "Inspection Passed", variant: "paid" }, action: "View DPR Photos" },
          { col1: "SITE-03 (Commercial Plaza)", col2: "External Plastering West Wing", col3: "32 Men", col4: "Cement: 80 Bags · Sand: 2 Trucks", col5: "Sunil M.", badge: { text: "Rain Delay 2h", variant: "notice" }, action: "View DPR Photos" },
        ],
      },
      {
        id: "materials",
        label: "Material Indent & Store Stock",
        icon: "fa-boxes-stacked",
        summaryText: "Cement, Steel & Aggregate Material Stock · Indent Approval Workflow Active",
        actionButton: "+ Material Indent",
        tableHeaders: ["Indent #", "Material Description", "Indented / Delivered", "Supplier Name", "QC Cube Test", "Status", "Action"],
        rows: [
          { col1: "IND-901", col2: "UltraTech 53-Grade OPC Cement", col3: "1,000 Bags / 1,000 Bags", col4: "M/s Sharma Building Materials", col5: "7-Day Cube: 32.5 MPa (Passed)", badge: { text: "Stock Inward ✓", variant: "paid" }, action: "Store Ledger" },
          { col1: "IND-902", col2: "Jindal Fe550 TMT Rebar 16mm", col3: "20 MT / 20 MT", col4: "Jindal Steel Distributor", col5: "Mill Test Cert Verified", badge: { text: "Stock Inward ✓", variant: "paid" }, action: "Store Ledger" },
          { col1: "IND-903", col2: "Ready-Mix Concrete M30 Grade", col3: "60 Cu.m (Scheduled)", col4: "ACC Concrete Plant", col5: "Slump Test: 120mm", badge: { text: "In Transit", variant: "notice" }, action: "Gatepass In" },
        ],
      },
      {
        id: "mb",
        label: "Measurement Book (MB)",
        icon: "fa-ruler-combined",
        summaryText: "Subcontractor Measurement Book (MB) · Quantity Verification & Payment Certificates",
        actionButton: "+ Record MB Entry",
        tableHeaders: ["MB Entry #", "Subcontractor Firm", "Trade / Scope of Work", "Measured Quantity", "Agreed Rate", "Status", "Action"],
        rows: [
          { col1: "MB-401", col2: "M/s Buildcon Shuttering Works", col3: "Formwork & Shuttering (Slab 7)", col4: "2,400 Sq.m Measured", col5: "₹85 / Sq.m (₹2,04,000)", badge: { text: "Eng Signed ✓", variant: "paid" }, action: "Payment Slip" },
          { col1: "MB-402", col2: "Sardar Steel Fabricators", col3: "Rebar Cutting, Bending & Binding", col4: "18.5 MT Measured", col5: "₹4,500 / MT (₹83,250)", badge: { text: "Verified ✓", variant: "paid" }, action: "Payment Slip" },
          { col1: "MB-403", col2: "Balaji Bricklaying & Plaster", col3: "Internal Brickwork 9-Inch", col4: "1,200 Sq.m Measured", col5: "₹65 / Sq.m", badge: { text: "Under Verification", variant: "notice" }, action: "Inspect Site" },
        ],
      },
      {
        id: "machinery",
        label: "Plant & Heavy Equipment Log",
        icon: "fa-truck-pickup",
        summaryText: "14 Heavy Equipment Units · Fuel Consumption & Running Hours Logged Daily",
        actionButton: "+ Log Machine Hours",
        tableHeaders: ["Equipment ID", "Machinery Description", "Today's Running Hours", "Diesel Issued", "Operator Name", "Status", "Action"],
        rows: [
          { col1: "EQ-01", col2: "JCB 3DX Super Backhoe Loader", col3: "8.5 Hours", col4: "70 Liters High-Speed Diesel", col5: "Dilip Singh (Certified)", badge: { text: "Operating ✓", variant: "paid" }, action: "Log Telemetry" },
          { col1: "EQ-02", col2: "Tower Crane TC-5040 (45m Height)", col3: "9.0 Hours", col4: "Electrical Power Meter", col5: "Sunil M. (Rigging Certified)", badge: { text: "Operating ✓", variant: "paid" }, action: "Log Telemetry" },
          { col1: "EQ-03", col2: "Schwing Stetter Concrete Pump", col3: "4.5 Hours", col4: "45 Liters Diesel", col5: "Ravi Shankar", badge: { text: "Cleaned & Staged", variant: "paid" }, action: "Maintenance" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-clipboard-check", title: "Mobile Site DPR", desc: "Engineers submit daily labor counts, photo logs, and progress reports directly from smartphones." },
      { icon: "fa-boxes-stacked", title: "Material Indent Controls", desc: "Stop cement and steel theft with gatepass verification and theoretical vs actual variance." },
      { icon: "fa-ruler-combined", title: "Measurement Book (MB)", desc: "Transparent subcontractor billing linked to physical site measurements and engineer sign-offs." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 2 active construction sites free forever. Scale up as project contracts expand." },
    ],
    setupSteps: [
      { step: 1, title: "Create Construction Projects & Sites", desc: "Set up project names, client contracts, estimated budgets, and project bill of quantities (BOQ)." },
      { step: 2, title: "Add Subcontractors & Material Rates", desc: "Define trade contractors (Shuttering, Steel, Masonry) and negotiated cement/steel purchase rates." },
      { step: 3, title: "Submit Daily Reports & Clear MB Bills", desc: "Site supervisors log daily progress, measure completed work, and approve contractor payouts." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Site Reporting",
        icon: "fa-clipboard-check",
        title: "Mobile Daily Progress Reports (DPR)",
        desc: "Get real-time visibility from remote job sites with mobile DPRs capturing labor attendance, photos, and weather delays.",
        bullets: [
          "Headcount tracking across trade contractors, skilled masons, and daily wage labor.",
          "High-resolution site photo uploads with automatic GPS timestamps.",
          "Consolidated daily progress summary sent to project directors via automated email.",
        ],
      },
      {
        badge: "Pillar 2: Material Control",
        icon: "fa-boxes-stacked",
        title: "Cement, Steel & Aggregate Reconciliation",
        desc: "Stop material theft on site. Track purchase indents, weighbridge slips, and theoretical consumption against drawings.",
        bullets: [
          "Reconciliation comparing actual cement bags used against structural mix designs.",
          "Site store inward gatepass system with supplier delivery challan upload.",
          "Quarantine tracking for concrete cube testing and laboratory test certificates.",
        ],
      },
      {
        badge: "Pillar 3: Subcontractor MB",
        icon: "fa-ruler-combined",
        title: "Digital Measurement Book & RA Bills",
        desc: "End disputes with labor subcontractors using digital Measurement Books (MB) requiring engineer verification before payout.",
        bullets: [
          "Itemized measurement entries calculated against contract item rates.",
          "Running Account (RA) bill generation with retention money and TDS deductions.",
          "Subcontractor advance recovery schedules linked directly to weekly payouts.",
        ],
      },
      {
        badge: "Pillar 4: Plant & Assets",
        icon: "fa-truck-pickup",
        title: "Heavy Equipment, Diesel & Site Cash",
        desc: "Track operating costs for excavators, batching plants, and tower cranes alongside site supervisor petty cash ledgers.",
        bullets: [
          "Daily diesel consumption logs flagging equipment with poor fuel efficiency.",
          "Preventive maintenance schedules for hydraulic equipment, winches, and pumps.",
          "Site petty cash ledger balancing vouchers, tea allowances, and emergency supplies.",
        ],
      },
    ],
    competitorName: "Procore",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 2 active sites)", competitor: "Expensive Enterprise SaaS ($500+/mo)", traditional: "Paper Pad Daily Logs" },
      { capability: "Subcontractor Measurement (MB)", axoix: "Built-in Measurement Book with RA Bills", competitor: "Complex Custom Configuration Needed", traditional: "Handwritten Paper Registers" },
      { capability: "Direct UPI Vendor Payments", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "None (B2B Wire Transfer Only)", traditional: "Cash Advances / Petty Cash" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Site Engineer & Labor Payroll", competitor: "Separate Tool Required", traditional: "Disconnected Excel Sheets" },
      { capability: "Indian Tax & GST TDS", axoix: "Native Indian GST, TDS 194C & E-Way Engine", competitor: "Generic Foreign Tax Modules", traditional: "CA Manual Calculations" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Vulnerable Physical Files" },
    ],
    transparencyPoints: [
      "No percentage cuts on project contracts, client billing amounts, or subcontractor payouts.",
      "100% compliant with Indian Central Public Works Department (CPWD) Measurement Book rules.",
      "Full exportability of site DPR logs, material consumption records, and audit ledgers.",
      "Granular site permissions: site supervisors cannot view overall project contract profit margins.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can site engineers submit Daily Progress Reports (DPR) from their phones?", answer: "Yes. Site engineers can open AXOIX on any mobile browser, snap progress photos, log labor counts, and submit DPRs in under 3 minutes." },
      { question: "How does the subcontractor Measurement Book (MB) work?", answer: "Enter measured dimensions (length, breadth, height) for completed work like shuttering or plaster. AXOIX computes total quantity, applies contracted unit rates, and generates an approved RA bill." },
      { question: "Can we track diesel fuel consumption for machinery like JCBs?", answer: "Yes. Log daily machinery running hours alongside liters of diesel issued to detect excessive fuel consumption and mechanical issues early." },
      { question: "How does material reconciliation prevent theft on site?", answer: "The system compares actual cement and steel inward against theoretical structural quantities required for poured cubic meters, flagging any shrinkage." },
      { question: "Does AXOIX handle retention money and contractor TDS deductions?", answer: "Yes. Running Account bills automatically deduct statutory 1% or 2% TDS under Section 194C and withhold retention money until project completion." },
    ],
  },

  consulting: {
    slug: "consulting",
    title: "Consulting & Advisory Practice Management",
    subtitle: "Purpose-built for professional advisory firms: client engagements, timesheet tracking, deliverable milestone billing, utilization rates, and NDA-gated project rooms.",
    headerBranchOptions: ["Corporate Strategy Practice", "Transaction Advisory", "Digital Transformation"],
    headerQuickAction: "+ Log Billable Hours",
    kpis: [
      { label: "Active Retainers", value: "18 Clients", sub: "₹48.5L MRR" },
      { label: "Consultant Utilization", value: "84.2%", sub: "Billable hours" },
      { label: "Hours Logged Week", value: "740 Hours", sub: "100% submitted" },
      { label: "Unbilled WIP", value: "₹14.2L", sub: "Milestones near" },
      { label: "Average Realized Rate", value: "₹4,200/Hr", sub: "Blended advisory" },
      { label: "Deliverables Due", value: "8 This Month", sub: "Decks & Audits" },
      { label: "Client NPS Score", value: "+72", sub: "World class" },
      { label: "Proposal Win Rate", value: "42%", sub: "RFP pipeline" },
    ],
    tabs: [
      {
        id: "engagements",
        label: "Client Engagements & Retainers",
        icon: "fa-briefcase",
        badge: "18 Active",
        badgeVariant: "green",
        summaryText: "18 Active Advisory Engagements · ₹48.5L Monthly Retainer Pipeline",
        actionButton: "+ New Engagement",
        tableHeaders: ["Engagement #", "Client Organization", "Scope / Deliverable", "Lead Partner", "Budget / Fee Structure", "Status", "Action"],
        rows: [
          { col1: "ENG-101", col2: "Tata Power Strategic Supply Chain", col3: "Milestone 2/4 (Operating Model)", col4: "Vikramaditya S. (Partner)", col5: "₹24.0L Fixed Milestone", badge: { text: "On Track ✓", variant: "paid" }, action: "View Room" },
          { col1: "ENG-102", col2: "Apollo Hospitals Organization Redesign", col3: "Milestone 1/3 (Job Banding)", col4: "Dr. Meera N.", col5: "₹18.0L Fixed Milestone", badge: { text: "On Track ✓", variant: "paid" }, action: "View Room" },
          { col1: "ENG-103", col2: "FinTech Series B Commercial DD", col3: "Market Sizing & Cohort Retention", col4: "Rohan G.", col5: "₹8.0L / Month Retainer", badge: { text: "Review Due", variant: "notice" }, action: "View Room" },
        ],
      },
      {
        id: "timesheets",
        label: "Timesheet Submissions & Hours",
        icon: "fa-clock",
        summaryText: "740 Billable Hours Logged This Week · 84.2% Firm-Wide Utilization",
        actionButton: "+ Submit Timesheet",
        tableHeaders: ["Timesheet Ref", "Consultant Name & Role", "Engagement Charged", "Billable Hours", "Non-Billable / BD", "Status", "Action"],
        rows: [
          { col1: "TS-8401", col2: "Siddharth Verma (Senior Consultant)", col3: "Tata Power Supply Chain", col4: "38.5 Hours", col5: "4.0 Hours (Internal BD)", badge: { text: "Partner Approved ✓", variant: "paid" }, action: "Audit Log" },
          { col1: "TS-8402", col2: "Ananya Roy (Associate)", col3: "Apollo Hospitals Redesign", col4: "41.0 Hours", col5: "2.0 Hours", badge: { text: "Partner Approved ✓", variant: "paid" }, action: "Audit Log" },
          { col1: "TS-8403", col2: "Karan Joshi (Analyst)", col3: "FinTech Commercial DD", col4: "39.0 Hours", col5: "5.0 Hours", badge: { text: "Pending Approval", variant: "notice" }, action: "Review Entry" },
        ],
      },
      {
        id: "billing",
        label: "Milestone Fee Invoices & GST",
        icon: "fa-file-invoice-dollar",
        summaryText: "Professional Advisory Invoicing · Automated SAC Code 9983 GST Billing",
        actionButton: "+ Create Fee Note",
        tableHeaders: ["Invoice #", "Client Name", "Milestone / Period", "Fee Amount", "GST (18% SAC)", "Status", "Action"],
        rows: [
          { col1: "INV-CONS-901", col2: "Tata Power Strategic Services", col3: "Milestone 2 Completion Signoff", col4: "₹6,00,000", col5: "₹1,08,000 (18% GST)", badge: { text: "Tax Invoice Sent ✓", variant: "paid" }, action: "PDF Invoice" },
          { col1: "INV-CONS-902", col2: "Apollo Hospitals Enterprise", col3: "Milestone 1 Handover Signoff", col4: "₹6,00,000", col5: "₹1,08,000 (18% GST)", badge: { text: "Paid via NEFT ✓", variant: "paid" }, action: "Receipt Voucher" },
          { col1: "INV-CONS-903", col2: "FinTech Venture Retainer", col3: "October 2026 Monthly Retainer", col4: "₹8,00,000", col5: "₹1,44,000 (18% GST)", badge: { text: "Payment Due", variant: "due" }, action: "Send Reminder" },
        ],
      },
      {
        id: "proposals",
        label: "RFP Pipeline & Deal Desk",
        icon: "fa-file-signature",
        summaryText: "Deal Desk Pipeline · ₹1.2Cr Proposals Outstanding · 42% Historic Win Rate",
        actionButton: "+ New Proposal",
        tableHeaders: ["Proposal Ref", "Prospective Client", "Engagement Scope", "Estimated Value", "Probability", "Status", "Action"],
        rows: [
          { col1: "PROP-401", col2: "FMCG Multinational Conglomerate", col3: "ERP Architecture & Vendor Selection", col4: "₹45.0L", col5: "75% (Final Partner Oral)", badge: { text: "Oral Presentation", variant: "paid" }, action: "View Proposal" },
          { col1: "PROP-402", col2: "Private Sector Bank Retail Wing", col3: "Cloud Banking Migration Strategy", col4: "₹30.0L", col5: "50% (RFP Submitted)", badge: { text: "Submitted", variant: "notice" }, action: "View Proposal" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-briefcase", title: "Engagement Retainers", desc: "Manage fixed-fee milestones, monthly retainers, and time-and-materials contracts." },
      { icon: "fa-clock", title: "Utilization & Timesheets", desc: "Track billable vs non-billable hours, project budgets, and consultant burnout risks." },
      { icon: "fa-lock", title: "NDA-Gated Project Rooms", desc: "Confidential document sharing with access logs ensuring strict client confidentiality." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 5 client retainers free forever. Scale as your advisory practice expands." },
    ],
    setupSteps: [
      { step: 1, title: "Create Client Retainers & SOWs", desc: "Set up client accounts, Statements of Work (SOW), milestone fee schedules, and assigned partners." },
      { step: 2, title: "Set Consultant Hourly Billing Rates", desc: "Configure billing rates per partner, senior consultant, and analyst to track project margins." },
      { step: 3, title: "Log Timesheets & Send GST Invoices", desc: "Consultants submit weekly hours, and partners generate GST fee notes with 1 click." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Practice Management",
        icon: "fa-briefcase",
        title: "Engagement Scopes & Milestone Governance",
        desc: "Prevent scope creep with structured Statements of Work (SOW), milestone sign-off certificates, and deliverable checklists.",
        bullets: [
          "Fixed-fee, monthly retainer, and T&M contract structures with budget burn alerts.",
          "Client sign-off certificates triggering immediate milestone billing.",
          "NDA-gated digital deal rooms with watermarked document sharing.",
        ],
      },
      {
        badge: "Pillar 2: Billable Hours",
        icon: "fa-clock",
        title: "Consultant Timesheets & Utilization Analytics",
        desc: "Keep practice margins healthy with frictionless weekly timesheets, partner approval workflows, and utilization targets.",
        bullets: [
          "Mobile and desktop timesheet logging with client and project task tagging.",
          "Real-time utilization rate dashboards comparing billable vs bench hours.",
          "Automated reminders for consultants who haven't submitted weekly timesheets.",
        ],
      },
      {
        badge: "Pillar 3: Advisory Billing",
        icon: "fa-file-invoice-dollar",
        title: "SAC 9983 GST Fee Notes & Disbursements",
        desc: "Bill clients accurately with professional fee notes factoring travel disbursements, out-of-pocket expenses, and GST.",
        bullets: [
          "Out-of-pocket travel and lodging expense pass-through with attached receipt slips.",
          "Automated SAC Code 9983 tax invoicing with B2B corporate GSTIN validation.",
          "Multi-currency invoices for international clients with automated exchange rate logging.",
        ],
      },
      {
        badge: "Pillar 4: Firm Back-Office",
        icon: "fa-users-gear",
        title: "Partner Profit Splits, Payroll & P&L",
        desc: "Run firm operations smoothly with partner equity draws, associate payroll, and practice-area profit-and-loss statements.",
        bullets: [
          "Partner equity profit-sharing and revenue attribution calculations.",
          "Consultant biometric clock-in, leave approvals, and monthly salary ledger.",
          "Comprehensive firm-wide financial audit ledgers and tax return exports.",
        ],
      },
    ],
    competitorName: "Kantata",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 5 client retainers)", competitor: "High Per-Seat Enterprise Pricing", traditional: "Excel Timesheet Templates" },
      { capability: "Integrated Back-Office", axoix: "Built-in Consultant Payroll, HR & Accounts", competitor: "Standalone Timesheet Tool Only", traditional: "Disconnected Bookkeeping" },
      { capability: "Direct UPI Collections", axoix: "Zero Fee Dynamic QR Directly to Bank A/C", competitor: "None (Wire Transfer Only)", traditional: "Delayed Bank Cheques" },
      { capability: "Indian Tax & SAC GST", axoix: "Native Indian SAC 9983 & TDS 194J Engine", competitor: "Generic Foreign Tax Modules", traditional: "CA Manual Calculations" },
      { capability: "Deal Desk & Proposals", axoix: "Built-in RFP Pipeline & Proposals", competitor: "Requires Separate CRM License", traditional: "Scattered Word Documents" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Shared Drives" },
    ],
    transparencyPoints: [
      "No percentage cuts on professional consulting fees or client advisory retainers.",
      "100% compliant with Indian Institute of Chartered Accountants (ICAI) and legal confidentiality norms.",
      "Complete client data confidentiality with isolated MySQL tenant databases.",
      "Strict project walls: consultants cannot view billing rates or confidential client files outside their pod.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can consultants log billable hours against specific projects and tasks?", answer: "Yes. Consultants log hours against client engagements and specific milestone tasks with billable/non-billable tagging." },
      { question: "How does partner sign-off work for timesheets and fee notes?", answer: "Partners review submitted timesheets at the end of each week. Once approved, the hours convert directly into unbilled WIP or milestone invoices." },
      { question: "Does AXOIX handle client out-of-pocket expenses (disbursements)?", answer: "Yes. Log flight tickets, hotel stays, and client meals with attached receipts, and auto-bill them as reimbursable disbursements on the next invoice." },
      { question: "Can we track partner equity profit shares and practice area P&L?", answer: "Yes. Generate practice-area profit-and-loss statements comparing realized advisory revenue against consultant salaries and overhead expenses." },
      { question: "Is Indian TDS under Section 194J supported?", answer: "Yes. Billing ledgers automatically account for statutory 10% TDS deductions on professional advisory services." },
    ],
  },

  "it-services": {
    slug: "it-services",
    title: "IT Services & Software Agency OS",
    subtitle: "Complete IT delivery operating system: sprint sprint velocity, client time tracking, retainer contracts, milestone escrows, AWS/GCP cloud billing passthrough, and SLA monitoring.",
    headerBranchOptions: ["Main Engineering Pods", "Client Retainer Deck", "Cloud DevOps Team"],
    headerQuickAction: "+ New SOW Project",
    kpis: [
      { label: "Active Sprints", value: "12 Teams", sub: "88 Devs deployed" },
      { label: "Billable Rate", value: "91.4%", sub: "Staff augmentation" },
      { label: "SLA Compliance", value: "99.8%", sub: "Uptime & Bug fixes" },
      { label: "Contract Value", value: "₹82.5L", sub: "Monthly MRR" },
      { label: "Cloud Passthrough", value: "₹6.4L", sub: "AWS, Azure, GCP" },
      { label: "Open P1 Tickets", value: "0 Tickets", sub: "Within SLA target" },
      { label: "Bench Rate", value: "8.6%", sub: "6 on upskilling" },
      { label: "Blended Hourly Rate", value: "$28 / Hr", sub: "Global accounts" },
    ],
    tabs: [
      {
        id: "projects",
        label: "Client Projects & Sprints",
        icon: "fa-laptop-code",
        badge: "12 Sprints",
        badgeVariant: "green",
        summaryText: "12 Active Agile Engineering Pods · 88 Software Engineers Deployed",
        actionButton: "+ New Sprint",
        tableHeaders: ["Project ID", "Client & Project Name", "Sprint Progress", "Engineering Pod", "Velocity", "Status", "Action"],
        rows: [
          { col1: "PRJ-801", col2: "FinTech Core Banking Mobile App", col3: "Sprint 14 of 20", col4: "6 Devs + 1 QA Lead", col5: "42 Story Points", badge: { text: "On Schedule ✓", variant: "paid" }, action: "Sprint Board" },
          { col1: "PRJ-802", col2: "US HealthTech Telemedicine Portal", col3: "Sprint 08 of 12", col4: "4 Full-Stack Devs", col5: "34 Story Points", badge: { text: "On Schedule ✓", variant: "paid" }, action: "Sprint Board" },
          { col1: "PRJ-803", col2: "Logistics Microservices (Go/K8s)", col3: "Sprint 04 of 08", col4: "5 Backend Devs", col5: "38 Story Points", badge: { text: "Ahead of Schedule", variant: "paid" }, action: "Sprint Board" },
        ],
      },
      {
        id: "time",
        label: "Developer Time & T&M Logs",
        icon: "fa-clock",
        summaryText: "Developer Time Tracking · Integrated Jira Sync & Client Timesheet Sign-Off",
        actionButton: "+ Log Dev Hours",
        tableHeaders: ["Log ID", "Developer Name & Seniority", "Project Assigned", "Tech Stack", "Hours (Week)", "Status", "Action"],
        rows: [
          { col1: "DEV-401", col2: "Aditya K. (Lead Full-Stack)", col3: "FinTech Banking (PRJ-801)", col4: "React Native / Node", col5: "40.0 Hours", badge: { text: "Client Verified ✓", variant: "paid" }, action: "Audit Commit" },
          { col1: "DEV-402", col2: "Priyanshu M. (Cloud Architect)", col3: "Logistics Backend (PRJ-803)", col4: "Go / AWS EKS", col5: "38.5 Hours", badge: { text: "Client Verified ✓", variant: "paid" }, action: "Audit Commit" },
          { col1: "DEV-403", col2: "Sanya T. (QA Automation Lead)", col3: "FinTech Banking (PRJ-801)", col4: "Playwright / Cypress", col5: "40.0 Hours", badge: { text: "Client Verified ✓", variant: "paid" }, action: "Audit Commit" },
        ],
      },
      {
        id: "contracts",
        label: "Client MSA, SOW & Retainers",
        icon: "fa-file-contract",
        summaryText: "Active Statements of Work (SOW) · Retainers, Milestones & Dedicated Pods",
        actionButton: "+ Draft SOW",
        tableHeaders: ["SOW Ref", "Client Organization", "Engagement Model", "Monthly Retainer", "Currency / Billing", "Status", "Action"],
        rows: [
          { col1: "SOW-2026-14", col2: "HealthTech Corp (San Francisco)", col3: "Dedicated Pod (4 Devs)", col4: "$18,000 / Month", col5: "USD (ACH Wire)", badge: { text: "Active Contract", variant: "paid" }, action: "View SOW" },
          { col1: "SOW-2026-15", col2: "Retail Global Group", col3: "Time & Materials ($32/hr)", col4: "Capped at $25,000/mo", col5: "USD Wire", badge: { text: "Active Contract", variant: "paid" }, action: "View SOW" },
          { col1: "SOW-2026-16", col2: "Indian FinTech Unicorn", col3: "Fixed Milestone Escrow", col4: "₹14,00,000 / Sprint", col5: "INR + 18% GST", badge: { text: "Active Contract", variant: "paid" }, action: "View SOW" },
        ],
      },
      {
        id: "sla",
        label: "SLA Monitor & Incident Response",
        icon: "fa-shield-halved",
        summaryText: "Client SLA Compliance 99.8% · 24/7 Production Monitoring & P1 Incident Routing",
        actionButton: "+ Log Ticket",
        tableHeaders: ["Incident #", "Service / Component", "Severity Level", "Reported At", "Response SLA", "Status", "Action"],
        rows: [
          { col1: "INC-901", col2: "API Gateway Latency Spike (>250ms)", col3: "P1 Critical", col4: "Today 08:15 AM", col5: "4 mins (Target < 15m)", badge: { text: "Resolved (22m) ✓", variant: "paid" }, action: "Post-Mortem" },
          { col1: "INC-902", col2: "Database Read Replica Lag", col3: "P2 High", col4: "Yesterday 04:30 PM", col5: "8 mins (Target < 30m)", badge: { text: "Resolved ✓", variant: "paid" }, action: "Post-Mortem" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-laptop-code", title: "Agile Sprint Tracking", desc: "Manage engineering pods, story point velocities, and sprint delivery roadmaps." },
      { icon: "fa-clock", title: "T&M Developer Timesheets", desc: "Granular timesheets linked to GitHub/GitLab commit logs with 1-click client sign-off." },
      { icon: "fa-globe", title: "Multi-Currency Billing", desc: "Bill international clients in USD, EUR, and GBP with automated FIRC currency reconciliation." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 10 developers free forever. Scale without per-seat project management fees." },
    ],
    setupSteps: [
      { step: 1, title: "Create Engineering Pods & Devs", desc: "Add software developers, tech stacks (React, Node, Python, AWS), and hourly billing rates." },
      { step: 2, title: "Set Up Client SOWs & Retainers", desc: "Define fixed-price milestones, monthly team retainers, or hourly Time & Materials (T&M) caps." },
      { step: 3, title: "Track Sprints & Export Invoices", desc: "Log developer hours, track velocity, and generate professional GST and export invoices." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Delivery Engine",
        icon: "fa-laptop-code",
        title: "Agile Pod Velocity & Sprint Governance",
        desc: "Maintain strict software engineering standards across multiple client pods with sprint roadmaps, story points, and commit audits.",
        bullets: [
          "Interactive sprint planning boards tracking planned vs completed story points.",
          "Developer bench management matching available skills with incoming client demand.",
          "Automated sprint health alerts when tasks exceed estimated story point hours.",
        ],
      },
      {
        badge: "Pillar 2: Client Transparency",
        icon: "fa-clock",
        title: "Verifiable Developer Timesheets & Proof of Work",
        desc: "Eliminate client invoice disputes with verifiable developer time logs, task descriptions, and GitHub commit references.",
        bullets: [
          "Developer time tracking with granular task tagging and git commit hash linking.",
          "Client approval portal where client managers sign off on weekly timesheet hours.",
          "Non-billable internal hours tracking for continuous training and R&D hackathons.",
        ],
      },
      {
        badge: "Pillar 3: Contracts & Billing",
        icon: "fa-file-contract",
        title: "Global Multi-Currency Billing & FIRC Tracking",
        desc: "Invoicing built for Indian IT exporters: foreign currency billing, Foreign Inward Remittance Certificates (FIRC), and GST refunds.",
        bullets: [
          "Direct multi-currency invoicing in USD, EUR, GBP, and SGD with automatic forex calculation.",
          "Zero-rated GST export invoice generation with Letter of Undertaking (LUT) support.",
          "AWS, GCP, and cloud hosting billing passthrough with markup margin calculations.",
        ],
      },
      {
        badge: "Pillar 4: Production SLAs",
        icon: "fa-shield-halved",
        title: "24/7 SLA Monitoring & Engineer Payroll",
        desc: "Deliver mission-critical reliability with SLA response timers, on-call schedules, and automated engineering salary ledgers.",
        bullets: [
          "Severity-based SLA timers (P1, P2, P3) with automated escalation to lead architects.",
          "Engineering biometric shifts, remote work check-ins, and monthly salary disbursements.",
          "Comprehensive profit-and-loss statements breaking down profitability by client account.",
        ],
      },
    ],
    competitorName: "Productive",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 10 developers)", competitor: "Expensive Per-User SaaS ($15-$25/seat)", traditional: "Excel Timesheet Spreadsheets" },
      { capability: "Integrated Back-Office", axoix: "Built-in Developer Payroll, HR & Accounts", competitor: "Standalone Project Management Only", traditional: "Separate Accounting Software" },
      { capability: "Direct UPI & Wire Billing", axoix: "Zero Fee Dynamic QR & Wire Reconcile", competitor: "High Payment Processing Cuts", traditional: "Manual Bank Entry" },
      { capability: "Indian IT Export & LUT", axoix: "Native Indian Zero-Rated GST LUT & FIRC", competitor: "Generic Foreign Invoicing Tool", traditional: "CA Manual Compilation" },
      { capability: "SLA Incident Monitor", axoix: "Included Incident Response & SLAs", competitor: "Requires Separate PagerDuty Addon", traditional: "Messy Slack / WhatsApp Groups" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Code / Drive Links" },
    ],
    transparencyPoints: [
      "No percentage cuts on software engineering contracts, retainers, or client hourly billing.",
      "100% compliant with Indian IT export regulations, Letter of Undertaking (LUT), and GST norms.",
      "Complete code and IP ownership retained by you and your clients.",
      "Strict data isolation: developers cannot access client invoices or developer payroll data.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can we track developer hours for both fixed-price and Time & Materials (T&M) projects?", answer: "Yes. AXOIX supports fixed-price milestones, dedicated monthly team retainers, and hourly Time & Materials (T&M) with custom billing rates per developer." },
      { question: "How does Indian IT export invoicing (zero-rated GST) work?", answer: "You can generate export invoices in foreign currencies (USD, EUR, GBP) under a GST Letter of Undertaking (LUT) with zero GST charged, compliant with Indian export laws." },
      { question: "Can clients review and approve weekly timesheets online?", answer: "Yes. Clients receive a secure link or portal login to inspect developer hours and approve timesheets before invoices are dispatched." },
      { question: "Can we bill cloud hosting costs (AWS/GCP) to clients as passthrough?", answer: "Yes. Log cloud server bills, apply an optional agency management fee or markup, and append them directly to the client invoice." },
      { question: "Does AXOIX integrate with GitHub, GitLab, and Jira?", answer: "Yes. Developers can reference commit hashes and issue tickets directly in their time entries for full auditability." },
    ],
  },

  "arena-gaming": {
    slug: "arena-gaming",
    title: "Turf, Sports Arena & Gaming Center Software",
    subtitle: "Complete sports facility OS: multi-turf hourly slot booking, floodlight automated switching, equipment rentals, prepaid tournament registrations, and dynamic UPI QR billing.",
    headerBranchOptions: ["Turf Alpha & Beta (FIFA)", "Box Cricket Pitches", "Badminton Wooden Courts"],
    headerQuickAction: "+ Quick Slot Booking",
    kpis: [
      { label: "Turf Occupancy", value: "88% Today", sub: "Peak 5–11 PM" },
      { label: "Slots Booked Today", value: "38 Slots", sub: "Football & Cricket" },
      { label: "Today's Revenue", value: "₹48,200", sub: "Direct UPI & Cash" },
      { label: "Average Slot Price", value: "₹1,250/Hr", sub: "Floodlight surge" },
      { label: "Equipment Rentals", value: "24 Sets", sub: "Bibs, Balls, Bats" },
      { label: "Tournament Teams", value: "16 Teams", sub: "Weekend Cup" },
      { label: "Monthly Passes", value: "64 Active", sub: "Regular players" },
      { label: "Floodlight Control", value: "Synced", sub: "Auto timer active" },
    ],
    tabs: [
      {
        id: "slots",
        label: "Live Turf & Court Schedule",
        icon: "fa-calendar-days",
        badge: "38 Booked",
        badgeVariant: "green",
        summaryText: "Live Arena Schedule · 4 Pitches & Courts · 88% Prime-Time Occupancy",
        actionButton: "+ Quick Walk-In",
        tableHeaders: ["Slot Code", "Court / Pitch Description", "Time Window", "Team / Booker Name", "Amount", "Status", "Action"],
        rows: [
          { col1: "SLOT-801", col2: "Turf Alpha (FIFA 5v5 Football)", col3: "06:00 PM – 07:00 PM", col4: "Koramangala FC (Aman S.)", col5: "₹1,400", badge: { text: "Paid via UPI ✓", variant: "paid" }, action: "Floodlight On" },
          { col1: "SLOT-802", col2: "Box Cricket Pitch 01", col3: "07:00 PM – 08:30 PM", col4: "Techies 11 (Rahul N.)", col5: "₹1,800", badge: { text: "Advance Paid (₹500 Due)", variant: "notice" }, action: "Collect Balance" },
          { col1: "SLOT-803", col2: "Badminton Court 01 (Teakwood)", col3: "06:00 PM – 07:00 PM", col4: "Singles Match (Vikram R.)", col5: "₹600", badge: { text: "Paid Online ✓", variant: "paid" }, action: "Gatepass" },
          { col1: "SLOT-804", col2: "Turf Alpha (FIFA 5v5 Football)", col3: "07:00 PM – 08:00 PM", col4: "Titans FC (Siddharth M.)", col5: "₹1,400", badge: { text: "Confirmed Slot", variant: "paid" }, action: "Floodlight On" },
        ],
      },
      {
        id: "memberships",
        label: "Subscription Passes & Coaching",
        icon: "fa-id-card",
        summaryText: "64 Active Monthly Passes · Automated Academy Attendance & Coach Rosters",
        actionButton: "+ Issue Pass",
        tableHeaders: ["Pass Code", "Pass Category", "Member / Academy Student", "Validity / Sessions", "Pass Fee", "Status", "Action"],
        rows: [
          { col1: "PASS-101", col2: "Morning Cricket Pass (06-08 AM)", col3: "Rohan Gupta (+91 98110...)", col4: "18 of 30 Days Used", col5: "₹4,500 / Month", badge: { text: "Active Member", variant: "paid" }, action: "Punch Day" },
          { col1: "PASS-102", col2: "Weekend Youth Football Academy", col3: "Aryan Roy (Under-16 Batch)", col4: "Coach David (Batch Sat/Sun)", col5: "₹3,500 / Month", badge: { text: "Active Student", variant: "paid" }, action: "View Roster" },
        ],
      },
      {
        id: "rentals",
        label: "Sports Gear & Cafeteria POS",
        icon: "fa-baseball-bat-ball",
        summaryText: "Equipment Rentals & Sports Drink Counter · Zero Shrinkage Deposit System",
        actionButton: "+ Counter Sale",
        tableHeaders: ["Rental Ref", "Gear / Equipment Rented", "Borrower Team", "Security Deposit", "Rental Fee", "Status", "Action"],
        rows: [
          { col1: "RENT-201", col2: "2x Nivia Pro Football + 10x Bibs", col3: "Koramangala FC", col4: "₹500 Deposit Held", col5: "₹200", badge: { text: "In Use on Turf", variant: "notice" }, action: "Return & Refund" },
          { col1: "RENT-202", col2: "2x Yonex Nanoray Rackets + Shuttles", col3: "Vikram R.", col4: "₹500 Deposit Held", col5: "₹150", badge: { text: "Returned Checked ✓", variant: "paid" }, action: "Return Deposit" },
        ],
      },
      {
        id: "tournaments",
        label: "League Tournaments & Fixtures",
        icon: "fa-trophy",
        summaryText: "Monsoon Box Cricket Cup · 16 Teams · ₹50,000 Prize Pool Knockouts",
        actionButton: "+ New Tournament",
        tableHeaders: ["Tournament ID", "Tournament Title", "Sport / Format", "Registered Teams", "Prize Pool", "Status", "Action"],
        rows: [
          { col1: "TRN-01", col2: "Monsoon Box Cricket Cup 2026", col3: "Box Cricket (8 Overs)", col4: "16 of 16 Teams (Full)", col5: "₹50,000 Winner Pool", badge: { text: "Knockout Round", variant: "paid" }, action: "View Fixtures" },
          { col1: "TRN-02", col2: "Corporate Midnight Futsal Cup", col3: "5v5 Futsal (20m Halves)", col4: "8 of 12 Teams", col5: "₹35,000 Winner Pool", badge: { text: "Registration Open", variant: "notice" }, action: "Register Team" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-calendar-days", title: "Visual Hourly Calendar", desc: "Drag-and-drop slot calendar showing daytime and floodlight night prime-time hours." },
      { icon: "fa-qrcode", title: "Direct UPI Slot Bookings", desc: "Collect slot advance payments with zero transaction fees straight to your current account." },
      { icon: "fa-trophy", title: "Tournament Fixture Engine", desc: "Automate knockout and round-robin tournament schedules, scores, and prize pools." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 2 turfs or courts free forever. Scale as you add more pitches and sports." },
    ],
    setupSteps: [
      { step: 1, title: "Add Turfs, Pitches & Hourly Rates", desc: "Enter your sports turfs, box cricket arenas, court surfaces, and peak floodlight rate slabs." },
      { step: 2, title: "Publish Online Booking Link", desc: "Share your branded web booking link on Instagram and WhatsApp for players to check live availability." },
      { step: 3, title: "Collect UPI & Manage Court Shifts", desc: "Receive instant zero-fee UPI advance deposits and control facility gate entries." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Turf Booking",
        icon: "fa-calendar-days",
        title: "Real-Time Hourly Court Scheduling",
        desc: "End phone call booking confusion with an interactive public booking calendar showing available 30, 60, and 90-minute playing slots.",
        bullets: [
          "Dynamic pricing for peak hours (6 PM - 11 PM) and weekend prime slots.",
          "Automatic 15-minute slot holding timer while players complete UPI payments.",
          "Automated WhatsApp confirmation with Google Maps location and booking slip.",
        ],
      },
      {
        badge: "Pillar 2: Direct Payments",
        icon: "fa-qrcode",
        title: "Zero-Fee UPI Collections & Advance Deposits",
        desc: "Eliminate booking no-shows by requiring partial or full advance payments through dynamic zero-fee UPI QR codes.",
        bullets: [
          "100% of revenue lands directly into your bank current account with zero MDR fees.",
          "Automated tracking of balance payment dues collected at the counter before kickoff.",
          "Instant digital receipt generation sent to the team captain's WhatsApp.",
        ],
      },
      {
        badge: "Pillar 3: Tournaments & Gear",
        icon: "fa-trophy",
        title: "League Tournaments & Gear Rental POS",
        desc: "Maximize revenue by running weekend tournaments and renting football boots, bats, bibs, and sports refreshments.",
        bullets: [
          "Knockout and league tournament scheduler with automated match fixtures.",
          "Equipment rental deposit tracking ensuring rented sports gear is returned intact.",
          "Fast counter POS for selling energy drinks, water, and grip tape.",
        ],
      },
      {
        badge: "Pillar 4: Arena Back-Office",
        icon: "fa-chart-pie",
        title: "Coach Academies, Staff HR & Utility Costs",
        desc: "Run complete facility operations including coaching academy passes, ground staff payroll, and electricity floodlight costs.",
        bullets: [
          "Monthly coaching academy attendance and coach commission revenue splits.",
          "Facility ground staff attendance tracking and payroll calculation.",
          "P&L reporting comparing hourly turf revenue against land lease rent and floodlight power bills.",
        ],
      },
    ],
    competitorName: "Playo / Hudle",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 2 turfs/courts)", competitor: "Hefty Commission per Booking (10-15%)", traditional: "Manual Notebook Diary" },
      { capability: "Direct Booking Cuts", axoix: "0% Commission on Direct Web Bookings", competitor: "Takes 10% - 15% Platform Cut", traditional: "Cash Only at Venue" },
      { capability: "Integrated Cafeteria & Gear", axoix: "Included POS for Drinks & Rentals", competitor: "None (Slot Booking Only)", traditional: "Unchecked Paper Registers" },
      { capability: "Staff HR & Biometric Shifts", axoix: "Built-in Ground Staff Attendance & Payroll", competitor: "None (Booking Engine Only)", traditional: "Disconnected Spreadsheets" },
      { capability: "Automated WhatsApp Confirm", axoix: "Built-in WhatsApp Booking Slips", competitor: "Third-Party SMS Only", traditional: "Manual Calling Captains" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Physical Paper Logs" },
    ],
    transparencyPoints: [
      "Zero commission cuts on hourly turf slot bookings made through your branded website.",
      "No customer data leakage: competitor apps cannot market rival turfs to your players.",
      "Full exportability of member player registries and revenue transaction ledgers.",
      "Staff role controls: court managers cannot delete past booking records or alter cash totals.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can players view available turf slots and book online?", answer: "Yes. Share your branded arena booking link where players see real-time availability, select their desired time, and pay via UPI." },
      { question: "How does the advance deposit system prevent no-shows?", answer: "You can require an advance payment (e.g., ₹500 or 100%) via dynamic UPI QR code. The slot locks only after payment confirmation." },
      { question: "Does AXOIX support weekend tournaments and league fixtures?", answer: "Yes. The tournament module manages team registrations, generates knockout or league match brackets, and updates live scores." },
      { question: "Can we track sports equipment rentals and security deposits?", answer: "Yes. Log borrowed gear (balls, bibs, rackets) and hold refundable deposits until items are returned and inspected." },
      { question: "Can we run monthly coaching academies and passes?", answer: "Yes. Issue monthly subscription passes for morning academy batches with automated attendance punch." },
    ],
  },

  "study-abroad": {
    slug: "study-abroad",
    title: "Study Abroad & Immigration Consultancy CRM",
    subtitle: "Enterprise overseas education OS: university course matching, SOP & document locker, visa interview mock logs, intake deadline tracking, and university commission invoicing.",
    headerBranchOptions: ["Overseas Admissions (2026-27)", "UK & Europe Desk", "US & Canada Desk"],
    headerQuickAction: "+ New Student Lead",
    kpis: [
      { label: "Active Student Pipeline", value: "380 Students", sub: "Fall & Spring Intakes" },
      { label: "University Applications", value: "142 Filed", sub: "US, UK, Canada, Aus" },
      { label: "Offers Received", value: "94 Letters", sub: "66% Offer rate" },
      { label: "Visa Success Rate", value: "98.4%", sub: "Embassy approved" },
      { label: "University Commissions", value: "₹34.8L", sub: "Receivable MTD" },
      { label: "IELTS / GRE Batches", value: "6 Batches", sub: "84 Test preppers" },
      { label: "Upcoming Deadlines", value: "14 Days", sub: "UK Jan Intake" },
      { label: "Average Tuition", value: "$28,500", sub: "Per candidate" },
    ],
    tabs: [
      {
        id: "applications",
        label: "Live University Applications",
        icon: "fa-graduation-cap",
        badge: "142 Filed",
        badgeVariant: "green",
        summaryText: "Overseas University Applications · 142 Active across US, UK, Canada & Australia",
        actionButton: "+ File Application",
        tableHeaders: ["App ID", "Student Name", "Target University & Degree", "Intake Target", "Counselor Assigned", "Status", "Action"],
        rows: [
          { col1: "APP-9101", col2: "Ananya Sharma (+91 98110...)", col3: "University of Manchester (MSc Data Science)", col4: "UK Jan 2027 Intake", col5: "Meenakshi R. (Lead)", badge: { text: "Unconditional Offer ✓", variant: "paid" }, action: "CAS / Visa Desk" },
          { col1: "APP-9102", col2: "Rohan Verma (+91 98450...)", col3: "Univ of Texas Dallas (MS CS)", col4: "US Fall 2027 Intake", col5: "Amit S.", badge: { text: "Under University Review", variant: "notice" }, action: "View Documents" },
          { col1: "APP-9103", col2: "Tanvi Patel (+91 99010...)", col3: "University of Melbourne (Master of Mgmt)", col4: "Aus Feb 2027 Intake", col5: "Meenakshi R.", badge: { text: "Offer + A$10K Scholarship ✓", variant: "paid" }, action: "Accept Offer" },
          { col1: "APP-9104", col2: "Aditya Rao (+91 98220...)", col3: "Trinity College Dublin (MSc Finance)", col4: "Ireland Fall 2027", col5: "Pooja V.", badge: { text: "SOP Revision Required", variant: "due" }, action: "SOP Review" },
        ],
      },
      {
        id: "documents",
        label: "Document Locker & Checklist",
        icon: "fa-folder-open",
        summaryText: "Encrypted Student Document Vault · Passports, Transcripts, SOPs & Financial Affidavits",
        actionButton: "+ Upload Document",
        tableHeaders: ["Doc File #", "Student Name", "Required Checklist Items", "IELTS / GRE Score", "Financial Affidavit", "Status", "Action"],
        rows: [
          { col1: "DOC-401", col2: "Ananya Sharma", col3: "Degree Transcripts, 3x LORs, SOP Final", col4: "IELTS 8.0 Band (Verified)", col5: "Bank Proof ₹32L Verified ✓", badge: { text: "100% Complete & Locked ✓", variant: "paid" }, action: "Download Pack" },
          { col1: "DOC-402", col2: "Rohan Verma", col3: "GRE Scorecard (324), 2x LORs", col4: "GRE 324 · TOEFL 108", col5: "CA Valuation Pending", badge: { text: "Financial Proof Due", variant: "notice" }, action: "Request CA Slip" },
          { col1: "DOC-403", col2: "Tanvi Patel", col3: "Transcripts, Resume, Statement of Purpose", col4: "PTE 76 (Verified)", col5: "Education Loan Letter ₹28L", badge: { text: "Complete ✓", variant: "paid" }, action: "Download Pack" },
        ],
      },
      {
        id: "visa",
        label: "Embassy Visa Desk & Mock Logs",
        icon: "fa-passport",
        summaryText: "Embassy Visa Applications & VFS Biometrics · 98.4% Historic Visa Grant Rate",
        actionButton: "+ Schedule Mock Interview",
        tableHeaders: ["Visa Ref", "Candidate Name", "Embassy & Visa Subclass", "VFS Biometrics Date", "Mock Interview Score", "Status", "Action"],
        rows: [
          { col1: "V-APP-101", col2: "Ananya Sharma", col3: "UK Student Visa (Points Based)", col4: "VFS New Delhi (Completed 04 Oct)", col5: "Mock Score: 9/10 (Cleared)", badge: { text: "Visa Approved & Stamped ✓", variant: "paid" }, action: "Pre-Departure" },
          { col1: "V-APP-102", col2: "Tanvi Patel", col3: "Australian Student Visa (Subclass 500)", col4: "Biometrics Scheduled 12 Oct", col5: "GTE Statement Approved", badge: { text: "Biometrics Scheduled", variant: "notice" }, action: "Briefing Sheet" },
          { col1: "V-APP-103", col2: "Aditya Rao", col3: "Ireland Study Visa", col4: "VFS Mumbai (Queued)", col5: "Mock 1 Completed (Needs Prep)", badge: { text: "Mock 2 Queued", variant: "due" }, action: "Schedule Mock" },
        ],
      },
      {
        id: "commissions",
        label: "University Commission Invoicing",
        icon: "fa-file-invoice-dollar",
        summaryText: "International University Commission Ledgers · Multi-Currency Invoicing (GBP, USD, AUD)",
        actionButton: "+ Raise Commission Note",
        tableHeaders: ["Commission #", "Partner University", "Enrolled Student", "Tuition Fee", "Agency Commission Slab", "Status", "Action"],
        rows: [
          { col1: "COMM-801", col2: "University of Manchester (UK)", col3: "Ananya Sharma (Jan Intake)", col4: "£24,000 Annual Tuition", col5: "12% (£2,880 / ₹3,05,000)", badge: { text: "Invoice Dispatched", variant: "notice" }, action: "View Claim" },
          { col1: "COMM-802", col2: "University of Texas Dallas (USA)", col3: "Rohan Verma (Fall Intake)", col4: "$32,000 Annual Tuition", col5: "Fixed $2,500 Bounty", badge: { text: "Pending Enrollment Wire", variant: "due" }, action: "View Claim" },
          { col1: "COMM-803", col2: "University of Melbourne (Aus)", col3: "Tanvi Patel (Feb Intake)", col4: "A$42,000 Annual Tuition", col5: "10% (A$4,200 / ₹2,35,000)", badge: { text: "Approved by Uni ✓", variant: "paid" }, action: "View Claim" },
        ],
      },
    ],
    trustBar: [
      { icon: "fa-graduation-cap", title: "University Course Finder", desc: "Filter courses across 800+ universities in the US, UK, Canada, and Australia by budget & IELTS." },
      { icon: "fa-folder-open", title: "Encrypted Document Vault", desc: "Secure digital locker for student transcripts, SOP drafts, LORs, and bank financial affidavits." },
      { icon: "fa-passport", title: "Embassy Visa Mock Desk", desc: "Structured mock interview scoring and SOP statement of purpose plagiarism checkers." },
      { icon: "fa-gift", title: "Free Starter Tier", desc: "Manage up to 25 student files free forever. Upgrade only as your consultancy intake expands." },
    ],
    setupSteps: [
      { step: 1, title: "Add Counselor Teams & Intake Targets", desc: "Create counselor profiles, assign destination countries (US, UK, Canada), and set intake quotas." },
      { step: 2, title: "Configure Partner Universities & Slabs", desc: "Enter international university partnerships, degree levels, and commission percentages." },
      { step: 3, title: "Track Student Funnels & File Visas", desc: "Guide students from course shortlisting through SOP drafting to visa approval and commission payout." },
    ],
    pillars: [
      {
        badge: "Pillar 1: Admissions Pipeline",
        icon: "fa-graduation-cap",
        title: "Course Matching & Application Governance",
        desc: "Match student academic profiles and test scores with best-fit universities, tracking application deadlines and conditional offers.",
        bullets: [
          "Eligibility matching against minimum IELTS, GRE, GMAT, and GPA requirements.",
          "Application stage tracker from document submission to offer letter acceptance.",
          "Counselor performance metrics tracking conversion rates per destination country.",
        ],
      },
      {
        badge: "Pillar 2: Student Vault",
        icon: "fa-folder-open",
        title: "Digital Document Locker & SOP Reviews",
        desc: "Stop chasing documents over WhatsApp. Students upload transcripts, LORs, and financial affidavits to an encrypted portal.",
        bullets: [
          "Document readiness checklists preventing incomplete university submissions.",
          "Integrated Statement of Purpose (SOP) version control with mentor feedback notes.",
          "Financial proof affidavit calculator converting INR bank balances to USD/GBP/AUD.",
        ],
      },
      {
        badge: "Pillar 3: Embassy Visa Desk",
        icon: "fa-passport",
        title: "Visa Interview Mocks & VFS Biometrics",
        desc: "Maximize visa grant rates with mock interview scoring, genuine temporary entrant (GTE) statements, and financial file audits.",
        bullets: [
          "Mock interview question banks tailored for US F-1, UK Student, and Australian 500 visas.",
          "VFS and embassy appointment date tracking with automated client reminders.",
          "Pre-departure briefing checklists covering forex, student housing, and flights.",
        ],
      },
      {
        badge: "Pillar 4: B2B University Invoicing",
        icon: "fa-file-invoice-dollar",
        title: "International University Commissions & Accounts",
        desc: "Never lose track of overseas university commission receivables with multi-currency claims and sub-agent commission payouts.",
        bullets: [
          "Multi-currency commission claims in GBP, USD, CAD, and AUD with automated wire tracking.",
          "Sub-agent referral payouts calculating commission shares for freelance educational counselors.",
          "Full SAC code GST invoicing and Indian income tax compliance.",
        ],
      },
    ],
    competitorName: "ApplyBoard",
    comparisonRows: [
      { capability: "Starter Pricing", axoix: "Free Forever (up to 25 student files)", competitor: "Hefty Annual Software Fee ($200+/mo)", traditional: "Excel / WhatsApp Folders" },
      { capability: "Document Locker Vault", axoix: "Included Encrypted Student Document Vault", competitor: "Requires Paid Cloud Storage Addon", traditional: "Scattered Google Drive Folders" },
      { capability: "Direct Student UPI Fees", axoix: "Zero Fee Dynamic QR for Consulting Fees", competitor: "High Gateway Fee Cuts", traditional: "Loose Cash / Screenshots" },
      { capability: "Counselor HR & Payroll", axoix: "Built-in Counselor Biometric Attendance & Payroll", competitor: "None (CRM Only)", traditional: "Disconnected Spreadsheets" },
      { capability: "International Commission Ledger", axoix: "Multi-Currency University Claims (GBP/USD)", competitor: "Complex Setup / Add-on Required", traditional: "Manual Notebook Calculations" },
      { capability: "Data Residency & Sovereignty", axoix: "Isolated MySQL Tenant DBs in India", competitor: "Shared Foreign Cloud Architecture", traditional: "Unprotected Physical Files" },
    ],
    transparencyPoints: [
      "No commission cuts on student consulting fees or university commission payouts.",
      "100% student data privacy: student contact files are never sold or shared with third parties.",
      "Full exportability of candidate records, university admissions, and financial ledgers.",
      "Role-based access: junior counselors cannot view overall company university commission ledgers.",
      "Hosted in certified Tier-4 Indian datacenters with automated daily backups.",
    ],
    faqList: [
      { question: "Can counselors track university application deadlines across multiple countries?", answer: "Yes. Set deadlines for Fall, Spring, and Summer intakes across the US, UK, Canada, Australia, and Europe with automated countdown reminders." },
      { question: "How does the encrypted student document vault work?", answer: "Students and counselors upload transcripts, IELTS scorecards, and financial affidavits to a secure student file. The system checks off required documents before filing." },
      { question: "Does AXOIX help with visa interview preparation?", answer: "Yes. Counselors can conduct and log mock visa interviews, rate candidate responses against standard rubrics, and provide feedback notes." },
      { question: "How do we track university commissions in foreign currencies?", answer: "Log commission claims in GBP, USD, CAD, or AUD. The system tracks the status from university confirmation to final bank wire receipt with forex conversion." },
      { question: "Can we manage sub-agents and freelance student recruiters?", answer: "Yes. Tag students to specific sub-agents and calculate automated referral commission payouts once the student enrolls." },
    ],
  },
};

const ALIAS_MAP: Record<string, string> = {
  gym: "gym-fitness",
  salon: "salon-spa",
  realestate: "real-estate",
  car: "car-dealer",
  bike: "bike-dealer",
  arena: "arena-gaming",
  studyabroad: "study-abroad",
};

export const getVerticalDetailConfig = (
  slug: string,
  title: string = "Business",
  subtitle: string = ""
): VerticalDetailConfig => {
  const norm = slug.toLowerCase().trim();
  const canonicalSlug = ALIAS_MAP[norm] || norm;
  const existing = VERTICAL_CONFIGS[canonicalSlug];

  if (existing) {
    return {
      slug: canonicalSlug,
      title: existing.title || `${title} Management Software`,
      subtitle: existing.subtitle || subtitle || `Complete operating system for modern ${title.toLowerCase()} operations.`,
      headerBranchOptions: existing.headerBranchOptions || [`Main Branch (HQ)`, `Branch 2 (North)`],
      headerQuickAction: existing.headerQuickAction || `+ Quick Action`,
      kpis: existing.kpis || getDefaultKpis(title),
      tabs: existing.tabs || getDefaultTabs(title),
      trustBar: existing.trustBar || getDefaultTrustBar(title),
      setupSteps: existing.setupSteps || getDefaultSetupSteps(title),
      pillars: existing.pillars || getDefaultPillars(title),
      competitorName: existing.competitorName,
      comparisonRows: existing.comparisonRows || getDefaultComparison(title, existing.competitorName),
      transparencyPoints: existing.transparencyPoints || getDefaultTransparency(title),
      faqList: existing.faqList || getDefaultFaq(title),
    };
  }

  // Dynamic domain config generation for any other vertical
  return {
    slug: canonicalSlug,
    title: `${title} Management Software`,
    subtitle: subtitle || `The all-in-one business operating system purpose-built for ${title.toLowerCase()} operations in India.`,
    headerBranchOptions: [`Main Branch (HQ)`, `Regional Branch 02`],
    headerQuickAction: `+ Quick Action`,
    kpis: getDefaultKpis(title),
    tabs: getDefaultTabs(title),
    trustBar: getDefaultTrustBar(title),
    setupSteps: getDefaultSetupSteps(title),
    pillars: getDefaultPillars(title),
    competitorName: undefined,
    comparisonRows: getDefaultComparison(title),
    transparencyPoints: getDefaultTransparency(title),
    faqList: getDefaultFaq(title),
  };
};

const getDefaultKpis = (title: string): VerticalKpi[] => [
  { label: "Active Operations", value: "92%", sub: "Healthy utilization" },
  { label: "Today's Workflows", value: "148", sub: "+16% vs target" },
  { label: "Today's Revenue", value: "₹94,500", sub: "Direct UPI & Cards" },
  { label: "Average Ticket", value: "₹1,850", sub: "Standard service" },
  { label: "Turnaround SLA", value: "98.4%", sub: "Within target time" },
  { label: "Staff on Shift", value: "18 Members", sub: "Active roster" },
  { label: "Open Inquiries", value: "8 Inquiries", sub: "Storefront leads" },
  { label: "Ledger Reconciliation", value: "Balanced", sub: "Zero variance" },
];

const getDefaultTabs = (title: string): VerticalTabConfig[] => [
  {
    id: "operations",
    label: "Live Operations & Tracking",
    icon: "fa-chart-pie",
    badge: "Live",
    badgeVariant: "green",
    summaryText: `Real-Time ${title} Operations Matrix · All Workflows Online`,
    actionButton: "+ Quick Action",
    tableHeaders: ["Ref Code", "Entity / Client", "Service / Type", "Assigned Staff", "Amount / Units", "Status", "Action"],
    rows: [
      { col1: "OP-101", col2: `${title} Client 01`, col3: "Standard Service Package", col4: "Rajesh K.", col5: "₹12,400", badge: { text: "Active ✓", variant: "paid" }, action: "View Details" },
      { col1: "OP-102", col2: `${title} Client 02`, col3: "Priority Express Workflow", col4: "Anita P.", col5: "₹18,500", badge: { text: "In-Progress", variant: "due" }, action: "Update Stage" },
      { col1: "OP-103", col2: `${title} Client 03`, col3: "Custom Solution Package", col4: "Vikram S.", col5: "₹8,200", badge: { text: "Review Due", variant: "notice" }, action: "Approve" },
      { col1: "OP-104", col2: `${title} Client 04`, col3: "Annual Support Plan", col4: "Deepak M.", col5: "₹34,000", badge: { text: "Completed ✓", variant: "paid" }, action: "Print Invoice" },
    ],
  },
  {
    id: "billing",
    label: "Billing & Collections",
    icon: "fa-file-invoice-dollar",
    summaryText: "Automated GST Invoicing with Dynamic UPI QR Reconciliation",
    actionButton: "Run Billing Cycle",
    tableHeaders: ["Invoice #", "Customer Name", "Billing Items", "GST Breakdown", "Total Amount", "Status", "Action"],
    rows: [
      { col1: "INV-8910", col2: `${title} Client 01`, col3: "Monthly Contract Services", col4: "₹1,860 (18% GST)", col5: "₹12,400", badge: { text: "UPI Paid ✓", variant: "paid" }, action: "Tax Receipt" },
      { col1: "INV-8911", col2: `${title} Client 02`, col3: "Service Addon Pack", col4: "₹2,775 (18% GST)", col5: "₹18,500", badge: { text: "Payment Due", variant: "due" }, action: "Send UPI QR" },
      { col1: "INV-8912", col2: `${title} Client 03`, col3: "Periodic Retainer Bill", col4: "₹1,230 (18% GST)", col5: "₹8,200", badge: { text: "Pending Review", variant: "notice" }, action: "Send Reminder" },
      { col1: "INV-8913", col2: `${title} Client 04`, col3: "Enterprise Milestone Fee", col4: "₹5,100 (18% GST)", col5: "₹34,000", badge: { text: "Auto-Reconciled ✓", variant: "paid" }, action: "Download PDF" },
    ],
  },
  {
    id: "crm",
    label: "Client Inquiries & CRM",
    icon: "fa-users",
    summaryText: "Lead Pipeline & Public Storefront Bookings",
    actionButton: "+ Add Contact",
    tableHeaders: ["Client ID", "Organization / Name", "Primary Contact", "Inquiry Channel", "Stage", "Status", "Action"],
    rows: [
      { col1: "CRM-301", col2: "Greenfield Enterprise", col3: "+91 98112 34567", col4: "Official Storefront", col5: "Proposal Sent", badge: { text: "Qualified", variant: "paid" }, action: "Open Chat" },
      { col1: "CRM-302", col2: "Nova Tech Corp", col3: "+91 98450 12345", col4: "WhatsApp Direct", col5: "Initial Contact", badge: { text: "Follow Up", variant: "due" }, action: "Schedule Call" },
      { col1: "CRM-303", col2: "Horizon Group", col3: "+91 99001 98765", col4: "Referral Network", col5: "Contract Signing", badge: { text: "Closing", variant: "paid" }, action: "Send E-Sign" },
    ],
  },
  {
    id: "logs",
    label: "Compliance & Audits",
    icon: "fa-shield-halved",
    summaryText: "Immutable Ledger History & Regulatory Compliance",
    actionButton: "Export Audit Log",
    tableHeaders: ["Log ID", "Event Description", "User / Role", "IP / Device", "Timestamp", "Status", "Verification"],
    rows: [
      { col1: "LOG-501", col2: "Monthly GSTR-1 Tax Return Filed", col3: "Finance Administrator", col4: "103.21.24.18 (Encrypted)", col5: "Today 10:30 AM", badge: { text: "Verified ✓", variant: "paid" }, action: "View Filing" },
      { col1: "LOG-502", col2: "Ledger Voucher Settlement Verified", col3: "Accountant", col4: "103.21.24.18 (Encrypted)", col5: "Today 11:15 AM", badge: { text: "Balanced", variant: "paid" }, action: "Voucher PDF" },
      { col1: "LOG-503", col2: "Automatic Daily Backup Snapshot", col3: "System Service", col4: "AWS ap-south-1", col5: "Today 02:00 AM", badge: { text: "Encrypted", variant: "paid" }, action: "Integrity Check" },
    ],
  },
];

const getDefaultTrustBar = (title: string): VerticalTrustItem[] => [
  { icon: "fa-layer-group", title: `${title} Architecture`, desc: `Engineered specifically for the real-world workflows of Indian ${title.toLowerCase()} operators.` },
  { icon: "fa-qrcode", title: "Direct UPI Collections", desc: "Dynamic QR code invoices with instant reconciliation and zero gateway commission cuts." },
  { icon: "fa-mobile-screen-button", title: "Mobile Staff Portal", desc: "Role-aware mobile access for on-the-ground employees, managers, and field staff." },
  { icon: "fa-gift", title: "Free Forever Plan", desc: "Zero platform fees to get started. Upgrade only as your business transaction volume expands." },
];

const getDefaultSetupSteps = (title: string): VerticalSetupStep[] => [
  { step: 1, title: `Configure ${title} Catalog`, desc: "Import your operational items, service packages, and pricing rules using our rapid bulk setup assistant." },
  { step: 2, title: "Invite Staff & Define Roles", desc: "Assign specific roles to team members with granular permission controls for billing, operations, and reports." },
  { step: 3, title: "Go Live & Collect Payments", desc: "Start generating GST compliant invoices, collecting online UPI payments, and tracking live operational KPIs." },
];

const getDefaultPillars = (title: string): VerticalPillar[] => [
  {
    badge: "Pillar 1: Operations",
    icon: "fa-gauge-high",
    title: `Core ${title} Operations Engine`,
    desc: `Streamline day-to-day ${title.toLowerCase()} operations with automated workflows, status tracking, and error-free execution.`,
    bullets: [
      "Real-time operational dashboard with key performance indicators and alerts.",
      "Customizable workflow stages matching your company's exact operational flow.",
      "Granular role-based permissions preventing unauthorized actions by junior staff.",
    ],
  },
  {
    badge: "Pillar 2: Collections",
    icon: "fa-qrcode",
    title: "Direct UPI Payments & Fast Settlement",
    desc: "Collect customer payments faster with dynamic UPI QR codes printed on invoices or delivered via SMS/WhatsApp.",
    bullets: [
      "Zero payment gateway deductions: funds land directly in your registered bank account.",
      "Instant automated reconciliation matching received payments to open invoices.",
      "Scheduled payment reminder notifications reducing payment collection delays.",
    ],
  },
  {
    badge: "Pillar 3: Customer Experience",
    icon: "fa-users",
    title: "Customer Portal & Communications",
    desc: "Delight your clients with branded self-service tracking, automated progress updates, and instant WhatsApp support.",
    bullets: [
      "Branded customer portal for tracking orders, services, payments, and invoices.",
      "Automated WhatsApp and SMS notifications at each operational milestone.",
      "Integrated customer feedback collection and satisfaction scoring.",
    ],
  },
  {
    badge: "Pillar 4: Back-Office Suite",
    icon: "fa-chart-pie",
    title: "Built-in HR, Accounting & Tax Compliance",
    desc: "Stop paying for 5 separate software subscriptions. AXOIX bundles complete back-office management into one platform.",
    bullets: [
      "Staff biometric attendance tracking, leave management, and automated payroll.",
      "Full double-entry general ledger, expense tracking, and profit-and-loss reports.",
      "Native Indian GST return preparation and compliant tax invoice generation.",
    ],
  },
];

const getDefaultComparison = (title: string, competitorName?: string): VerticalComparisonRow[] => {
  const comp = competitorName || "Traditional Software / Point Apps";
  return [
    { capability: "Starter Pricing", axoix: "Free Forever Tier Available", competitor: "Paid from Day 1 + Setup Fees", traditional: "Excel / Paper Registers" },
    { capability: "Integrated Back-Office", axoix: "Built-in Staff HR, Payroll, Accounting & GST", competitor: "Single-Function Only (No Back-office)", traditional: "Disconnected Spreadsheets" },
    { capability: "Dynamic UPI QR Payments", axoix: "Included with Instant Zero-Fee Auto-Match", competitor: "High Payment Gateway Fees (2-3%)", traditional: "Manual Bank Entry" },
    { capability: "Public Branded Website", axoix: "Included Custom-Domain Storefront (0% Cut)", competitor: "Extra Monthly Charge or None", traditional: "No Direct Digital Engine" },
    { capability: "Team Collaboration Chat", axoix: "Built-in Team Messaging & Video Calls", competitor: "None (External WhatsApp Groups)", traditional: "Unorganized Group Chats" },
    { capability: "Indian Tax & E-Invoicing", axoix: "Native Indian GST Engine with 1-Click Export", competitor: "Generic Foreign Tax Calculator", traditional: "CA Manual Compilation" },
  ];
};

const getDefaultTransparency = (title: string): string[] => [
  "No hidden transaction cuts on direct UPI payments to your current account.",
  "100% data residency in certified Indian enterprise cloud datacenters.",
  "Full exportability of all client, billing, and operational data in Excel and JSON formats.",
  "Granular role-based access control protecting sensitive financial data from unauthorized staff.",
  "Automated daily encrypted backups with 99.9% platform availability guarantee.",
];

const getDefaultFaq = (title: string): Array<{ question: string; answer: string }> => [
  {
    question: `How does AXOIX differ from generic business software for ${title.toLowerCase()}?`,
    answer: `Unlike generic accounting apps or point tools, AXOIX is a complete business operating system. It combines ${title.toLowerCase()}-specific workflows with back-office HR, payroll, inventory, and zero-fee UPI collections in a single unified login.`,
  },
  {
    question: "Do I need technical skills or IT staff to set up AXOIX?",
    answer: "No. AXOIX is built for rapid onboarding. Our 3-step setup wizard guides you through configuring your catalog, inviting team members, and running operations in less than 5 minutes.",
  },
  {
    question: "How does the zero-fee UPI QR collection work on invoices?",
    answer: "Every invoice generated by AXOIX can include a dynamic UPI QR code. When your customer scans with Google Pay, PhonePe, or Paytm, funds land directly in your registered bank account with zero merchant deduction fees.",
  },
  {
    question: "Can I manage multiple locations or branches?",
    answer: "Yes. AXOIX supports multi-branch operations natively. You can manage separate branches, allocate staff permissions per location, and review consolidated financial performance from one dashboard.",
  },
  {
    question: "Can my existing data be migrated into AXOIX?",
    answer: "Yes. We provide easy-to-use CSV and Excel bulk import templates for your customer contacts, catalog items, opening stock, and historical records.",
  },
];
